---
en:
  _components:
    toggleCard:
      close: Collapse tab
  _errors:
    _default: Something went wrong...
    _title: Error occurred
    config_conflict: Someone else might have updated settings while you were at it too. Reload the page or open a new window with new settings and update the settings again.
    job_already_running: Feed job is already running, we just need some more time.
    job_not_found: Feed job was not found. Please contact our support.
  _popupLeave:
    close: Stay on this spage
    info: You are leaving page with unsaved changes. If you continue, this changes will be lost. Are you sure?
    submit: Leave anyway
    title: You have unsaved changes
  abTests:
    _errors:
      _default: Something went wrong...
      abTestDurationConflictError: The schedule of the test is in conflict with another test. Only one test can be run at a time.
      duplicitConfigurationVersion: The test is referencing same configuration version duplicitly.
      duplicitTitle: The title of the test is already used. Please pick a unique title.
      forbiddenUpdateChange_sample_size: Unable to update the test as it is already past its start date. Stop it and create a new one if needed.
      forbiddenUpdateChange_valid_range: Unable to update the test as it is already past its start date. Stop it and create a new one if needed.
      forbiddenUpdateChange_variant: Unable to update the test as it is already past its start date. Stop it and create a new one if needed.
      invalidConfiguration: Invalid configuration is being referenced.
      invalidSampleSize: The sample size must be between 1 and 100.
      invalidValidRange: Please check time range of the test and make sure both values are filled.
      missingConfigurationVersion: The referenced configuration version is missing.
      missingTitle: Missing title of the test.
      missingValidRange: Please check time range of the test and make sure both values are filled.
      missingVariantIdentifier: Missing variant identifier. Only "A" and "B" are allowed.
      unableToRemoveTestError_finished: Unable to remove the test as it is already past its start date.
      unableToRemoveTestError_running: Unable to remove the test as it is already past its start date.
      unableToRemoveTestError_stopped: Unable to remove the test as it is already stopped.
      unableToStopError_draft: Unable to stop the test as it is only in its draft stage.
      unableToStopError_finished: Unable to stop the test as it has already finished.
      unableToStopError_scheduled: Unable to stop the test as it is only in its scheduled stage. Please edit the test and save it as draft instead.
      unableToStopError_stopped: Unable to stop the test as it is already stopped.
      unableToUpdateHistoricAbTest: Unable to change the test as it has already finished. Stop it and create a new one if needed.
      unableToUpdateToDraft: Unable to change the test to draft as it is already past its start date.
      wrongVariantIdentifier: Unexpected variant identifier. Only "A" and "B" are allowed.
    createProject: Create your first test
    customizeButton: Create new A/B test
    detail:
      buttons:
        discard: Discard test
        saveAndRun: Save and run A|B test
        saveAsDraft: Save as draft
      cycleWarning: The test should run in a repeating <b>cycle</b>, like every 7, 14, 21 days, starting and ending on a specific day of the week (e.g., Monday to Sunday). It should last <b>at least 7 days.</b>
      scenarios:
        create_configuration: Create new configuration
        desc1: You always test the control <b>A variant (current settings)</b> against the experimental B variant (specific changes in settings). Within one experimental variant, you can test changes in settings for only one specific service.
        desc2: You prepare the experimental variant by making <b>modifications to the settings</b> of a specific service that you use.
        desc3: If you want to start with tests but don't know how, <b>you can use one of the predefined test scenarios</b>.
        duplicate_configuration: Duplicate & edit Control A Variant
        title: What scenarios and variants do you want to test?
        variantA: Control A variant
        variantB: Experimental B variant
      subTitle: Set the scenario you want to compare, choose the sample size of your traffic, decide how long the experiment should live, and run the test.
      testTooLongWarning: "<b>Test should not be longer than 90 days</b> as results cannot be interpreted for the whole duration of the test."
      times:
        14_days: 14 days
        28_days: 28 days
        7_days: 7 days
        90_days: 3 months
        custom_range: Custom range
        dayAndTime: 'Day and time of launch:'
        desc1: We recommend that tests <b>last at least one to 2 weeks</b>, but they can also be longer if more data is needed or if seasonal factors impact performance.
        desc2: "<b>Holidays can have a significant impact</b> on e-commerce conversions. You should take these factors into account in your A/B test."
        launchTest: We will launch the test {launchTime} at 00:00, and <b>it will last for</b>
        shutDownTime: 'Date and time of automatic shutdown:'
        testDuration: 'Test duration:'
        title: When should the test be launched?
      title: Creating a new A/B test
      titles:
        desc1: "<b>Choose a title with context for easier test identification</b> when you return to it later. Well-named tests generally improve clarity and orientation in lists not only for you but also for your colleagues."
        desc2: "<b>For example</b>: Personalization Test, Margin Test..."
        descLabel: If you want, you can add a test description or a note.
        textAreaPlaceholder: I need to test...
        title: What is the title of that A/B test?
        titleLabel: Keep the existing title or enter your own.
      visitors:
        desc: A larger sample size <b>decreases the duration and increases your ability</b> to detect smaller effects and achieve statistical significance. Don't worry, our tests are safe.
        limitAlert: "<b>The recommended sample size is at least 75 %</b> to ensure that the test can successfully complete within the specified time. This value is <b>based on the traffic to your website.</b>"
        recommendedSize: Recommended sample size is <b>at least 75 %</b> (based on the traffic to your website).
        sampleDistribution: 'The sample will be evenly distributed (e.g., A 50 % : B 50 %) among the tested variants.'
        setText: 'Set sample size: <b>{percentage} % of traffic</b>'
        title: How many visitors do you want to include in the test?
        warning: "<b>The recommended sample size is at least 75 %</b> to ensure that the test can successfully complete within the specified time. This value is <b>based on the traffic to your website</b>."
    discardPopup:
      desc: Are you sure you want to <b>discard test “{x}” that you set?</b> You can't take back this change.
      title: Discard test
    easyToSetupMessage: Experiments are easy to set up and use and secure. You don't need to worry about messing anything up.
    howItWorks: How do A/B tests work?
    intro:
      buttonText: Create new test
      subTitle: Our A/B tests and experiments enable you to confidently fine-tune, test, and measure new strategies, all from the LBX app. The more tests you conduct, the better results you will achieve. Experiments are easy to set up and secure. They are an ideal way to quickly improve your business metrics.
      title: A/B Tests & Experiments
    list:
      completed: Completed experiments
      createdAbTest: Created A/B tests
      item:
        analytics: Analytics
        buttonShow: Show experiment settings
        draft: "🥶 <b>It is so cold...</b> The experiment is waiting for planning."
        editExperiment: Edit experiment
        finishedTest: ✅ <b>The test was successful!</b> We recommend to apply the winning variant <a href="//{href}">{variant}</a> as your site configuration.
        finishedTestFirst: "✅ The test was successful! We recommend staying with the current site configuration {variant} configuration ({version})."
        finishedTestNotSuccess: "❌ <b>The test was not successful.</b> Some of the results (AOV, CVR and AOV*CVR) are non-significant."
        finishedTestSrmError: "❌ <b>The test was not succesful.</b> High Sample Ratio Mismatch was identified (session distribution was {srm1}%:{srm2}%, instead of 50%:50%)."
        finishedTestSrmWarning: "⚠️ <b>The test was succesful.</b> Low Sample Ratio Mismatch was identified (session distribution was {srm1}%:{srm2}%, instead of 50%:50%)."
        internal: Internal LBX test
        lastUpdate: 'Last Update:'
        not_significant: Non-significant results
        not_significant_field: Non-significant!
        not_significant_info1: Unsuccessful test
        not_significant_info2: Some of the results (AOV or CVR) are non-significant
        not_significant_yet: Non-significant results for AOV and CVR yet
        not_significant_yet_info1: Wait for completion
        not_significant_yet_info2: The results can only be declared significant once the test has been completed.
        runningTest: "👍 <b>The test is running.</b> For significant results, please wait for its completion."
        scheduledTest: "🧊 <b>The test is set and scheduled.</b>"
        stopTest: Stop the A/B test
        stoppedTest: "❌ <b>The test was not successful.</b> Someone stopped the test earlier than planned."
        table:
          aov:
            description: Total value of transactions divided by the number of transactions
            short: AOV
            title: Average Order Value
          aov_cvr:
            description: Multiplication of AOV and CVR (revenue per session)
            short: AOV * CVR
            title: AOV * CVR
          cvr:
            description: Number of transactions divided by the number of sessions
            short: CVR
            title: Conversions Rate
          result:
            description: Final result of the A/B test
            short: Result
            title: Result
          sessions:
            description: Number of sessions regardless of whether they are associated with any service
            short: Sessions
            title: Sessions
          titlevariant:
            short: Variant
          transactions:
            description: Number of transactions, regardless of whether they are attributed to any service
            short: Transactions
            title: Transactions
        test_stopped: The test was stopped
      searchIn: 'Search in tests:'
      sidebar:
        completion: Completion
        controlA: Control A variant
        days: <span class="badge badge--rounded font-small-3 badge-light text-dark">{n}</span> day | <span class="badge badge--rounded font-small-3 badge-light text-dark">{n}</span> days
        description: Description
        duration: Duration
        experimental: Experimental B variant
        goToConfig: Go to configuration
        internalNotes:
          description: "(Non-visible for clients)"
          discard: Discard changes
          edit: Edit note
          placeholder: You can add a custom note for the LBX people...
          save: Save note
          title: LBX Internal notes
        launch: Launch
        name: Title
        remain: left
        sampleSize: Sample size
        title: Experiment settings
        trafficSplit: Traffic split
      status:
        draft: Draft
        finished_bad: Finished
        finished_good: Finished
        running: Running
        scheduled: Scheduled
        stopped: Stopped
      types:
        a: Control variant
        b: Experimental variant
    noAbTests: No A/B tests have been created yet.
    options:
      14_days: 14 days
      28_days: 28 days
      7_days: 7 days
      90_days: 3 months
      custom_range: Custom range
    stopTestPopup:
      desc: Are you sure <b>you want to stop</b> the running test <b>”{x}”?</b> You can't take back this change. After stopping the test, <b>it won't be possible to continue</b> with testing.
      title: Stop the test in progress
  acSetup:
    advanced:
      cards:
        abTest: AB testing running and set up
        code: Style edits and custom code
        general: General settings
        screenshots: Screenshots of the integration page
        structure: Structure and content set up
      general:
        abTest: Run AB testing
        autoReposition: Turn on automatic reposition
        customClass: Custom class (selector) for autocomplete
        eval: Evaluate all titles including the text "View all products"
        locale: Locale
        showLogo: Display logo
        showLogo2: Luigi's Box
        typingSuggestionsTimeout: Time limit for listing suggestions (ms)
        waitForSelectors: Waiting for selector
        width: Autocomplete width
      hideShowAll: Hide / show all
      info: These settings are intended for advanced users, programmers, and Luigi's Box integration team. If it is not necessary, do not change them.
      placeholders:
        customClass: Enter a class name
        locale: en, sk, cz...
        searchInAttributes: Search in attributes
        typingSuggestionsTimeout: Enter a number
        waitForSelectors: Enter a selector
        width: Enter a number
      structure:
        addAttribute: "+ Add available attributes"
        addedAttributes: Added attributes
        duplicate: Duplicate
        maxCategoriesForQuery: Most categories for the query
        maxQueriesWithCategories: Most phrases with categories
        order: Order
        position: Position
        queriesWithCategories: Phrases with categories
        queriesWithCategoriesFirst: Phrases with categories in the first position
        recommendSize: Number of recommendations
        recommendTitle: Recommendation name
        size: Quantity
        title: Name
      title: Advanced autocomplete settings
    buttons:
      discard: Discard all changes
      firstSetupContinue:
        design: Continue to structure and texts
        structure: Continue to autocomplete colors
      firstSetupDiscard: Discard all changes
      preview: Show live preview
      publish: Publish
      saveAndLaunch: Save and publish
      saveDraft: Save as draft
    colors:
      colorNames:
        buttons_text_color: The color of the text in the button
        header_color: Basic text color
        highlight_background_color: Background color of highlighted text
        highlight_color: Text color in the highlighted part
        hover_background_color: Button text color
        luigi_ac_hero_color: Main conversion color
        luigi_ac_hero_color_clickable: Button background after mouse hover
        main_name_color: Message title and text for "no results"
        others_block_color: Autocomplete block background without products
        others_name_color: Color for other texts
      info: Customize the autocomplete color to your site design with a simple color change. Start by entering the main color, and we will design the entire color scheme based on it; then, it is up to you whether you keep this setting or decide to continue editing further.
      setColorScheme: Generate color scheme
      title: Autocomplete colors
    components:
      colors: Autocomplete colors
      design: Display method
      structure: Structure and texts
    design:
      alignSelect: 2. Select how to align the autocomplete
      asideAlign:
        left: On the right
        right: On the left
      asideAlignInfo: Placement of found categories and brands, etc. results
      boxAlign:
        center: In the middle
        input-left: On the left
        input-right: On the right
      boxAlignInfo: Alignment of the autocomplete to the search field on the page
      howToSetup: How to set up Autocomplete
      info: If the selected setting does not suit you, choose your autocomplete style to replace your original solution. You can change additional display options like layout or alignment depending on your selection.
      showBorder: Show framing around the autocomplete
      themeSelect: 1. Select how to display the autocomplete
      themes:
        heromobile:
          description: Display results divided into blocks with optimized mobile viewing.
          name: Heromobile (recommended)
        line:
          description: Display all results in rows with identical mobile display.
          name: Line
      title: Display method
    draft:
      discard: Discard changes
      info: Your settings changes are waiting to be published. Unfortunately, the last changes made in the settings have not been published yet. How do you wish to proceed?
      publish: Publish changes
    locales:
      cz: Čeština
      de: Deutsch
      en: English
      hu: Magyar
      pl: Polski
      ro: Română
      sk: Slovenčina
    popupDiscard:
      close: Close window
      info: If you continue, you will <strong>remove all changes, including the saved draft</strong>; the only preserved settings in production will be the current ones.
      submit: Set original values
      title: Discard all changes
    popupLaunch:
      close: Close window
      info: If you continue, you will <strong>activate Luigi's Box Autocomplete service</strong> and replace the current solution on your site.
      submit: Run the service
      title: Service launch
    popupLeave:
      close: Close window
      info: If you continue, you will <strong>lose all the unsaved changes</strong> you made in the settings.
      submit: Continue without saving
      title: You have not saved your changes
    preview:
      info: Enter any phrase to display search results with current settings. The changes are shown immediately, you do not need to save the settings in order to see them.
      placeholder: Enter any phrase
      title: Settings preview
    searchSettingsInfo: To fully utilize the potential of our product and intelligent search, it is necessary to <a href="{link}" target="_blank">set up and start the search</a>.
    status:
      active: Active
      disabled: Disabled
      waitingForLaunch: Awaiting launch
    structure:
      discardPopup:
        close: Close window
        info: If you choose to continue, you will remove the changes made for the product result type and restore the original settings.
        submit: Set to original values
        title: Settings to original values
      info: Set the composition of the results or their number for each type of found item. You can also set the recommended products display in the autocomplete after clicking the search field, even before typing the search phrase.
      otherSettings:
        hint: Text in the search field for the desktop
        hintTooltip: This text will be displayed as placeholder in the search field until the user starts typing.
        info: Additional settings and texts for autocomplete and search fields.
        locale: Language of texts
        localeTitle: Language settings
        localeTooltip: Translations from the selected dictionary will be used, you can edit them in the advanced settings
        placeholderText: Text in the search field for mobile devices
        placeholderTextTooltip: This text will be displayed by default in the search field on mobile devices until the user starts typing.
        priceFormatInfo: You can adjust how the prices should be displayed {link}.
        priceFormatInfoLink: in the integration settings
        priceFormatTitle: Price format settings
        title: Additional settings
        typingSuggestions: List search suggestions
        typingSuggestionsTooltip: It shows recommended queries to search while typing the phrase in the search box.
      title: Structure and texts
      typeSettings:
        discard: Original values
        doNotShow: Do not display
        heroName: Block name for "Hero" product
        heroNameTooltip: Name of the block TOP product in autocomplete, if it is activated.
        info: Displays recommended or related results for the product type according to the entered phrase.
        nameAndSize: Title for found records and number of results
        nameAndSizeTooltip: Name of the block and amount of displayed items in autocomplete.
        recommendNameAndSize: Title for recommended records and quantity
        recommendNameAndSizeTooltip: Name of the block and amount of displayed items with recommended products in autocomplete, when the user clicks on the search bar without entering the phrase.
        showAllText: Button text to move to search results
        showAllTextTooltip: Textin button that displays all found products
        showHero: Display "Hero" product
        showHeroTooltip: Show the TOP product in autocomplete as a separate block.
        type: Type
      types:
        article: Article
        brand: Brand
        category: Category
        item: Product
        product: Product
        query: Query
      typesTitle: Types of results in autocomplete
    title: Autocomplete setup
    toasts:
      activated: Autocomplete was activated
      disabled: Autocomplete was disabled
      popupBlocked: Opening of the preview failed, allow pop-up windows please
      saved: Autocomplete settings were saved
      savedAndPublished: Autocomplete settings were saved and published
    topInfo:
      active: "<strong>Luigi's Box Autocomplete service is active</strong>. You can turn it off (deactivate) in an emergency anytime. When deactivating, your last settings will be saved, and your original solution will start again on the page."
      buttonActivate: Activate autocomplete
      buttonDisable: Deactivate autocomplete
      disabled: "<strong>Luigi's Box Autocomplete service is inactive</strong>. However, you can restart the service at any time. When activated, the service starts with its last saved settings and replaces your original solution again."
      waitingForLaunch: You replace your original autocomplete with Luigi's Box by setting up and running the service. You can change these settings anytime or simply turn off the service if necessary.
    useDefaultTranslation: Leave blank to use the default dictionary
    welcome:
      button: Get started with your autocomplete setup
      info: Starting the service allows you to set up and add Luigi's Box Autocomplete to your website.
      title: Autocomplete setup
  analyticsInsider:
    dropdown:
      noOption: No options available
      select: Select
    header:
      description: Analytics Insider contains statistics of usage of Luigi's Box Services with ability to filter, aggregate and export data over a longer period of time.
      title: Analytics Insider
    metrics:
      autocomplete:
        assisted_conversions_count:
          description: Number of conversions that happened after a click on a result of Autocomplete
          label: Number of assisted conversions
        avg_pageviews_after_last_search:
          description: Average number of pageviews since the last Autocomplete in session
          label: Average pageviews after the last Autocomplete
        avg_pageviews_after_search:
          description: Average number of pageviews after Autocomplete
          label: Average pageviews after Autocomplete
        avg_query_reformulations:
          description: Average number of reformulations of query in Autocomplete
          label: Average number of reformulations
        cart_value_avg:
          description: Average price of items added to cart from Autocomplete
          label: Average cart value
        cart_value_sum:
          description: The total price of the items added to the cart from Autocomplete
          label: The total value of the cart
        clicks_count:
          description: Number of clicks on Autocomplete results
          label: Number of clicks
        conversions_count:
          description: Number of direct conversions from Autocomplete results
          label: Number of direct conversions
        description: Number of requests to Autocomplete endpoint
        direct_transactions_count:
          description: Number of transactions that occurred after adding an item from Autocomplete to the cart
          label: Number of direct transactions
        exit_rate:
          description: The ratio of sessions with exit from Autocomplete and the total number of sessions where Autocomplete was used
          label: Exit rate
        exits:
          description: Number of sessions that ended with exit after using Autocomplete
          label: Number of exits
        label: Autocomplete requests
        lists_count:
          description: Number of Autocomplete impressions
          label: Number of impressions
        no_result_exits:
          description: Number of sessions that ended with exit after Autocomplete with no results
          label: Number of exits after no-result
        sessions_count:
          description: The number of sessions that did not end up with bounce
          label: Number of sessions
        transactions_count:
          description: The number of transactions that occurred after clicking on a result of Autocomplete
          label: Number of transactions
      autocomplete_with_userid:
        description: Number of requests to Autocomplete endpoint that contain user identifier
        label: Autocomplete requests with User ID
      banners_impressions:
        description: Total number of banner impressions
        label: Banner impressions
      breakdown:
        assisted_conversions:
          converted:
            description: Number of sessions that contain at least one assisted conversion from Autocomplete or Search
            label: Sessions with assisted conversion
          not_converted:
            description: Number of sessions that do not contain any assisted conversion from Autocomplete or Search
            label: Sessions without assisted conversion
          not_converted_clicked:
            description: Number of autocompletes and searches with a click in sessions without assisted conversion from Autocomplete or Search
            label: Lists with click
          not_converted_clicked_rate:
            description: Ratio of autocompletes and searches with clicks in sessions without assisted conversion from Autocomplete or Search
            label: Click rate
          not_converted_no_results:
            description: Number of no-results autocompletes and searches in sessions without assisted conversion from Autocomplete or Search
            label: No-result lists
          not_converted_no_results_rate:
            description: Ratio of autocompletes and searches without results in sessions without assisted conversion from Autocomplete or Search
            label: No-result rate
          not_converted_not_clicked:
            description: Number of no-clicks autocompletes and searches in sessions without assisted conversion from Autocomplete or Search
            label: Lists without click
          not_converted_not_clicked_rate:
            description: The ratio of autocompletes and searches without click in sessions without assisted conversion from Autocomplete or Search
            label: No-click rate
          not_converted_searches:
            description: Number of searches in sessions without assisted conversion from Autocomplete or Search
            label: Lists
        direct_conversions:
          converted:
            description: Number of sessions that contain at least one direct conversion from Autocomplete or Search
            label: Sessions with direct conversion
          not_converted:
            description: Number of sessions that do not contain any direct conversion from Autocomplete or Search
            label: Sessions without direct conversion
          not_converted_clicked:
            description: Number of searches with a click in sessions without direct conversion from Autocomplete or Search
            label: Lists with click
          not_converted_clicked_rate:
            description: The ratio of listings with clicks in listings without direct conversion from Autocomplete or Search
            label: Click rate
          not_converted_no_results:
            description: Number of no-results searches in sessions without direct conversion from Autocomplete or Search
            label: No-result lists
          not_converted_no_results_rate:
            description: The ratio of listings without results in listings without direct conversion from Autocomplete or Search
            label: No-result rate
          not_converted_not_clicked:
            description: Number of no-clicks searches in sessions without direct conversion from Autocomplete or Search
            label: Lists without click
          not_converted_not_clicked_rate:
            description: The ratio of listings without click in listings without direct conversion from Autocomplete or Search
            label: No-click rate
          not_converted_searches:
            description: Number of searches in sessions without direct conversion from Autocomplete or Search
            label: Lists
        direct_transactions:
          converted:
            description: Number of sessions that contain at least one direct transaction from Autocomplete or Search
            label: Sessions with direct transaction
          not_converted:
            description: Number of sessions that do not contain any direct transaction from Autocomplete or Search
            label: Sessions without direct transaction
          not_converted_clicked:
            description: Number of searches with a click in sessions without direct transaction from Autocomplete or Search
            label: Lists with click
          not_converted_clicked_rate:
            description: The ratio of listings with clicks in listings without direct transaction from Autocomplete or Search
            label: Click rate
          not_converted_no_results:
            description: Number of no-results searches in sessions without direct transaction from Autocomplete or Search
            label: No-result lists
          not_converted_no_results_rate:
            description: The ratio of listings without results in listings without direct transaction from Autocomplete or Search
            label: No-result rate
          not_converted_not_clicked:
            description: Number of no-clicks searches in sessions without direct transaction from Autocomplete or Search
            label: Lists without click
          not_converted_not_clicked_rate:
            description: The ratio of listings without click in listings without direct transaction from Autocomplete or Search
            label: No-click rate
          not_converted_searches:
            description: Number of searches in sessions without direct transaction from Autocomplete or Search
            label: Lists
        not_used_search:
          converted:
            description: Sessions with conversion, where there was no usage of Autocomplete nor Search
            label: Sessions without search with conversion
          description: Sessions where there was no usage of Autocomplete nor Search
          label: Sessions without search
          not_converted:
            description: Sessions without conversion, where there was no usage of Autocomplete nor Search
            label: Sessions without search without conversion
        transactions:
          converted:
            description: Number of sessions that contain at least one assisted transaction from Autocomplete or Search
            label: Sessions with transaction
          not_converted:
            description: Number of sessions that do not contain any assisted transaction from Autocomplete or Search
            label: Sessions without transaction
          not_converted_clicked:
            description: Number of searches with a click in sessions without assisted transaction from Autocomplete or Search
            label: Lists with click
          not_converted_clicked_rate:
            description: The ratio of listings with clicks in listings without assisted transaction from Autocomplete or Search
            label: Click rate
          not_converted_no_results:
            description: Number of no-results searches in sessions without assisted transaction from Autocomplete or Search
            label: No-result lists
          not_converted_no_results_rate:
            description: The ratio of listings without results in listings without assisted transaction from Autocomplete or Search
            label: No-result rate
          not_converted_not_clicked:
            description: Number of no-clicks searches in sessions without assisted transaction from Autocomplete or Search
            label: Lists without click
          not_converted_not_clicked_rate:
            description: The ratio of listings without click in listings without transaction from Autocomplete or Search
            label: No-click rate
          not_converted_searches:
            description: Number of searches in sessions without assisted transaction from Autocomplete or Search
            label: Lists
        used_search:
          description: Number of sessions where the Autocomplete or Search were used
          label: Search sessions
        used_search_rate:
          description: Ratio of sessions where Autocomplete or Search were used
          label: Used search rate
      cvr_assisted:
        description: Ratio of sessions with assisted conversion from Autocomplete or Search and sessions where Autocomplete or Search were used
        label: CVR (assisted conversions)
      cvr_direct:
        description: Ratio of sessions with direct conversion from Autocomplete or Search and sessions where Autocomplete or Search were used
        label: CVR (direct conversions)
      cvr_direct_transactions:
        description: Ratio of sessions with direct transaction from Autocomplete or Search and sessions where Autocomplete or Search were used
        label: CVR (direct transactions)
      cvr_from_sessions_without_search:
        description: Ratio of sessions with any conversion and sessions where Autocomplete or Search were not used
        label: CVR from sessions without search
      cvr_transactions:
        description: Ratio of sessions with transaction from Autocomplete or Search and sessions where Autocomplete or Search were used
        label: CVR (transactions)
      exit_rate_from_autocomplete:
        description: The ratio of sessions with exit from Autocomplete and the total number of sessions where Autocomplete was used
        label: Exit rate from Autocomplete
      exit_rate_from_plp:
        description: The ratio of sessions with exit from Product listing and the total number of sessions where Product listing was used
        label: Exit rate
      exit_rate_from_search:
        description: The ratio of sessions with exit from Search and the total number of sessions where Search was used
        label: Exit rate from Search
      items_in_catalog:
        description: Total number of items (all types) in catalog
        label: Number of items in the catalog
      pageviews:
        description: Number of pageviews
        label: Pageviews
      plp:
        assisted_conversions_count:
          description: Absolute number of assisted conversions after a click on a result of Product listing
          label: Number of assisted conversions
        avg_pageviews_after_last_search:
          description: Average number of pageviews since the last Product listing
          label: The average number of pageviews after the last listing
        avg_pageviews_after_search:
          description: Average number of pageviews after Product listing
          label: Average number of pageviews after listing
        avg_query_reformulations:
          description: Average number of reformulations
          label: Average number of changes in listing
        cart_value_avg:
          description: Average price of items added to cart from Product listing
          label: Average cart value
        cart_value_sum:
          description: The total price of the items added to the cart from Product listing
          label: The total cart value
        clicks_count:
          description: Number of clicks on a result from Product listing
          label: Number of clicks
        conversions_count:
          description: Number of conversions from results of Product listing
          label: Number of direct conversions
        direct_transactions_count:
          description: The number of transactions that occurred after adding an item from Product listing to the cart
          label: Number of direct transactions
        exit_rate:
          description: The ratio of sessions with exit from Product listing and the total number of sessions where Product listing was used
          label: Exit rate
        exits:
          description: Number of sessions that ended with exit after using Product listing
          label: Number of exits
        lists_count:
          description: Number of Product listing impressions
          label: Number of list impressions
        no_result_exits:
          description: Number of sessions that ended up leaving after Product listing with no results
          label: Number of exits after no-results
        sessions_count:
          description: The number of sessions that did not bounce
          label: Number of sessions
        transactions_count:
          description: The number of transactions that occurred after clicking on a result of Product listing
          label: Number of transactions
      product_listings:
        description: Number of requests to Product listing endpoint
        label: Product listing requests
      profiles:
        description: Number of users. Registered users are counted only once.
        label: Users
      recommender:
        assisted_conversions_count:
          description: Number of conversions that happened after a user clicked on a Recommender result
          label: Assisted conversions
          sessions:
            description: Number of sessions with conversion that happened after a user clicked on a Recommender result
            label: Assisted conversion sessions
          users:
            description: Number of users with conversion that happened after a user clicked on a Recommender result
            label: Users with assisted conversion
        assisted_conversions_cvr:
          description: Ratio of sessions with assisted conversion from Recommender and sessions with click from Recommender result
          label: CVR (assisted conversions)
        clicks_count:
          description: Number of clicks on a result of Recommender
          label: Clicks
          sessions:
            description: Number of sessions with a click on a result of Recommender
            label: Click sessions
          users:
            description: Number of users that clicked on a result of Recommender
            label: Users with a click
        conversions_count:
          description: Number of conversions that happened after a user clicked on a Recommender result
          label: Direct conversions
          sessions:
            description: Number of sessions with conversion that happened after a user clicked on a Recommender result
            label: Sessions with direct conversion
          users:
            description: Number of users with conversion that happened after a user clicked on a Recommender result
            label: Users with direct conversion
        conversions_cvr:
          description: Ratio of direct conversions count and clicks count from Recommender
          label: CVR (direct conversions)
        ctr:
          description: Ratio of clicks count and impressions count from Recommender
          label: CTR
        lists_count:
          description: Number of Recommender impressions
          label: List impressions
          sessions:
            description: Number of sessions with Recommender impressions
            label: Sessions with list impression
          users:
            description: Number of users that have Recommender impressions
            label: Users with list impression
        requests:
          description: Number of requests to Recommender endpoint
      search:
        description: Number of requests to Search endpoint
        label: Search requests
      search_results:
        assisted_conversions_count:
          description: Absolute number of conversions after a click on a Search result
          label: Number of assisted conversions
        avg_pageviews_after_last_search:
          description: Average number of pageviews since the last Search
          label: The average number of pageviews after the last search
        avg_pageviews_after_search:
          description: Average number of pageviews after Search
          label: Average number of pageviews after a search
        avg_query_reformulations:
          description: Average number of reformulations
          label: Average number of reformulations
        cart_value_avg:
          description: Average price of items added to cart from Search
          label: Average cart value
        cart_value_sum:
          description: The total price of the items added to the cart from Search
          label: Total cart value
        clicks_count:
          description: Number of clicks on a result of Search
          label: Number of clicks
        conversions_count:
          description: Number of conversions from results of Search
          label: Number of direct conversions
        direct_transactions_count:
          description: The number of transactions that occurred after adding an item from Search to the cart
          label: Number of direct transactions
        exit_rate:
          description: The ratio of sessions with exit from Search and the total number of sessions where Search was used
          label: Exit rate
        exits:
          description: Number of sessions that ended with exit after using Search
          label: Exits
        lists_count:
          description: Number of Search results impressions
          label: List impressions
        no_result_exits:
          description: Number of sessions that ended up leaving after Search with no results
          label: Exits after no-results
        sessions_count:
          description: The number of sessions that did not bounce
          label: Sessions
        transactions_count:
          description: The number of transactions that occurred after clicking on a result of Search
          label: Transactions
      search_with_userid:
        description: Number of requests to Autocomplete endpoint that contain user identifier
        label: Autocomplete requests with User ID
      sessions_count:
        description: The number of sessions that did not bounce
        label: Sessions
      sessions_with_consent_count:
        description: Number of session where user granted cookie consent for marketing purposes. These sessions can be used to improve services that take into account a user (personalization, AB tests, etc.).
        label: Sessions with consent
      top_items:
        description: Number of requests to Top items endpoint
        label: Top Items requests
      trending_queries:
        description: Number of requests to Trending queries endpoint
        label: Trending Queries requests
    modal:
      body: Are you sure you want to <strong>remove custom preset “{name}” that you set?</strong> You can't take back this change.
      close: Close
      remove: Remove custom preset
      title: Remove custom preset
    notifications:
      createdPreset: Preset created successfully
      error: Error
      removedPreset: Preset removed successfully
      success: Success
      updatedPreset: Preset updated successfully
    presets:
      description: This screen allows you to create custom Analytics Insider presets. You can choose from a list of available metrics and select the stats you want to apply to them.
      edit: Edit
      globalDescription: "<b>Set as a global preset.</b> The global preset is available and visible in Analytics Insider for all clients."
      internalPreset: Internal preset
      listOfMetrics: List of Metrics
      metrics: Metrics
      name: Name of custom preset
      nameRequired: Preset name is required
      remove: Remove
      removePreset: Remove preset
      savePreset: Save preset
      searchInList: Search in list
      selectedText: Selected {selected} from {total}
      setInternalPreset: Set as Internal Preset
      title: Create custom preset
    services:
      autocomplete: Autocomplete
      product_listing: Product Listing
      recommender: Recommender
      recommenders: Recommenders
      search_autocomplete: Search and Autocomplete
      search_results: Search
      units: Usage
    table:
      header:
        addCustomPreview: Add custom preset
        export:
          selectedCsv: Selected data (*.csv)
          selectedXlsx: Selected data (*.xlsx)
          wholeCsv: Whole project (*.csv)
          wholeXlsx: Whole project (*.xlsx)
        exportData: Export data
        groupBy: 'Group by:'
        metrics: Metrics
        preset: 'Preset:'
        presetTooltip: Choose from a predefined set of metrics or create a custom one.
        selectPreset: Select Preset
        setCurrentSite: Set to current site
        sites: Sites
        statsFor: 'Stats for:'
        times:
          days: Days
          months: Months
          weeks: Weeks
          years: Years
        unsetPreset: Unset preset
  app:
    add: Save
    backHome: Back to home
    close: Close
    codeNotFound: Code for insertion was not found.
    customization:
      list:
        customization: Customization
        detail: Detail
        query: Query
    language:
      interfaceLanguage: Interface language
    noOptions: Sorry, we found nothing on your query.
    notFound: "Page Not Found 🕵🏻‍♀️"
    notFoundDesc: "Oops! 😖 The requested URL was not found on this server."
    notifications:
      leave: Are you sure you want to leave this site? Some settings have not been saved.
    remove: Delete
  assistant:
    assistantActivation:
      moreInfo: More about Shopping Assistant
      moreInfoLink: https://www.luigisbox.com/products/shopping-assistant/
    assistantEditor:
      buttons:
        addNewAttribute: Add new attribute
        analytics: Analytics
        archive: Archive
        basicSettings: Basic settings
        buildMySA: Build My Shopping Assistant
        cancel: Cancel
        close: Close
        continueAndGenerate: Continue and Generate the Assistant
        customizeYourself: Create Manually
        deactivate: Deactivate
        discardUnsavedChanges: Discard unsaved changes
        duplicate: Duplicate
        edit: Edit
        editDialog: Edit dialogue
        editPlacement: Edit placement
        editStyle: Edit style
        generateAssistant: Generate Assistant
        howDoesWork: How does Shopping Assistant work?
        livePreview: Live preview
        preview: Preview
        publish: Publish
        publishChanges: Publish changes
        restore: Restore
        save: Save
        saveDraft: Save draft
        unarchive: Unarchive
      create:
        alreadyInUse: This name is already in use, please try another one.
        categoriesDescription: Choose all categories from which products should be displayed in the assistant.
        categoriesPlaceholder: Categories
        chooseCategories: Choose product categories
        effectiveInputDescription: 'Briefly describe what you need help with, provide clear and specific input, such as: "I want to simplify tire selection for users." Include anything that reflects your goals—training documents, examples, notes, or even partial drafts. However, you don''t have to provide any input. The Assistant can generate the dialogue on its own.'
        effectiveInputHeading: How to give the Assistant effective input?
        formalTone: Formal Tone
        formalToneTooltip: Always communicate in a formal, professional style. Write in complete sentences and avoid abbreviations, slang, or emojis. Remain polite, factual, and consistently professional.
        includeContentPlaceholder: Include your content here ...
        informalTone: Informal Tone
        informalToneTooltip: Keep the tone friendly and relaxed. Always communicate in an informal register. Use casual "you/your" language and feel free to add occasional emojis or everyday slang when it feels natural.
        inputsAndResources: Inputs and Resources for the Assistant
        nameDescription: The name is only visible in the assistant list and won't appear on your website.
        namePlaceholder: Name
        nameYourAssistant: Name your assistant
        optional: "(Optional)"
        playfulTone: Playful Tone
        playfulToneTooltip: Always communicate in a playful, upbeat style. Sprinkle in light-hearted jokes, exclamation marks, and rhetorical questions to keep things lively. Use casual "you/your" language and feel free to add fun emojis.
        provideRequirements: Provide the requirements and reference materials that the assistant should use in generating dialogue.
        referenceInstructions: Reference Instructions and Content for the Assistant
        setNameAndCategory: Set a name for your assistant and choose a category
        title: New assistant
        toneOfVoice: Tone of Voice
        toneOfVoiceDescription: Specify your assistant's tone of voice to match your needs. This option cannot be changed after saving.
      detail:
        addAttributeInstructions: First, add attribute, then the values from your catalog for the selected answer. As a result the user will receive filtered products.
        addParameters: Add one or more parameters for this answer
        addPlacement: Add placement
        addPlacementBeforePreview: Add a placement before previewing
        answer: 'Answer: '
        archived: Archived
        attributeValue: 'Attribute value: '
        categoriesDescription: Choose all categories from which products should be displayed in the assistant.
        categoriesPlaceholder: Categories
        chooseCategories: Choose product categories
        chosenAttributes: 'Chosen attributes: '
        createFilter: Create filter
        deactivated: Deactivated
        deleteCategoryWarning: Deleting an existing category may affect predefined settings and cause issues.
        filterInstructions: Filter the result in the assistant by entering an attribute value or a dynamic context value.
        lastSave: 'last save:'
        modalAccept: Accept
        modalCreateFilters: Create filters to each answer
        modalGeneratedAssistant: Your assistant was generated but this <b>is not yet complete.</b>
        modalInstructions: To ensure your assistant works, you have to manually assign filters to each answer.
        modalToolbarInstructions: Select an answer, then click the Create filter button in the toolbar below.
        nameDescription: The name is only visible in the assistant list and won't appear on your website.
        namePlaceholder: Name
        nameYourAssistant: Name your assistant
        noMatchingProducts: The combination of attributes you've entered doesn't match any product in your catalog. Please adjust the attributes and their values above.
        other: Other
        previewOfResults: Preview of results
        previousAttributes: 'Previous attributes: '
        publishInfo: By clicking Publish, your assistant <b>will go live on your website.</b>
        published: Published
        question: 'Question: '
        search: Search results
        selectParameter: Select parameter to add
        selectValues: Select values
        simulatedView: A simulated view of up to 30 products based on your current attributes selection. Product visibility may differ based on factors like availability, ongoing campaigns, etc.
        sureOfPublish: Are you sure you want to publish your assistant?
        toPreviewText: To preview your shopping assistant, please add a placement where it should appear first.
        unpublishedChanges: "✏️ Unpublished changes"
        waitingForPublish: Draft
        warning: Warning!
      historySidebar:
        changedBy: Changed by
        latest: latest
        loadMore: Load more
        modalDescription: This assistant will be restored as a draft. If it’s an older version, you won’t lose any changes – it will simply be saved as a new version of the assistant.
        modalTitle: Restoring an older version
        restoreVersion: Restore version
        showDetail: Show detail
        version: version
        versionHistory: Version history
      list:
        changedBy: 'Changed by: '
        changes: Changes
        description: Create or import shopping assistants to guide users to the right product with simple questions.
        heroDescription: Shopping Assistant is a tool that guides users to the right product by asking simple questions. With each answer, it filters the options, narrowing the selection to match their needs.
        heroTitle: Shopping Assistant
        listOf: List of assistants
        name: Assistant's Name
        numOfRows: Number of rows
        of: of
        page: Page
        search: Search
        state: State
        title: Assistants list
  assistantAnalysis:
    allAssitantsTable:
      title: All assistants
    cards:
      answerDetail:
        completion_rate:
          info: The percentage of users who completed the assistant flow after selecting this answer.<br /><br />A completion is counted when a user selects an answer for the question and continues along a path that ends with the final screen displaying the top product(s).
          title: Completion rate
          title2: Completion rate
        conversion_rate:
          info: The percentage of users who made at least one direct conversion from a product shown after selecting this answer.<br /><br />A direct conversion occurs when there is a conversion event (e.g., add to cart) on a product that the assistant showed.
          title: CVR
          title2: Products Conversion rate
        ctr:
          info: The percentage of users who have clicked on a product after selecting this answer.
          title: CTR
          title2: Products Click-through rate
      globalDetail:
        activations_ctr:
          info: The percentage of users who activated the assistant of all who saw it.
          title: Activation rate
          title2: Activation rate
        average_order_value:
          info: Average transaction value from sessions in which the assistant was used.<br /><br />The value is calculated as the total price of all items purchased within a single transaction.
          title: Average order value
          title2: Average order value
        completion_rate:
          info: The percentage of users who saw the last step of the assistant (with top products) of all those who used it.
          title: Completion rate
          title2: Completion rate
        conversion_rate:
          info: The percentage of users who generated a direct conversion out of all the users who used the assistant.<br /><br />Direct conversion occurs when a user converts (e.g., adds to cart) on a product that the assistant showed.
          title: CVR
          title2: Products Conversion rate
        conversions:
          info: Number of direct conversions. Direct conversion occurs when a user converts (e.g., adds to cart) on a product that the assistant showed.
          title: Conversions
          title2: Conversions
        exit_rate:
          info: Percentage of sessions where the users bounced after using the assistant.
          title: Exit rate
          title2: Exit rate
        transactions:
          info: Number of transactions during sessions where the users used the assistant.
          title: Transactions
          title2: Transactions
        users:
          info: Number of users who used the assistant.
          title: Users
          title2: Users
      questionDetail:
        completion_rate:
          info: The ratio of times users completed the assistant flow out of all impressions when the question appeared.<br /><br />A completion is counted when a user selects an answer for the question and continues along a path that ends with the final screen displaying the top product(s).
          title: Completion rate
          title2: Completion rate
        conversion_rate:
          info: The ratio of product direct conversions to the total number of times the question appeared.<br /><br />A direct conversion occurs when there is a conversion event (e.g., add to cart) on a product that the assistant showed.
          title: CVR
          title2: Products Conversion rate
        ctr:
          info: The ratio of impressions that led to a product click to times the question appeared.
          title: CTR
          title2: Products Click-through rate
        exit_rate:
          info: Percentage of sessions where the user bounced after seeing the assistant's question.
          title: Exit rate
          title2: Exit rate
        nrr:
          info: Percentage of question impressions where no products were available to display (no results).
          title: No-results Rate
          title2: No-results Rate
        restart_rate:
          info: Percentage of question impressions that were followed by a restart of the assistant dialog flow.
          title: Restart rate
          title2: Restart rate
        users:
          info: Number of users who selected the answer.
          title: Users
          title2: Users
      quickViewDetail:
        activations_ctr:
          info: The percentage of users who activated the assistant of all who saw it.
          title: Activations rate
          title2: Activations rate
        average_order_value:
          info: Average transaction value from sessions in which the assistant was used.<br /><br />The value is calculated as the total price of all items purchased within a single transaction.
          title: Average order value
          title2: Average order value
        completion_rate:
          info: The percentage of users who saw the last step of the assistant (with top products) of all those who used it.
          title: Completion rate
          title2: Completion rate
        conversion_rate:
          info: The percentage of users who generated a direct conversion out of all the users who used the assistant.<br /><br />Direct conversion occurs when a user converts (e.g., adds to cart) on a product that the assistant showed.
          title: CVR
          title2: Products Conversion rate
        conversions:
          info: Number of direct conversions. Direct conversion occurs when a user converts (e.g., adds to cart) on a product that the assistant showed.
          title: Conversions
          title2: Conversions
        exit_rate:
          info: Percentage of sessions where the users bounced after using the assistant.
          title: Exit rate
          title2: Exit rate
        transactions:
          info: Number of transactions during sessions where the users used the assistant.
          title: Transactions
          title2: Transactions
        users:
          info: Number of users who used the assistant.
          title: Users
          title2: Users
      statsDetail:
        activations_ctr:
          info: The percentage of users who activated the assistant of all who saw it.
          title: Activations rate
          title2: Activations rate
        average_order_value:
          info: Average transaction value from sessions in which the assistant was used.<br /><br />The value is calculated as the total price of all items purchased within a single transaction.
          title: Average order value
          title2: Average order value
        completion_rate:
          info: The percentage of users who saw the last step of the assistant (with top products) of all those who used it.
          title: Completion rate
          title2: Completion rate
        conversion_rate:
          info: The percentage of users who generated a direct conversion out of all the users who used the assistant.<br /><br />Direct conversion occurs when a user converts (e.g., adds to cart) on a product that the assistant showed.
          title: CVR
          title2: Products Conversion rate
        conversions:
          info: Number of direct conversions. Direct conversion occurs when a user converts (e.g., adds to cart) on a product that the assistant showed.
          title: Conversions
          title2: Conversions
        exit_rate:
          info: Percentage of sessions where the users bounced after using the assistant.
          title: Exit rate
          title2: Exit rate
        transactions:
          info: Number of transactions during sessions where the users used the assistant.
          title: Transactions
          title2: Transactions
        users:
          info: Number of users who used the assistant.
          title: Users
          title2: Users
    global:
      createNewAssistant: Create New Assistant
      subTitle: This dashboard provides an overview of all your shopping assistants and their performance. It includes individual assistant metrics as well as aggregated data to help you track overall impact and identify key trends
    intro:
      addExisting: Add Existing Assistants
      buildAssistant: Build My Shopping Assistant
      desc: Shopping Assistant is a tool that guides users to the right product by asking simple questions. <b>Build and publish</b> a shopping assistant to start collecting data and see insights here.
      howItWorks: How does Shopping Assistant work?
      title: Assistant Analytics
    numberOfUsers: "{number} users"
    openInEditor: Open in editor
    questionDetailTableAnswers:
      title: Answers overview
    questionDetailTableResults:
      title: First 10 products (results) in this question
    sidebarDetail:
      answer: Answer
      answerPerformance: Answer performance
      close: Close
      followingQuestions: The following questions in the path by the answers
      goToFullAnalysis: Go to full analytics
      parentQuestion: Parent question
      previewOfFilter: Preview of created filter
      previousQuestion: Previous question in path
      question: Question
      questionPerformance: Question performance
      title: Analytics
      titleAnswer: Analytics of Answer
      titleQuestion: Analytics of Question
    table:
      assistantPerformance: Assistant performance
      numberOfRows: Number of rows
      searchAssistant: Search assistant
    tableHeaders:
      ALL_ASSISTANTS_GLOBAL:
        activations_ctrTooltip: The percentage of users who activated the assistant of all users who saw the assistant.
        completion_rateTooltip: The percentage of users who saw the last step of the assistant (with top products) of all the users who used the assistant.
        conversion_rateTooltip: The percentage of users who had direct conversions among all the users who used the assistant. Direct conversion occurs when a user converts (e.g., adds to cart) on a product that was shown by the assistant.
        exit_rateTooltip: Percentage of sessions where the user bounced after using an assistant.
        nameTooltip: Identifier of the assistant
        usersTooltip: Number of users who used the assistant.
      ASSISTANT_DETAIL_ALL:
        average_positionTooltip: The average ranking position of a product displayed in the assistant.
        conversionsTooltip: The absolute number of direct conversions on a product.<br /><br />A direct conversion occurs when there is a conversion event (e.g., add to cart) on a product that was shown in the assistant.
        ctrTooltip: The ratio between product impressions that resulted in a click (on product) and those where the product was shown in the assistant dialog.
        cvrTooltip: The ratio between product impressions that resulted in a direct conversion and those where the product was shown in the assistant dialog.<br /><br />A direct conversion occurs when there is a conversion event (e.g., add to cart) on a product that was shown in the assistant.
        impressionsTooltip: The number of times a product was shown in the assistant dialog.
        nameTooltip: Title of the product form catalog
      ASSISTANT_DETAIL_ANSWERS:
        completion_rateTooltip: Percentage of users who completed the assistant after selecting this answer.<br /><br />A completion occurs when, after selecting an answer for the question, the user follows a path that leads to the assistant’s final screen displaying the top product(s).
        conversion_rateTooltip: The percentage of users who made at least one direct conversion from the products shown after selecting this answer.<br /><br />A direct conversion occurs when there is a conversion event (e.g., add to cart) on a product that was shown in the assistant.
        ctrTooltip: The percentage of users who have clicked on a product after selecting this answer.
        nameTooltip: Title of the answer
        usersTooltip: Number of users who selected the answer.
      ASSISTANT_DETAIL_ANSWERS_OVERVIEW:
        completion_rateTooltip: Percentage of users who completed the assistant after selecting this answer.<br /><br />A completion occurs when, after selecting an answer for the question, the user follows a path that leads to the assistant’s final screen displaying the top product(s).
        cvrTooltip: The percentage of users who made at least one direct conversion from the products shown after selecting this answer.<br /><br />A direct conversion occurs when there is a conversion event (e.g., add to cart) on a product that was shown in the assistant.
        items_ctrTooltip: The percentage of users who have clicked on a product after selecting this answer.
        nameTooltip: Answer name
        usersTooltip: Number of users who selected the answer.
      ASSISTANT_DETAIL_FIRST_POSITION:
        average_positionTooltip: The average ranking position of a product displayed in the assistant.
        conversionsTooltip: The absolute number of direct conversions on a product.<br /><br />A direct conversion occurs when there is a conversion event (e.g., add to cart) on a product that was shown in the assistant.
        ctrTooltip: The ratio between product impressions that resulted in a click (on product) and those where the product was shown in the assistant dialog.
        cvrTooltip: The ratio between product impressions that resulted in a direct conversion and those where the product was shown in the assistant dialog.<br /><br />A direct conversion occurs when there is a conversion event (e.g., add to cart) on a product that was shown in the assistant.
        impressionsTooltip: The number of times a product was shown in the assistant dialog.
        nameTooltip: Title of the product form catalog
      ASSISTANT_DETAIL_QUESTIONS:
        answers_ctrTooltip: The percentage of users who selected one of the answers for this question.
        completion_rateTooltip: The percentage of users who completed the assistant after seeing this question.<br /><br />A completion occurs when, after selecting an option for the question, the user follows a path that leads to the assistant’s final screen displaying the top product(s).
        conversion_rateTooltip: The percentage of users who have completed at least one direct conversion on a product displayed in this question.<br /><br />A direct conversion occurs when there is a conversion event (e.g., add to cart) on a product that was shown in the assistant.
        ctrTooltip: The percentage of users who have clicked on a product displayed in this question.
        exit_rateTooltip: Percentage of sessions where the user bounced after seeing the question of an assistant.
        items_ctrTooltip: The percentage of users who have clicked on a product displayed in this question.
        nameTooltip: Name of the question
        usersTooltip: Number of users who saw the question.
      ASSISTANT_DETAIL_QUESTION_PRODUCTS_OVERVIEW:
        average_positionTooltip: The average ranking position of a product displayed in the assistant.
        ctrTooltip: The ratio between product impressions that resulted in a click (on product) and those where the product was shown in the assistant dialog.
        cvrTooltip: The ratio between product impressions that resulted in a direct conversion and those where the product was shown in the assistant dialog.<br /><br />A direct conversion occurs when there is a conversion event (e.g., add to cart) on a product that was shown in the assistant.
        impressionsTooltip: The number of times a product was shown in the assistant dialog.
        nameTooltip: Name of the product
      activations_ctr: Activations rate
      answer: Answer
      answers_ctr: Answers CTR
      assistantName:
        ALL_ASSISTANTS_GLOBAL:
          name: Assistant name
        ASSISTANT_DETAIL_ALL:
          name: Product name
        ASSISTANT_DETAIL_ANSWERS:
          name: Answer name
          question: Question
        ASSISTANT_DETAIL_ANSWERS_OVERVIEW:
          name: Answer
        ASSISTANT_DETAIL_FIRST_POSITION:
          name: Product name
        ASSISTANT_DETAIL_QUESTIONS:
          name: Question name
        ASSISTANT_DETAIL_QUESTION_PRODUCTS_OVERVIEW:
          name: Product name
      average_position: Avg. position
      completion_rate: Completion rate
      conversion_rate: CVR
      conversions: Converions
      ctr: CTR
      cvr: CVR
      exit_rate: Exit rate
      impressions: Impressions
      items_ctr: CTR
      position: "#"
      users: Users
    tabs:
      ASSISTANT_DETAIL_ALL: All products in SA
      ASSISTANT_DETAIL_ANSWERS: Answers
      ASSISTANT_DETAIL_FIRST_POSITION: Top 3 products
      ASSISTANT_DETAIL_QUESTIONS: Questions
  auditLog:
    _errors:
      load: Failed to load activities
    _filters:
      query: Search
      types: Event type
      users: User
    _supportUser: Luigi's Box
    _types:
      create_ab_test: AB test created
      create_boosted_item: Boosted item created
      create_boosted_term: Boosted term created
      create_campaign: Banner campaign created
      create_category_facets: Category facets created
      create_configuration: Configuration created
      create_configuration_version: Configuration version created
      create_custom_keyword: Custom keyword created
      create_fixit: Fixit created
      create_plp_campaign: Product listing campaign created
      create_query_customization: Query customization created
      create_query_rewrite: Query rewrite created
      create_recommender_rule: Recommender rule created
      create_search_options_configuration: Search configuration created
      create_segment: Segment created
      create_site_configuration: Site configuration created
      create_sites_group: Sites group created
      create_synonym: Synonym created
      create_synonym_bulk: Synonyms in bulk created
      destroy_ab_test: AB test deleted
      destroy_boosted_item: Boosted item deleted
      destroy_boosted_term: Boosted term deleted
      destroy_campaign: Banner campaign deleted
      destroy_category_facets: Category facets deleted
      destroy_custom_keyword: Custom keyword deleted
      destroy_fixit: Fixit deleted
      destroy_plp_campaign: Product listing campaign deleted
      destroy_query_customization: Query customization deleted
      destroy_query_rewrite: Query rewrite deleted
      destroy_recommender_rule: Recommender rule deleted
      destroy_segment: Segment destroyed
      destroy_sites_group: Sites group destroyed
      destroy_synonym: Synonym deleted
      destroy_synonym_bulk: Synonyms in bulk deleted
      reset_catalog: Catalog reset
      site_access: Site access
      update_ab_test: AB test updated
      update_boosted_item: Boosted item updated
      update_boosted_term: Boosted term updated
      update_campaign: Banner campaign updated
      update_category_facets: Category facets updated
      update_configuration: Configuration updated
      update_configuration_version: Configuration version updated
      update_custom_keyword: Custom keyword updated
      update_feed_plan: Feed plan updated
      update_fixit: Fixit updated
      update_plp_campaign: Product listing campaign updated
      update_query_customization: Query customization updated
      update_query_rewrite: Query rewrite updated
      update_recommender_rule: Recommender rule updated
      update_segment: Segment updated
      update_site_configuration: Site configuration updated
      update_sites_group: Sites group updated
      update_synonym: Synonym updated
    description: The audit log records all important events in the settings, the time they occurred, and the user who made the change.
    diffAfter: 'After change:'
    diffBefore: 'Before change:'
    emptyMessage: No records match the search criteria
    filter:
      apply: Filter list
      clear: Clear all filters
      close: Close
      heading: Filter output
    pager:
      of: from
      page: 'Page:'
      pageSize: 'Number of rows:'
    tableHeading:
      detail: Event details
      number: "#"
      timestamp: Date and time
      type: Event type
      user: User
    title: Audit Log
    topFilter:
      filterButton: Filter results
      filters: 'Filters:'
      heading: 'List activities by: {sort}'
      search: 'Search records:'
  categoryFilters:
    confirmPopup:
      close: Close
      confirmDelete: Are you sure you want to <b>delete the "{category} category customization"?</b> You can't take this action back.
      confirmDeleteFilter: Are you sure you want to delete <b>{filter}</b> filter?
      confirmDiscard: Are you sure you want to <b>discard all changes</b>?
      confirmRemove: Are you sure you want to <b>remove all filters</b>?
      delete: Delete
      discard: Discard
      remove: Remove
      removeFilters: Remove all filters
    detail:
      about: About customization
      aboutText: This customization will change a common set of attributes on the product listing for the selected category. You can manually set up filters, their ordering, and the number of values for your customers on the product listing.
      addFilters: Add selected filters
      addNextFilter: Add next preferred filter
      addRecommended: Add recommended filters
      alphabet: Alphabet
      categoryFilters: Product listing filters
      categoryName: Type category name
      continue: Continue to customization
      customizing: 'Customizing facets for:'
      customizingText: This customization will change a common set of attributes on the product listing for the selected category.
      delete: Delete customization
      deleteFilter: Delete filter
      deleteShort: Delete
      discard: Discard changes
      enterCategory: Enter the category for customization
      filtersAboutText: You can manually set up filters, their ordering, and the number of values for your customers on the product listing. The recommended parameters include price range, availability, and parameters for segment-dependent items such as color, size, etc.
      howCustomizingWork: What does the Customizing facets mean?
      howFiltersWork: How do filters work?
      noAddedFilters: No filters have been added.
      notDisplayDynamicFilters: Only show the customized facets in this list of products
      notDisplayFiltersDesc1: If you choose to show only the customized facets, the user will always see only the facets you have set above.
      notDisplayFiltersDesc2: Otherwise, static facets and dynamic facets can be shown also.
      number_of_products: Number of products
      recommendedText: If you want to make your work easier, <b>add recommended default filters and settings by Luigi's Box</b> first and change them however you need.
      removeFilters: Remove all filters
      save: Save customization
      saveChanges: Save changes
      saveShort: Save
      selectFilters: Select your preferred filters
    item:
      allItemsTooltip: You can change the maximum number of items (parameters) in the added facet. If you choose the "without limit" option, it will add all available items (parameters) of added facet.
      confirm: confirm
      filterName: Filter name
      filters: Filters
      nameTooltip: This name is also displayed in the filters on the page. But sometimes, it could look more user-friendly, so we recommend changing it to something more understandable. It will not affect its functionality. You can rename it to the original name at any time.
      numberOfItems: Number of all items
      pressEnter: Press enter to
      resetName: Reset name
      sortItems: Sort items by
      sortTooltip: You have two options to sort items (parameters) of added facet. You can sort items by a number of products (parameter with the most products first) or use alphabetical sorting.
      visibleItems: Visible items
      visibleItemsTooltip: You can change the number of items (parameters) defaults visible in the added facet. There is the "More" button after them.
      withoutLimit: Without limit
    list:
      category: Category
      customization: Customization
      customizeNew: Customize new category
      customized: Customized categories ({number})
      deleteCustomization: Delete customization
      details: Details
      editCustomization: Edit customization
      howItWorks: How it works?
      ownFilters: You have set your own {number} preferred filters on the product listing for this category.
      subTitle: Enhance your product listing with manual setup filters of selected categories.
      title: Product Listing Facets Customization
    notifications:
      changesDiscarded: Changes discarded
      deleted: Customization deleted
      detailLoadError: Detail load error
      duplicitCategory: This category has already been customized. Please edit the category instead
      emptyRecommendedFilters: Could not recommend any filters. Please run faceter first
      invalidCategoryAttribute: Could not find attribute '{value}' in your category structure
      invalidCategoryAttributeValue: Value '{value}' can not be used. Please use a value with exactly one member.
      invalidCategoryPath: Could not determine 'Category path'
      notFoundError: Could not find requested category facets
      recommendedFilters: Recommended filters added
      recommendedFiltersError: Failed to recommended filters
      saved: Customization saved
  configurations:
    _errors:
      disable: Error occurred while disabling configuration
      load: Error occurred while loading data
      save: Error occurred while saving configuration
      saveNote: Error occurred while saving note
      titleConflict: Configuration with this title already exists
    all:
      activeDescription: Overview of all configurations currently used on the sites and in A/B tests in the entire project.
      activeHeading: Active configurations
      activeList: Active configurations
      createConfiguration: Create new configuration
      createdConfigurations: Created configurations
      empty: No configurations to show
      howItWorks: How do configurations work?
      inactiveDescription: Overview of all configurations are not currently used on the sites and in A/B tests in the entire project.
      inactiveHeading: Non-active configurations
      inactiveList: Non-active configurations
      title: All configurations in project
    confirmation:
      discard: Discard this change
      fromVersion: You want to restore the older version of configuration. <strong>The selected version will be added as a new version of the configuration</strong>. You can change the configuration to the previous version anytime you need.
      save: Confirm and save the change
      selected: 'Selected configuration:'
      title: Confirmation of the new site configuration
      version: "(v{version})"
      whereToUse:
        all: All sites in the project "{project}"
        allInfo: This configuration will be set as the default configuration on all pages in the project.
        group: Specific group in the project
        groupInfo: Choose one or more specific groups in the project where you want to use the configuration.
        site: Only on the site "{site}"
        siteInfo: This configuration will be set as default configuration only for this one site in project.
        title: Where do you want to use this configuration?
    createNew:
      configurationInfo_html: 'Configurations are <strong>shared across all pages in the project</strong>. You can create configuration for planned A/B tests and seasonal promotions or optimize services as needed. <strong>If a configuration is used</strong> (eq.: in an A/B test), it won''t be possible to further modify it. However, you can create a <strong>new one by copying it</strong>.'
      newConfiguration: New configuration
      noteLabel: Add a notes or description
      notePlaceholder: You can add a notes or description of the prepared configuration...
      title: Creating a new configuration
      titleInfo_html: "<strong>Choose a title with context:</strong><br>PersonalizationOFF, Black Friday, Margin test..."
      titleLabel: What is the title of configuration?
    detail:
      backToLatestVersion: Back to latest version (v{version})
      changedSettings: Changed settings
      confirmCloseModal:
        back: Discard changes
        stay: Stay on this page
        text: You have unsaved changes in your settings for this configuration. If you go back, you will loose them.
        title: Unsaved changes
      create: Save configuration
      disable: Disable configuration
      discardChanges: Discard all changes
      optionsButton: Configuration options
      restoreThisVersion: Restore this version (v{version})
      save: Save changes in configuration
      settings:
        ranking:
          description: 'Various factors (e.g.: ranking signals) influencing the relevance and ordering of search results returned by a query for autocomplete and search.'
          title: Relevance And Ranking
      settingsGroups:
        product_listing_page: Product listing page
        recommenders: Recommenders
        search_and_autocomplete: Search and Autocomplete
      settingsOverlay:
        back: Back to configuration
        info: You are currently in the settings of a specific site configuration.
        resetToPrevious: Reset to previous settings
        restore: Restore factory settings
        save: Save changes
        top: Configurations settings
      siteConfigurationTitle: Site configuration
      siteOptionsButton: Site configuration options
      switch:
        close: Close
        confirm: Confirm change
      title: Detail of configuration
      use: Use as site configuration
      versionTitle: Configuration history
      whatIsConfiguration: What is site cofiguration?
    filter:
      pageSize: 'On page:'
      search: 'Search in configurations:'
    history:
      changedBy: 'Changed by:'
      currentVersion: Current version
      lastEdit: 'Last edit:'
      olderVersions: Older versions
      showDetail: Show detail
      title: Version history of the configuration
      version: Version
    item:
      buttons:
        change: Change configuration for this site
        create: Create new configuration from default
        detail: Detail
        disable: Disable
        duplicate: Duplicate
        edit: Edit
        editCurrent: Edit the current configuration
        history: History
        more: Show more options
        note: Note
      notUsed: Not used
      usedBySites: Used by {n} site | Used by {n} sites
      usedBySitesAndTests1: Used by {n} site | Used by {n} sites
      usedBySitesAndTests2: and {n} test | and {n} tests
      usedByTests: Used by {n} test | Used by {n} tests
      usedOnThisSite: Used on this site
      version: "(v{version})"
    noteModal:
      close: Close
      discard: Discard changes
      edit: Edit note
      empty: No note for this configuration yet
      placeholder: You can add a description of the configuration...
      save: Save note
      title: Note
    overview:
      disable: Disable configuration
      edit: Edit the configuration
      info1: Are you sure you want to save changes in the configuration?
      info2: Your changes will affect all the below project sites that use this configuration as active site configuration
      noSites: This change will not apply to any site
      save: Save and apply changes to these pages
      site: Site
      siteConfiguration: Site configuration
      title: Configuration overview
    usedBy:
      info_html: Overview of project sites where the given configuration is currently being used <strong>in the specified version</strong>
      noSites: Currently not in use on any site
      noTests: Currently not in use in any test
      site: Site
      siteConfiguration: Site configuration
      test: Test
      testConfiguration: AB test configuration
      title: Configuration usage overview
      version: Version {version}
  datePicker:
    _RANGES:
      CUSTOM: Custom
      LAST_WEEK: Last week
      PREV_07_DAYS: Previous 7 days
      PREV_12_MONTHS: Previous 12 months
      PREV_14_DAYS: Previous 14 days
      PREV_28_DAYS: Previous 28 days
      PREV_30_DAYS: Previous 30 days
      PREV_60_DAYS: Previous 60 days
      PREV_90_DAYS: Previous 90 days
      THIS_WEEK: This week
      THIS_YEAR: This year (January - today)
    cancel: Cancel
    compareOn: Compare with another period
    compareWith: compare with
    confirm: Confirm
    from: From
    period: Time period
    select: Select time period
    to: To
  documentTitles:
    _default: Luigi's Box
    abTests: A/B Tests & Experiments
    acSetup: Autocomplete Setup
    allConfigurations: All Configurations In Project
    analyticsInsider: Analytics Insider
    assistantAnalysis: Assistant Analytics
    assistantCreate: Assistant Create
    assistantDetail: Assistant Detail
    assistantList: Assistant List
    assistantSetup: Assistant Setup
    auditLog: Audit Log
    catalogIntegration: Catalog Integration
    codeIntegration: Code Integration
    configurationCreate: Create Configuration
    configurationDetail: Configuration Detail
    facetCustomization: Product Listing Facets Customization
    facetCustomizationDetail: Facet Customization Detail
    facetCustomizationEdit: Edit Facet Customization
    feedsIntegration: Product Catalog Integration
    finishIntegration: Finish Integration
    infoRedesign: Application Interface Redesign
    integrationSettings: Integration Settings
    integrationSuccess: Integration Success
    logViewer: Log Viewer
    lumaClientSetup: Luma Client Setup
    lumaSetup: Luma Integration Setup
    oneMinuteOnboardingSuccess: Onboarding Success
    plpAnalyticsCategories: Product Listing Overview
    plpAnalyticsDashboard: Product Listing Overview
    plpAnalyticsDetail: Product Listing Category Detail
    productListing: Product Listing Page
    productListingCustomizations: Product Listing Customizations
    queryCustomizations: Query Customizations
    ranking: Relevance And Ranking
    recommender: Recommender
    recommenderSetup: Recommenders Setup
    recommendersCustomization: Recommendations Customization
    recommendersSetupDetail: Recommender Setup
    refinementTesting: Search Refinement Testing
    rewriteQuery: Rewrite Query
    search: Search
    searchResultCustomizations: Search Result Customizations
    searchSetup: Search Setup
    siteConfiguration: Site Configuration
    sitesGroups: Site Management
    synonyms: Synonyms
    transactionsHistoryImport: Transactions History Import
  failedFeedsNotifBanner:
    alternatively: Alternatively,
    intro: Data processing issue detected.
    supportLink:
      text: contact support.
      url: https://www.luigisbox.com/contact/
  feedFailedWarning:
    info: There is a problem with processing your data feed - for more information, see
    link: feed run history.
  feedsAdding:
    addSection:
      brandFeedUrl: Brand feed URL
      categoryFeedUrl: Category feed URL
      chooseFeedsFormat: Choose your product feed format
      chooseFormatPlaceholder: Choose format ...
      custom: Custom
      makeSure: Make sure the feeds meet all the requirements to run the services successfully. According to the documentation, the ideal solution is your own <a href="https://live.luigisbox.com/#feeds" target="_blank">Luigi's Box feeds</a>.
      noOtherFeeds: "<b>I don't have prepared</b> separate feeds for brands/categories/articles."
      onlyProductFeedInfo: If you only add a product feed we cannot determine URLs of brands and categories. This status <b>will not affect the functionality</b> of Luigi's Box services, but we will always redirect users to the search results when searching for a specific brand or category.
      processingInfo: This operation can take a while. You can wait, or you can continue with the settings. We will inform you about the result.
      processingTitle: We are processing data from your feeds
      productFeedUrl: Product feed URL
      title: Adding feeds
    continueSettings: Continue with the settings
    continueToMapping: Go to mapping
    inTutorial: in our tutorial.
    information: Learn more about product catalog integration and all feed settings in our <a href="https://live.luigisbox.com/#feeds" target="_blank">documentation</a>.
    notAvailableText: It is important the feed <b>includes unavailable products</b> that are visible on the website. Otherwise, recommenders cannot recommend correct alternatives for these products.
    optionalSection:
      additionalFeeds: Additional feeds
      availabilityFeedUrl: URL address of product availability feed
      blogFeedUrl: URL address of article feed (blog)
      optionalText: These feeds are optional; if you don't have them, <b>you can simply skip them</b>.
    variantInfo: If the feed <b>contains product variants</b>, they must meet <a href="https://docs.luigisbox.com/#feeds-product-feeds" target="_blank">the following conditions</a>. If not, you can edit the feed or postpone their mapping for later. Until then, you won't be able to display results based on the best match from a group of variants.
    youCanAlso: You can also
  feedsCompleted:
    subTitle: You have successfully added and saved all your feeds. If necessary, you can edit them at any time in your settings.
    title: Catalog integration is successfully completed.
  feedsInfo:
    EmptyFeed: No attributes found in given feed
    InvalidContent: Content of the provided feed is not valid. We expect .xml file.
    InvalidUrl: URL address is not valid
    ItemTagNotFound: Could not determine product tag in given feed
    articleFeed: Articles (blog) feed
    availabilityFeed: Availability feed
    blogFeed: Articles (blog) feed
    brandFeed: Brands feed
    categoryFeed: Categories feed
    changeButtonText: Change feeds URLs
    dangerViolation: "{n} violation | {n} violations"
    errorSubtitle: 'We''re sorry, but the following errors occurred while uploading feeds:'
    errorTitle: Uploading feeds failed
    failed: 'Error: {value}'
    productFeed: Products feed
    reloadButtonText: Reload
    shouldViolation: "{n} warning | {n} warnings"
    validFeed: Valid feed
  feedsMapping:
    addField: Add field
    addMoreFields: Add more fields
    allDataProcess: We automatically <b>process all, even unmapped fields</b>. However, there are cases when you want to modify some fields, for example, changing the field's name "mass" to "weight."
    alreadyExistsName: This field name already exists. Choose another one.
    availabilityMapping:
      availableValues: Values indicating available items
      chooseField: Choose availability field
      chosenAttribute: Chosen attribute
      desc: Availability should have at least two, ideally more (up to 15) different states. Thanks to an attribute with multiple availability states, search better ranks results (preferring more available items over less available ones).
      doItLater: You can complete the availability mapping later. However, missing availability will negatively impact search and product recommendations (all products will be considered available).
      goToDocumentation: <a target="_blank" href="https://docs.luigisbox.com/indexing/feeds.html">Go to documentation</a>
      moreValuesAlert: You likely selected the wrong attribute. The attribute has too many (more than 30) unique values.
      noAvailability: "<b>Feed does not contain availability</b> (cannot map availability)"
      sidebar:
        addFieldTitle: Assign the product availability field
        allAvailable: You have marked all values of the attribute as available, which may negatively impact product recommendations in the future.
        allUnavailable: You have marked all values of the attribute as unavailable, which may negatively impact product recommendations in the future.
        availableItems: Available items
        availableTitle: values indicating available items
        chooseAttribute: Choose attribute...
        confirmAttribute: Confirm attribute
        dropAvailable: Drag values indicating <b>available items</b> here
        dropUnavailable: Drag values indicating <b>unavailable items</b> here
        goToBetween: Move to...
        goToPosition: Move to position
        info:
          part1: "<b>Order determines weight</b>, influencing how products are prioritized in search results and recommendations."
          part2: You can place <b>multiple values at the same position</b> and give them equal weight (e.g., 2 in stock, in stock from supplier).
          part3: You can divide and arrange available values in the order <b>up to 14 positions</b> (more information in the <a target="_blank" href="https://docs.luigisbox.com/indexing/feeds.html">documentation</a>).
        mergeToPosition: Move and group
        notAssignedValues: Unassigned values
        notAvailableItems: Unavailable items
        numberOfValues: Number of items
        positionRandom: The order of values does not matter
        saveAvailabilityMapping: Save availability mapping
        sortAttributes: Sort and arrange attribute values
        sortDescriptions: Divide values into those indicating available or unavailable items. For numerical values from 1 to 14, classify them as available; if the value is 15, move it to the unavailable category. If there are only values 1 and 0, move 0 to unavailable items.
        sortValues: Sort values from most to least available
        title: Product Availability Mapping
        titleDesc: If there are multiple availability fields in the feed, prioritize the attribute that has more than 2 availability values.
        unAvailableTitle: values indicating unavailable items
      sortedValues: Attribute values are sorted from most available to most unavailable.
      title: Product Availability Mapping
      unAvailableValues: Values indicating unavailable items
    completedMapping: Mapping completed
    continueMappingText: Your feeds have been added successfully. Continue to map them.
    customFieldPlaceholder: Custom field name
    customFields: Custom fields
    edit: Edit
    enterDataField: Enter data type of the field
    enterNewName: Enter a new name
    infoTextFirst: For each feed, simply assign the corresponding fields from your feed to predefined items that we could not assign automatically.
    infoTextSecond: But don't worry; searching and filtering results will work, regardless of mapping.
    mappingTitle: Feed mapping
    masterAndVariants: Feed <b>contains master products</b> and variants
    missingSuggestedFields: Add missing suggested fields
    paramsMapping:
      continueLater: Edit the feed and complete the mapping for parameters anytime later.
      example1: 'Values: Color, Size, Weight...'
      example2: 'Values: Black, XL, 0.5 kg'
      fieldIs: 'This field is:'
      name: Name
      noParams: "<b>Feed does not contain additional parameter field.</b>"
      paramDesc: 'You can find it under names such as: Params, CustomParams, Attributes, Specifications, etc.'
      parameterField: Parameter field
      subTitle1: Parameters are a common part of the product feed. Each <b>parameter consists of a name and a value</b>, providing specific information about the product.
      subTitle2: If the feed contains such parameters, we need to <b>label and define them</b>, indicating which part of the parameter field contains the name and which contains the value.
      title: Additional product parameters
      value: Value
    placeholderAce: 'Example: ''smart_split | drop_last | to_s'''
    popover:
      texts:
        annotation: Short annotation or perex of the article.
        availability: Binary attribute. 1 = Product is orderable, 0 = Product is not orderable. To distinguish variation in availability, use availability_rank attribute.
        availability_rank: 'Numeric, strictly within <1, 15>, which encodes the availability of the product. The higher the number, the less available the product is. If your store uses 4 availabilities: "In stock", "Ships within 7 days", "Ships within 14 days", "Out of stock", then a good encoding scheme for availability_rank would be 1, 3, 7, 15. For unavailable products (where availability: 0), the availability_rank must be 15.'
        availability_rank_text: The exact availability text as it should appear in the product tile, e.g. "Ships within 14 days".
        boost: You can use the boost field to manage boosting level for a particular product. Luigi's Box support 3 boosting levels, encoded as 1, 2 and 3. The higher the number, the stronger the boosting effect. Use this field judiciously to avoid interfering with the core ranking algorithm too much. You can always set up boosting in the Luigi's Box application, boosting via feed data is primarily intended for synchronization purposes where you propagate boosting rules from another system.
        brand: Product brand. This exact brand must appear in the brand feed.
        category: Product category with full hierarchy. This exact category with hierarchy must appear in the category feed. If the product is categorized within multiple categories, simply use the <category> tag several times, for each hierarchy and mark the primary category with primary="true" attribute.
        description: Long-form description of the product. Feel free to use formatted HTML, but make sure it is correctly encoded, or enclosed in CDATA section.
        discount: Discount of the product in % (50%) or float number (0.5).
        ean: EAN of the product, if you have it available.
        hierarchy: Category hierarchy, where the | character serves as a category delimiter and does not include the category itself (only the path to it). We will automatically convert the hierarchy into an array that you can use to display category hierarchy in the autocomplete UI.
        identity: Attribute that provides unique identity for each field in the feed, e.g. id, url.
        image_link: Link to the image suitable for autocomplete and search.
        image_link_l: Link to image suitable for product tile in search results. Best size si 600x600 px. Use the same image that you use for rendering your present search result tile if possible.
        image_link_m: Link to the image suitable for autocomplete, around 200x200 px.
        image_link_s: Link to the tiny image used for variants previews. Best size is around 100x100px
        introduced_at: If you include this field, it must have a date/timestamp value in ISO 8601 format - meaning the novelty of a product or a date when product will start / started to sell. If an object does not have this field, we ignore the novelty. When available, this information is used to prefer newer items when sorting search results.
        item_group_id: Attribute used for grouping the variants.
        labels: Comma separated list of labels, such as "Free shipping" or "Sale".
        link: Canonical URL of the item. Often a single product can have several URLs, use the canonical URL for this feed.
        margin: If you include this field, it must have a float value of <0;1> (e.g., 0.42) - meaning the relative margin (e.g., margin is 42% of product price). If an object does not have this field, we treat it as if there is no margin. This information is used to prefer items with higher margin when sorting search results.
        master_group_id: The master product attribute to be used for grouping together with variants. As a general rule, its value is the same as item_group_id of its variants.
        parameters: Arbitrary product parameters in name/value format
        price: The fully formatted price as you want it to appear in the product tile, including currency. This is the price for which the product is available for purchase.
        price_old: If the product is in sale, then this is the original price.
        product_code: SKU code. Based on our experience, people often search for products using their codes, especially in B2B segments.
        rating: Custom rating of the products in any format
        text: Complete text of the article.
        title: Item title, make sure the encode the title with entities if necessary, e.g. "Black &amp; Decker" or simply wrap the data in CDATA directive.
        to_card_id: If you want the ability to add products to cart directly from search results page, make sure to include the internal ID of the product necessary. This may not be applicable in all cases, but include all attributes necessary to add product to cart.
        url: Canonical URL of the item. Often a single product can have several URLs, use the canonical URL for this feed.
        web_url: Canonical URL of the item. Often a single product can have several URLs, use the canonical URL for this feed.
      titles:
        annotation: Annotation
        availability: Availability
        availability_rank: Availability rank
        availability_rank_text: Availability rank text
        boost: Boost
        brand: Brand
        category: Category
        description: Description
        discount: Discount
        ean: EAN
        hierarchy: Hierarchy
        identity: Identity
        image_link: Link to the image
        image_link_l: Large image link
        image_link_m: Medium image link
        image_link_s: Tiny image link
        introduced_at: Introduced at
        labels: Labels
        link: Url
        margin: Margin
        parameters: Parameters
        price: Price
        price_old: Price old
        product_code: Product code
        rating: Rating
        text: Text
        title: Title
        to_card_id: To card ID
        url: Url
        web_url: Url
    preview: Preview
    productVariants: Product variants
    requiredFields: Required fields
    saveAndContinue: Save and continue mapping
    saveAndDone: Save and finish mapping
    selectField: Select a field ...
    selectFieldLabel: Select a field
    selectType: Select a type ...
    suggestedFields: Suggested fields
    transformationPopup:
      applicationTitle: 'Apply any transformations to the <b>{field}</b> field:'
      title: Add transformation
      transformationsList: Transformations list
    transformations: transformations
    transformationsList:
      append: Appends a string at the end of the value. Accepts one argument, as a string.
      clamp: Returns the "min" value if the input value is less than "min". Returns the "max" value if the input value is greater than "max". Otherwise, returns the input value. Expects two arguments and a numerical input value.
      drop_last: Drops the last element from an array (list) of values. Needs to be applied on an array as a whole, not on each individual array element.Does not accept arguments.
      first: Returns the first element of an array (list) of values. Needs to be applied on an array as a whole, not on each individual array element.Does not accept arguments.
      remove_value: Removes specified value from an array (list) of values. Needs to be applied on an array as a whole, not on each individual array element. Accepts one argument as a string.
      replace: Replaces specified character or a string of characters within a value with specified replacement. Accepts two strings as arguments - the value you want to replace and the replacement.
      smart_split: 'Splits a simple string into an array (list) of individual strings by one of these characters: > | ,. Handles whitespace automatically. Does not accept arguments.'
      split_by_separator: Splits a simple string into an array (list) of individual strings. This filter expects one argument which is the delimiter by which you wish the attribute value to be split, as string.
      strip: Removes any trailing whitespace from a string. Does not accept arguments.
      strip_html_tags: Strips any html tags from a string. Does not accept arguments. Usually used in description texts.
      to_f: Coerces attribute value to a decimal point number (Float). Does not accept arguments.
      to_i: Coerces attribute value to a number (Integer). Does not accept arguments.
      to_s: Coerces attribute value to a string. Does not accept arguments. Can be used to convert other object types like integers, floats or booleans into strings.
      translate: Replaces an entire value with a different one. Accepts two arguments, first the value you wish to translate, the second one is the value you wish to replace it with, both as strings or integers.
      translate_example: 'Example for availability transformation: translate(''in_stock'', 1) | translate(''out_of_stock'', 0)'
      uniq: Removes duplicates from an array (list) of values. Needs to be applied on an array as a whole, not on each individual array element. Does not accept arguments.
    updateFeed: Update
    updatingFeedText: You can make changes in the feed during mapping, e.g. fill in the missing fields. However, the feed must be updated after each change.
    variantsCardInfo: Variants must meet <a href="https://docs.luigisbox.com/#feeds-product-feeds" target="_blank">the following conditions</a> – a unique ID must link them and appear consecutively in the feed without being separated by unrelated products.
    variantsCheckbox: "<b>Feed doesn't contain variants</b> (variants don't meet the conditions)"
    variantsLaterInfo: You can edit the feed and complete the mapping for variants later.
  feedsNavigation:
    adding: Adding
    mapping: Mapping
    popupLeave:
      close: Close window
      info: You are going to navigate back in feeds settings. This may cause that you will have to set up the current setting from the beginning.
      submit: Continue
      title: Warning in step back
    popupLeaveGlobal:
      close: Close window
      info: If you decide to continue, <strong>you will loose all of your not saved changes</strong>, that you have made in settings.
      submit: Continue without saving
      title: Your changes have not been saved
    validation: Validation
  feedsValidation:
    cardErrorText: If a feed contains warnings or errors, you can decide based on their seriousness whether you want to <a href="feeds_adding">add and map new feeds</a>, correct the errors found in the current feeds and validate them all again, or <b>complete the integration</b> and return to editing feeds later in the settings.
    feedErrors: Feed contains errors?
    finishIntegration: Finish integration
    goToValidation: Go to feed validation
    mappedDataProcessing: We are processing the mapped data
    mappingSuccess: You have successfully added and mapped the feeds below. However, the mapped feeds must pass validation to complete the product catalog integration.
    popup:
      title: Feed quality overview
    title: Feeds validation
    validateAgain: Validate feeds again
    validateButtonText: Validate mapped feeds
  fieldTab:
    popup:
      removing: Deleting item "{text}"
      title: Do you really want to delete <b>{text}</b>?
  finishIntegration:
    addCode: To go ahead and run Luigi's Box services, we need to add a piece of our code to <b>{domainName}</b>.
    addLbx: Add LBX code to the page
    addedSuccess: The Luigi's Box code has been added to your page. Now you can safely continue to set up the services.
    analyticsSetup: Analytics setup
    catalogIntegration:
      addCatalog: Add a product catalog
      addCatalogAgain: Add the product catalog again
      addCatalogButtonText: Add product catalog
      addManually: Add product catalog manually
      addPlatform: Add {platform}
      addPlatform2: product catalog
      addedText: Your product catalog has been successfully sent, and we continue to process it. We will inform you about the result soon.
      allDetailSettings: You can easily prepare all the necessary inputs for the setting based on the documentation <a href="{address}" target="_blank">How to grant access via API in {platform}.</a>
      apiHost: API host
      apiIntegrationHowTo: Before getting started, we recommend you read <a href="https://docs.luigisbox.com/indexing/api.html" target="_blank">Luigi's Box API documentation</a>, which walks you through everything you need to gain access.
      apiIntegrationSubtitle: Use our content API to store your products, categories, brands, articles and other type of content in Luigi's Box.
      apiIntegrationTitle: Integration with Luigi's Box API
      apiKey: API key
      apiPart:
        apiIntegration: Integration with Luigi's Box API
        apiKey: API key
        apiKeyFinishText: You have chosen to integrate the catalog using <a href="https://docs.luigisbox.com/indexing/api.html" target="_blank">Luigi's Box API</a>. Use your generated API key to complete the integration and gain access to all the service's features.
        apiKeyText: The private key to authentification
        cannotChange: If you decide to proceed, you cannot change your chosen integration method.
        continueAndGenerate: Continue and generate API key
        copy: Copy
        documentationText: <a href="https://docs.luigisbox.com/indexing/api.html" target="_blank">Luigi's Box API documentation</a> guides you through all the necessary steps to set up API access and integration.
        entriesError: Errors occurred during integration, and your product catalog could not be added.
        generateTabText: If you choose to continue, you will confirm the option to integrate the product catalog using Luigi's Box API, and you will get a private API key that gives you access to all the service features.
        goToIntroduction: Go back to introduction
        showApiKey: Show API key
        title: Luigi's Box API
        trackerId: Tracker ID
        trackerIdText: Identifier (public key) assigned to your site
        unsuccessful: Unsuccessful
        waitingForEntries: Waiting for entries in the catalog
        wantApiIntegration: I want to integrate a catalog using the API
      attributeSettings: To add and process feeds, we must complete the API key and set some essential attributes and settings.
      authHost: Autentification host
      brandUrl: 'Brand URL for:'
      catalogAddedError: Something went wrong
      catalogAddedErrorText: If the issue repeats or you need help with a solution, <a href="https://www.luigisbox.com/contact/" target="_blank">contact our support.</a>
      catalogAddedSuccess: Product catalog has been added
      catalogAddedText: Your product catalog has been added, and we are continuing to process it. This operation may take some time. You can wait, or you can continue in settings. We will inform you about the result soon.
      categoryUrl: 'Category URL for:'
      categoryUrlName: Category URL
      chooseCurrency: Choose option...
      chooseOption: Choose the preferred option
      clientId: Client ID
      clientSecret: Client secret
      confirmKey: Confirm
      consumerKey: Consumer key
      consumerSecret: Consumer secret
      currencies: Currencies
      feedTextDetail: Using <b>URL addresses of feeds and attribute mapping</b> (assigning fields in feeds to specific attributes) <a href="https://docs.luigisbox.com/indexing/feeds.html" target="_blank">based on documentation.</a>
      howToAddAccess: <b>Don't know how to get your API key?</b> Follow our guide <a href="{address}" target="_blank">How to grant access via API in {platform}</a> to set up the catalog.
      invalidPlatform: Platform '{value}' is not supported.
      languages: Languages
      missingParameter: Missing required config parameter '{value}'.
      moreOptions: More catalog integration options
      necessaryInfo: Add the necessary data
      notMatch: If the chosen integration <b>{platform}</b> platform does not match your site's platform, you can change this setting below.
      notMatchingAny: If none of the options match your platform, you can continue adding the catalog manually or <a href="https://www.luigisbox.com/contact/" target="_blank">contact our support.</a>
      platformSync: Catalog synchronization <b>using your platform</b>. The easiest way to integrate using {platform} API key.
      prestashopError: Could not communicate with Prestashop API.
      prestashopTitleError: 'Your product catalog could not be added due to the following error:'
      productUrl: 'Product URL for:'
      projectKey: Project key
      recommendedIntegration: "⭐ Recommended integration method for you"
      subTitle: To start the services, we need to process your product catalog. You can synchronize the product catalog manually by adding feeds or using your platform and API key.
      syncFeedCatalog: Synchronization of the product catalog independently using feeds, regardless of which platform the site operates.
      title: Product catalog integration
      urlNotCorrect: The specified URL does not correspond to the category "{category}"
      usesPlatform: 'The site uses the platform:'
      verifyPlaceholder: https://wwww.domain.com/...
      verifyText: 'We need to verify your URL addresses. We selected specific items (product, category...) from your catalog. Please <b>assign the corresponding URL addresses</b> on your website to them. E.g.: https://www.domain.com.../product_detail'
      verifyUrl: Help us verify your URL addresses
      verifyWooText: 'We need to verify your URL address. We selected specific category from your catalog. Please <b>assign the corresponding URL address</b> on your website to them. E.g.: https://www.domain.com.../category_listing'
      woocommerceError: Could not communicate with Woocommerce API.
      wrongPlatform: Wrong platform?
    close: Close
    confirmSelection: Confirm selection
    continue: Continue
    continueSettings: Continue with the settings
    goToInstructions: Go to instructions
    howDataImportWorks: See how data import works
    instructions:
      back: Go back
      backToIntro: Back to the introduction
      information: Just add a piece of our code to <b>{domainName}</b>. You can find detailed information about the integration on our <a href="https://docs.luigisbox.com/javascript_collector.html#integration-manual" target="_blank">technical support page.</a>
      title: Add Luigi's Box to the pages
      verify: Verify the integration on the site
    integrateCatalog: Product catalog integration
    integrationSuccess:
      subTitle: You have successfully completed all the settings required to run Luigi's Box on your site. <b>Activate our services</b>, discover new options and functions, and <b>enjoy Luigi's Box to the fullest</b>.
      title: "Use Luigi's Box to the fullest 🚀"
    manual:
      needHelp: <a href="https://www.luigisbox.com/contact/" target="_blank">Need some help? Contact support</a>
      waitingButtonText: Awaiting completion
    needsCatalog: To start the services, we need to process your product catalog. Its processing depends on the platform on which your solution is built or the possibility of delivering separately prepared feeds.
    subTitle: Congratulations, to fully use our services, you still need to complete a few settings. Before you start, we recommend that you prepare the necessary outputs in advance.
    successIntegration:
      activateButtonText: Go to activation
      backEnd: "... or use the possibility of back-end integration on your sites."
      backEndText: A great choice if you have an available development team or a back-end solution supplier. By confirming, you will be redirected to documentation and preparation of annotations to continue running the services.
      confirmBackEnd: Confirm back-end integration
      helpButtonText: Contact support
      helpText: We are here for you if you have questions, need help, or support valuable advice.
      helpTitle: Need help?
      searchText: Intelligent personalized search from the first interaction.
      searchTitle: Search
      startManually: Start Luigi's Box services manually, in the application settings ...
    successfulTitle: Luigi's Box code was added successfully
    transactionHistory: Add transaction history
    welcome: "👋Welcome to Luigi's Box"
  header:
    userMenu:
      admin: Administration
      demo-leave: Demo mode
      demo-start: Demo mode
      profile: My account
      salesAdmin: Administration for Sales
      settings: Settings
      signOut: Logout
      siteAdministration: Site administration
  headerDefault:
    inDemoMode: You are in demo mode
    switchToFullMode: Switch to full mode
  idosell:
    address: Street Address
    billingEmail: Contact E-mail address
    billingInfo: Contact information
    city: City
    companyId: Company ID
    companyName: Company name
    country: Country
    everythingFine: It looks like everything works fine
    logInLuigis: Log in to Luigi's Box
    postalCode: ZIP / Postal code
    saveAndContinue: Save information and continue
    selectCountry: Select country
    subTitle: To utilize our services, providing a few additional details is necessary.
    taxId: TAX ID
    vatId: VAT ID
    wantChanges: If you want to make some improvements or changes in settings and setup, continue to the Luigi's Box app.
  image_uploads:
    create:
      access_denied: Access denied
      file_format_not_supported: Only images are allowed
      file_size_exceeded: File size exceeds 5MB limit
      invalid_object_state: Invalid object state
      service_error: Service error
      success: File uploaded successfully
      upload_failed: Upload failed
  integrationSettings:
    allInfoShopify: All integration settings options are available in your <a href="https://admin.shopify.com/store/{siteName}/apps/luigis-box">Luigi's Box add-on</a> in Shopify.
    apiKeys: API keys
    areYouSure: This action is non-returnable. Are you sure?
    catalogNotReset: Catalog has not been reset
    catalogReset: Catalog has been reset
    confirmText: To prevent accidental data loss and to confirm your intention to reset the catalog, type <b>"{site}"</b> in the field below.
    dataQuality: data quality control feature.
    discount: Quantity discount
    errors:
      sameFeedUrls: Feed url addresses cannot be the same.
    foreignLanguages: Addon "Foreign-languages"
    goTo: Go to
    hideApiKeys: Collapse the list of API keys
    hideScripts: Collapse the list of LBX scripts
    luigiScript: Luigi's Box script
    managedByAddon: This script is managed by an add-on
    noApiKeysAvailable: There are no API keys available
    notSaved: Settings has not been saved.
    personalizePrices: Personalization of prices for logged in users
    productFeeds: Product feeds
    resetAlert: You are about to reset the catalog for <b>{site}</b>. After resetting the catalog, your autocomplete and search will not return any results until you re-index the entire catalog.
    resetCatalog: Reset catalog
    resetProductCatalog: Reset product catalog
    saveSettings: Save settings
    saved: Settings saved
    scriptSubtitle: A unique code that is inserted on all your pages in the <head></head> section to use Luigi's Box services.
    shoptetSettings: Shoptet settings
    showAllApiKeys: Expand the entire list of API keys
    showAllScripts: Expand the entire list of LBX scripts
    site: Site
    subTitle: You can verify the functionality of the integration and product feeds using the
    title: Integration Settings
    trackingCode: Tracker ID
    usingThisFunctions: 'I use one of the following features:'
  integrationSetupSettings:
    beforeStartMessage: <a href="https://docs.luigisbox.com/#feeds" target="_blank">Read the documentation</a> on how to edit your feeds before continuing.
    currency: Currency
    decimalNumbers: The number of decimals
    decimalSeparator: Decimal separator
    feedCard:
      noFeed: No {feed} feed is added.
    feedCardOptions:
      addFeed: "+ Add feed"
      changeAdress: Change address
      copy: Copy address
      delete: Delete feed
      deleteConfirm: Do you really want to delete the feed?
      editMapping: Edit mapping
    howToSetPrice: How to set price and currencies
    pageLanguage: Page language
    priceFormat: Price format and display
    priceFormatPreview: Price format preview
    priceFormatText: Setting the price display format in the whisperer and in the search results.
    separators:
      comma: Comma (,)
      none: None
      period: Period (.)
      space: Space ( )
    symbol: Symbol
    symbolBefore: Display symbol before number
    thousandsSeparator: Thousands separator
    withoutDecimal: Display integers without decimals
  logViewer:
    clearFilters: Clear filters
    description: Take a closer look at your logs
    emptyMessage: No logs yet
    filter:
      _services:
        ANALYTICS: Analytics
        AUTOCOMPLETE: Autocomplete
        INDEXING: Indexing
        RECOMMENDER: Recommender
        SEARCH: Search
        _empty: ''
      _speeds:
        FAST: Fast
        NORMAL: Normal
        SLOW: Slow
        _empty: ''
      _statuses:
        CLIENT_ERROR: Error on client's side
        LBX_ERROR: Error on LBX's side
        OK: OK status
        _empty: ''
      from: Timestamp from
      id: Request ID
      service: Service
      speed: Speed
      status: Status
      to: Timestamp to
    ms: ms
    tableHeading:
      id: Request ID
      service: Service
      speed: Speed
      status: Status
      timestamp: Timestamp
      url: Endpoint
    title: Log viewer
  login:
    emailExample: email@address.com
    password: Password
  luigiPager:
    items: "{first} - {last} of {total} items"
    loadMore: Load more
    pages: Page {first} of {last}
  luigiselect:
    close: Close
    empty: Select items from your catalog
    noResults: No results found
    search: Start typing here to find items
    selectItem: Select item
    selectedItem: Selected item
    types:
      article: Articles
      brand: Brands
      category: Categories
      item: Products
      product: Products
  magento:
    accountExists: Already have an account at Luigi's Box?
    address: Street Address
    billingEmail: Contact E-mail address
    billingInfo: Contact information
    city: City
    companyId: Company ID
    companyName: Company name
    country: Country
    email: User E-mail
    everythingFine: It looks like everything works fine
    invalidCredentials: Email or password is incorrect
    linkAccount: Link account with this Magento instance
    logInLuigis: Log in to Luigi's Box
    newAccount: Create a new account
    password: Password
    postalCode: ZIP / Postal code
    saveAndContinue: Save information and continue
    selectCountry: Select country
    shopNotFound: We could not find your Magento shop
    shopNotFoundDescription: This probably means that an error occured during installation process. Our developers are already looking into it
    subTitle: To utilize our services, providing a few additional details is necessary.
    taxId: TAX ID
    vatId: VAT ID
    wantChanges: If you want to make some improvements or changes in settings and setup, continue to the Luigi's Box app.
  newAppSuccess:
    info: "🙌 You have completed the necessary settings to run Luigi's Box 🚀 on your site. Discover new options and functions, just 👉"
    infoNotFinished: "☝️ In order to run and use Luigi's Box 🚀 on your site to the full, you must first 👉"
    link: continue to activate services.
    linkNotFinished: finish necessary settings.
  oneMinuteOnboardingSuccess:
    firstSynonym: Setup your first synonym
    successInfo: You have successfully completed all the settings needed to launch Luigi’s Box on your website.
    title: "Congratulations! 🥳"
    trialInfo: Try our services for <b>free for 30 days</b>, explore new possibilities and features, and enjoy Luigi’s Box to the fullest.
  plpAnalytics:
    dashboard:
      conversionMode: Conversion mode
      conversionModeTooltip_html: <p>Assisted conversions take into account sessions where Search was used, and there was a click within the Autocomplete, Search, or Recommender results (for example, a click leading to a category or brand page). Afterward, a conversion event, such as "add-to-cart," is recorded within the same session.</p> <p>Direct conversions are based on sequences of events. For instance, a user might perform a search and then directly complete a conversion event (e.g., add-to-cart) on a search result. Alternatively, the sequence could involve using Autocomplete, clicking to go to a product detail page, and then completing a conversion event on the product detail page.</p>
      lists:
        with_high_conversion:
          description: The report analyzes categories with the highest conversion rates. The analysis includes only categories with at least one click on a result. Categories in the table are sorted by the number of conversions.
          title: High Conversions
        with_high_exit_rate:
          description: The report highlights categories that might cause visitors to exit your site. Categories in the table are sorted by exit rate.
          title: High Exit Rate
        with_low_conversion:
          description: The report analyzes categories with the lowest conversion rates. The analysis includes only categories with at least one click on a result. Categories in the table are sorted by conversion rate (CVR).
          title: Low Conversion Rate
        with_low_traffic:
          description: The report shows categories with a low number of users. The results are sorted by the number of users and include only categories that were visited by at least two different users.
          title: Low Traffic
      listsHeader: List of Product Listings Based on...
      modes:
        ASSISTED: Assisted Conversions
        DIRECT: Direct Conversions
      showMore: Show more categories
      title: Product Listings Analytics
    detail:
      catalogBrowser: Catalog Browser
      firstPage: First page ({n}) results by position
      info:
        identity: Identity
        level: Level
        levelTooltip: Level of Category
      modeInfo: All data in the previews is displayed using direct conversion mode.
      setsHeading: Different Result Sets
      setsHeading2: Different {total} result sets in the selected time range
    graphs:
      _other:
        assisted: Assisted
        direct: Direct
        first: First position
        first8: First 8 positions
      average_click_position:
        info: Average position of the item that was clicked from the results.
        title: Average Click Position
        title2: Average click position
      avg_cart_value:
        info: Average Cart Value is calculated as the total sum of the prices of items added to the cart divided by the number of product listings with conversion. If an item is added to the cart multiple times (i.e., if a conversion event occurs multiple times), its price is also included in the total sum multiple times.
        title: Average Cart Value
        title2: Average Cart Value
      conversions:
        info: Conversions are based on a sequence of events, such as impression on list 'product listing' → 'conversion event' (e.g., 'add-to-cart') on results page, or 'product listing' → 'click' to go to a product detail page → 'conversion event' on that product detail page.
        title: Conversions
        title2: Number of direct conversions
      conversions_joined:
        info: Direct conversions are based on a sequence of events, such as impression on list 'product listing' → 'conversion event' (e.g., 'add-to-cart') on results page, or 'product listing' → 'click' to go to a product detail page → 'conversion event' on that product detail page.<br /><br />Assisted conversions consider sessions where a 'product listing' was used, a 'click' within the results was made, and subsequently, a conversion event (e.g., 'add-to-cart') was recorded within the same session.
        title: Conversions
        title2: Number of direct conversions
      ctr:
        info: Percentage of list impressions that had at least one click within the results.
        title: Click-Through Rate
        title2: Click-Through rate
      cvr:
        info: Direct conversion rate shows the percentage of sessions where a direct conversion from a result ocurred.
        title: Conversion Rate
        title2: Conversion rate
      cvr_joined:
        info: Direct conversion rate shows the percentage of sessions where a direct conversion from a result ocurred.<br /><br />Assisted conversion rate represents the percentage of sessions in which an assisted conversion occurred. Assisted conversions consider sessions where product listing was used, a 'click' within the product listing results was made, and subsequently, a conversion event (e.g., 'add-to-cart') was recorded within the same session.
        title: Conversion Rate
        title2: Conversion rate
      direct_transactions_joined:
        info: 'Assisted transactions are based on the sequence of events: impression on list ''product listing'' → ''click'' on a results page, followed by a subsequent transaction within the same session. The transaction does not have to include the product that was clicked in the product listing. <br /></br />Direct transactions are based on the sequence of events: ''product listing'' → ''conversion'' (e.g., add-to-cart) on a product listing results page, followed by a subsequent transaction within the same session. The transaction does not have to involve a product that was added to the cart from the product listing results.'
        title: Transactions
        title2: Number of direct transactions
      exit_rate:
        info: Show percentages of sessions where a user bounced from the product listing result.
        title: Exit Rate
        title2: Exit rate from product listing
      impressions:
        info: Number of list views with results.
        title: Impressions
        title2: Number of impressions
      sum_cart_value:
        info: Total Cart Value is calculated as the total sum of the prices of items added to the cart. If an item is added to the cart multiple times (i.e., if a conversion event occurs multiple times), its price is also included in the total sum multiple times.
        title: Total Cart Value
        title2: Total Cart Value
      users:
        info: Number of users. If a user is logged in on multiple devices, they're only counted once.
        title: Users
        title2: Number of users
    noData: No data yet
    table:
      empty: No results matching current search
      limit: Number of rows
      page: Page
      pageOf: of {total}
      pager: "{first} - {last} of {total}"
      search: Search in all categories
    tableHeaders:
      average_position:
        title: Average Position
        tooltip: The average position of clicked items within a category.
      category_name:
        title: Category Name
      conversion_rate:
        title: Conversion Rate (%)
        tooltip: Percentage of category impressions that have at least one conversion
      conversions:
        title: Conversions
        tooltip: Total number of conversions from the category.
      ctr:
        title: CTR (%)
        tooltip: Percentage of product impressions that result in at least one click.
      exit_rate:
        title: Exit Rate (%)
        tooltip: The percentage of category impressions that were the final interaction in a session.
      level:
        title: Level
        tooltip: The position of the category within the category tree, where 1 represents a top-level category.
      number:
        title: "#"
      product_name:
        title: Product Name
        tooltip: Product Name
      products:
        title: Products
        tooltip: Number of products for a given category.
      results:
        title: Results
        tooltip: Results
      url:
        title: URL
        tooltip: URL
      users:
        title: Users
        tooltip: Number of users that viewed the category.
    timeline:
      conversionRate: Conversion Rate
      impressions: Impressions
      sets: "💡 You’ve shown {n} different result sets in the selected time range"
      show: Show
      title: Category Timeline
    welcome:
      analyticsPlp: This section allows you to view the analytics of your product listings.
      needToActivate: However, since <b>it has not yet been activated</b> on your account, it will need to be enabled. Simply click the 'Request Service Activation' button, and our team will reach out to you as soon as possible to provide access to this service.
      requestServiceActivation: Request service activation
      thanks: Thank you for expressing your interest.
      title: Product listing analysis
      willReachShortly: Somebody from Luigi's Box Team will reach out to you shortly.
  productListingCustomizations:
    buttons:
      addCustomResult: Add custom result
      approve: 'Yes'
      close: Close
      decline: 'No'
      delete: Delete
      discard: Discard
      discardCustomization: Discard customization
      editCustomization: Edit customization
      hideResult: Hide result
      moreOptions: More options
      removeCustomization: Remove customization
      save: Save
      saveCustomization: Save customization
    create:
      cardDescription: In the customization, you can place any items in the results in specific positions, completely prevent them from being displayed, or create custom criteria for their display using filters.
      cardTitle: Prodcut listing customizations
      chooseType: Choose type of result you are interested in (typically item or product)
      chooseTypePlaceholder: Choose type...
      customizeResults: Customize results
      searchQuery: 'Search product listing which you want to customize results for:'
      title: Enter a product listing and choose the type to customize
    detail:
      choosePlaceholder: Choose a specific item...
      collapse: Collapse future pinned results
      customizedResults: Customized results
      customizingQuery: Customizing results for the product listing
      doesNotExistTooltip: "<b>The items does not exist</b></br>The item is not found in the product catalog. Pinning it to this position will be ignored. You can safely remove this pin."
      evenUnavailable: Even unavailable
      expand: 1+ additional rules planned for the future
      hiddenResults: Hidden results
      noHiddenItems: No items have been hidden yet.
      overlapInfo: Rules are overlaping. Do you want to automatically move old rules down by 1 position?
      overlapTitle: Overlaping Rules
      productListing: Product Listing
      sidebar:
        activePermanently: Permanently
        addToPosition: 'Place the result at position:'
        availableDesc: Unavailable results (e.g., sold-out products) are not usually pinned. <br>However, you can choose to pin it even if it is not available.
        availableTitle: Pin unavailable
        description: "<b>Choose an item from the catalog</b> that you want to display in the search results at a specific position."
        edit: Edit settings
        endDate: 'End date:'
        header: Add custom result
        hiddenSave: Hide specific item
        hide: Hide result
        itemPositionDesc: Enter at which position in the order the result should be displayed in the product listing.
        itemPositionTitle: Position of the result in the product listing
        leaveBlank: Leave blank
        leaveTimeEmpty: Leave blank to pin the item permanently.
        noInterval: Permanently
        pin: Pin to position
        pinTimeDesc: You can schedule or time-limit pinning the result to a specific position.
        pinTimeTitle: Time frame selection
        positionAndTime: Setting the position and time frame
        resultSave: Pin the slected result
        specificItem: Specific item
        startDate: 'Start date:'
        title: 'Choose a type and set a custom result for a specific position:'
        unpin: Unpin result
      startTypeHere: Start typing here for values search
      tagsDesc: 'You can label your customization (e.g. #blackfriday, wintersale, shirts...) to find it more easily.'
      tagsSearchPlaceholder: Enter a tag name and press Enter
      tagsTitle: Label with your own tag
      type: Type
      unavailableTooltip: "<b>Currently unavailable item</b></br>The item is currently unavailable (e.g., out of stock, available for order...), but it will continue to be displayed in this position."
    errors:
      active_from_must_be_a_date: Active from must be a date
      active_to_must_be_a_date: Active to must be a date
      cannot_block_and_pin_item_for_one_query_at_the_same_time: Can't block and pin item for one product listing at the same time
      chosen_position_for_this_query_is_already_occupied_in_selected_active_range: Chosen position for this product listinguery is already occupied in selected active range
      default: Something went wrong
      export: Error occurred while exporting prodcut listing customizations
      import: Error occurred while importing prodcut listing customizations
      invalid_category_attribute: Error creating query - invalid category attribute
      item_url_cannot_be_empty: Item URL cannot be empty
      not_found: 404 - query not found
      position_must_be_number_greater_than_0: Position must be number greater than 0
      query_cannot_be_empty: Query cannot be empty
      results_position_cannot_be_empty: Results position cannot be empty
      this_item_is_already_blocked_for_this_query_in_selected_active_range: This item is already blocked for this product listing in selected active range
      this_item_is_already_set_for_this_query_in_selected_active_range: This item is already set for this product listing in selected active range
      type_cannot_be_empty: Type cannot be empty
    filters:
      cancelAll: Clear all filters
      close: Close
      modes: Modes
      saveButton: Filter the list
      searchByName: Search by name
      tags: Tags
      title: Filter the list
      types: Types
    importSidebar:
      buttons:
        close: Close
        continue: Continue
        export: Export customizations
        import: Import customizations
      example: Download Sample *.csv File
      export:
        info: Export all your created customizations to a *.csv file, which you can then easily open in any spreadsheet editor and modify or expand as needed. Or you can {example} and create a completely new list of your own customizations.
        infoEmpty: TAt this moment, there is nothing to export as no customizations have been added. However, you can {example} and create a completely new list of customizations for import yourself.
      import:
        dataError: Incorrect file type or format.
        info1: Save time by bulk importing your customizations. You can easily expand the list of existing ones, or discard all original customizations and upload a completely new list.
        info2: You only need to {example} (or modify the export of existing customizations), which you will open in your favorite spreadsheet editor and modify as needed.
        items: "{n} records"
        modes:
          add:
            info: Imported customizations will be added to the original list. If the import contains customizations for a word that already exists in the system, the new record will completely overwrite the original, older record.
            title: Add new customizations to existing ones
          replace:
            info: All previously created customizations will be removed. Only new imported customizations from the list will remain.
            title: Remove original customizations and keep only new ones
        step1: Choose your import method
        step2: Add your prepared list
        supportInfo_html: We only support <strong>CSV file format</strong>, which must not contain a legend (header row).
        uploadInfo_html: Drag and drop or <span>add a list</span> in <strong>CSV format</strong> here
      success: Done, your customizations list has been successfully added.
      title: Import/export customizations
    intro:
      buttonImport: Import customizations
      newQuery: Customize new product listing
      subTitle: Tailoring results of a particular product listing according to your preferences.
      title: Product listing customizations
    list:
      added: Added
      buttonImportExport: Import/Export customizations
      customizations: Customizations
      customize_button: Customize new product listing
      customized_queries: Customized Listings And Validity
      filterList: Filter the list
      noResult: We did not find any results matching your criteria.
      onThePage: On the page
      search: Search in customizations
      tags: Tags
      title: List of customizations
    modal:
      adjustSettings: Adjust settings
      deleteDesc: Do you really want to remove this customization? This change cannot be undone.
      deleteTitle: Delete customization
      discardDesc: Do you really want to discard these changes? This change cannot be undone.
      discardTitle: Discard changes
      unHide: Unhide
    queriesItem:
      blockedResults: "{n} blocked result | {n} blocked results"
      delete: Delete
      edit: Edit
      inAllResuls: in Autocomplete and Search
      inAutocompleteResuls: in Autocomplete
      inSearchResuls: in Search
      pinnedResults: "{n} pinned result | {n} pinned results"
      type: type
    success:
      created: Product listing created successfully
      deleted: Product listing deleted successfully
      saved: Product listing saved successfully
  progress:
    addLbxCode: Add LBX code to the page
    catalogIntegration: Product catalog integration
    completeSettings: Complete the settings
    itemText: 'To use Luigi''s Box to its full potential, there are a few settings to complete:'
    remainingSteps: "{n} step remaining | {n} steps remaining"
  ranking:
    _errors:
      load: Something went wrong when loading data
      save: Something went wrong when saving data
    _notifications:
      saved: Signal settings saved
    _weightTooltips_html:
      STONLY: STONLY
    _weights:
      cat_booster: Category popularity per Query
      discount: Product discount
      freshness: Freshness
      fulltext: Text relevance (SERP only)
      item_booster: Product popularity per Query
      location: Geo location
      long_term_personalization_attributes: Long term personalization of attributes
      long_term_personalization_categories: Long term personalization of categories
      long_term_personalization_items: Long term personalization of products
      margin: Priority
      penalized_categories: Penalized categories
      popularity: Product popularity
      session_intent_personalization_attributes: Short term personalisation of attributes
      session_intent_personalization_categories: Short term personalization of categories
    confirmationInfo: Your changes are waiting for confirmation
    descriptionHtml: Various factors influencing the relevance and ordering of results apply consistently <strong>across Search (including Autocomplete), Product Listing Pages, and Shopping Assistant integrations on site</strong>.
    discard: Discard all changes
    expertMode: "Edit signals (expert 🤓 mode)"
    heading: Relevance And Ranking
    helpSidebar:
      item1:
        description1_html: STONLY
        title: STONLY
      title: STONLY
    howItWorks: How do ranking signals work?
    notFound: Not found in your data. The signal will not be considered.
    reset: Reset to previous settings
    save: Save changes
    signals: Product Ranking Signals
    signalsInfo_html: "<strong>Start with changing the profile</strong> according to your preference, or it is based on your segment. You can change the default or create new custom settings."
    singnalSettings: Signals settings for SERP & PLP
    strategoryForAutocompleteAndSearch: Product ranking strategy for Search (including Autocomplete) and Product Listing
    usedOn:
      back: Go back and edit the settings
      create: Create a new configuration with changes
      info: Your changes will affect all the below project sites that use this configuration as active site configuration
      save: Save and apply changes to these pages
      title: Are you sure you want to save changes in the configuration?
  recoCustomization:
    added: Added
    catalogType: Type
    comprehensiveCriteria: Comprehensive criteria
    comprehensiveCriteriaText: This is a comprehensive criterion created by Luigi's Box in-house team. The criterion itself cannot be modified. All other options are available by default.
    copyingSidebar:
      alreadyExist: The customization with this input already exists in the target recommender.
      chooseRecommenderToCopy: Choose the recommender where you will copy and move the duplicated customization above.
      copyAndMove: Copy & Move
      copyAndReplace: Copy & Replace
      copyWhere: Where do you want to copy this customization?
      movingCustomization: This customization will be duplicated and moved as new into the recommenders <b>(only the same type)</b> you choose.
      processCancelled: The customization has not been copied. The process has been canceled.
      selectedCustomization: Selected customization
      success: The customization has been successfully copied.
      wantToReplace: Do you want to replace it?
    criteria:
      add: Add
      addAttribute: Add attribute
      addCondition: Add conditions that must be met
      addConditionGroup: Add new condition group
      cancel: Cancel
      commonSigns: Define common properties of all items for which you want to modify the recommender results. Create a custom criterion using conditions based on specific attributes and values that must meet.
      confirmRemove: Do you really want to remove this group?
      createCriterium: Create custom criterion
      desc: Define items for customization based on their common properties. Create a custom criterion using specific attributes and values that should meet.
      editCriterium: Edit criterion
      emptyCriteria: This group is empty. Start by adding attributes and setting their values.
      remove: Remove
      removeGroup: Remove condition group
      title: Criterion for recommender input
    customizeButton: Customize the recommender results
    delete: Delete customization
    deleteAllCustomizations: Delete all customizations
    deleteCustomization: Remove customization
    deletePopupDesc: Do you really want to remove this customization? This change cannot be undone.
    detail:
      addResult: Add custom result
      all: The entire recommender
      chooseReco: Choose a recommender...
      chooseToCustomize: Select item for customization
      chooseToText: Choose the recommender you want to customize
      chooseType: Choose item type
      confirmContinue: Confirm and continue
      criteria: Custom criteria
      customizationDesc: In customization, you can place any item in the recommender in specific positions, completely prevent their display, or create your own criteria for displaying them using filters for a specific item or for the entire recommender (all items).
      customizationDetail: Customization
      customizationFor: 'I want to customize the recommender''s results for:'
      customizationTitle: Customizing the recommender's results
      customizeResults: Customize results
      customizedResults: Customized results
      customizing_all: 'You are customizing results for the entire recommender:'
      customizing_criteria: 'You are customizing results for a custom criterion:'
      customizing_item: 'You are customizing results for a specific item:'
      delete: Delete
      forAllPositionsWarning: The rule will apply only to all free positions in the recommender. Your existing custom <b>results will be preserved.</b>
      hiddenResults: Hidden results
      hideResult: Hide result
      identification: Additional identifier
      infoPart1: The recommender always displays results for a specific input. The input is a specific item (or property of an item) for which these recommendations are displayed.
      infoPart2: Based on the selected recommender, we distinguish the exact type of input (items), such as product, category, brand, or article, for which recommendations will be displayed.
      item: Specific item
      itemPlaceholder: Free space for custom result
      itemType: Select item type
      learnMore: Learn more
      leaveEmpty: Leave blank if you don't know how to fill in the identifier
      moreOnPlacement: More on placement.
      notSeeingRealResults: You are right, you don't see real results.
      optional: Optional
      placeToAllPositions: Place the result in all free positions
      recoIdentifier: Identifier recommended by the recommender
      recoInput: Recommender input
      recoType: Recommender type
      save: Save
      saveChanges: Save changes
      welcome: Customize the recommender's results for specific input according to your needs.
      welcomeWithType: <b>Customize results for type "{type}"</b>, which will appear in the suggestion box when you click on the search field.
      whatIsInput: What is the input of the recommender
      whatIsItemType: What is item type
      yourRecos: Your recommenders
    discard: Discard changes
    discardPopupDesc: Do you really want to discard these changes? This change cannot be undone.
    dragItem:
      addPin: Add custom result
      cancelPin: Unpin
      copyPin: New from this
      deletePin: Delete result
      edit: Adjust settings
      hidePin: Hide this result
    editCustomization: Edit customization
    edits: Customizations
    errors:
      fileTooLarge: The uploaded file is too large. The maximum allowed size is 5000 records per file.
      invalidJson: Invalid JSON on line {line}. Please check the data format.
      loading: There was an error loading customization
      notJsonlFile: File failed to load, please check the file type or format, if it looks correct, try again.
    filterList: Filter the list
    filters:
      recommendation_type: Recommender Type
      tag: Tags
      target_type: Recommender Input
    filtersSidebar:
      cancelAll: Clear all filters
      close: Close
      saveButton: Filter the list
      title: Filter the list
    findOutMore: Find out more
    globalPinTooltip: Global rule, the result has been added for the entire recommender regardless of its input. You can edit the rule <a href="{url}" target="_blank">in the customization of the whole recommender.</a>
    importDetail:
      addFileToImport: Add file with customizations you want to import
      chooseMethod: Choose your import method
      dragAndDrop: <span class="text-muted font-small-3">Drag and drop or <span class="text-primary cursor-pointer">add list</span> in <b>*.JSONL</b> file format here</span>
      handleConflictsText: Conflicts may occur during the import process - the imported position is already occupied, or the same customization exists in another position. How you would like to handle with them?
      isImported: "<b>{file}</b> has been successfully imported."
      newImportExport: New import/export
      notExistDesc: If you import customizations for a recommender that doesn’t exist on the site, they won’t be added. If you import non-existing items in the catalog, they will be marked as unavailable and won’t appear in results.<br> After processing, we’ll inform you of the import status.
      notImported: "<b>{file}</b> has not been imported. Check the summary of the import bellow."
      options:
        cancelImport: Import only if no errors or conflicts are found
        cancelImportDesc: If a conflict or error occurs, the import will not be executed, and no changes will be applied.
        keepOriginal: Keep all original customizations
        keepOriginalDesc: Conflicts will be skipped, and all original customizations in the recommender will remain unchanged.
        overwrite: Overwrite original customizations with new ones
        overwriteDesc: In case of a conflict, all original customizations will be replaced with the new ones from the file.
      recoNotExist: What happens if the recommender or item does not exist on this site?
      uploadFile: Upload your file (only valid <b>*.JSONL</b> format is supported ) with customizations.
    importExport:
      createFile: Create a custom file for import?
      createFromScratch: Sample *.JSON file you can use to create your list from scratch
      downloadFile: Download the file
      excludeExpired: Excluding the expired customizations from the export
      excludeExpiredDesc: All customizations will be exported, excluding those with expired validity.
      expiredCustomizations: What do you want to do with your existing expired customization?
      exportCustomizations: Export customizations
      exportDesc: You can export all your created customizations to a *.JSON file (or create your own according to the sample below), which you can easily import to other site or open in your favorite text editor and modify or expand as needed.
      exportSummary: Export summary
      importCustomizations: Import customizations
      keepExpired: Keeping expired customizations in the exported file
      keepExpiredDesc: All customizations, including expired ones, will be exported.
      notification: Notification
      recommender: Recommender
      record: Record
      selectExport: Select the recommenders whose customizations you want to export
      summaryOfImport: Summary of the import processing
      title: Import/export customizations
    intro:
      buttonText: Customize recommender results
      subTitle: Customize recommended items in the results of a specific recommender according to your needs.
      title: Recommendations customization
    item:
      desc: Pin to a specific position or hide (disable display) specific items in the recommender results, permanently or only within the selected time range.
      descAll: Pin to a specific position or hide (disable display) specific items in the recommender results, permanently or only within the selected time range. The setting applies globally to all inputs.
    itemNotFound: Item not found in the catalog
    labels: Tags
    less: Less
    list: List of customizations
    localPinTooltip: Local rule (item or criterion) added to the selected position in the recommender. The result is visible only in this recommender and for the selected input above.
    more: More
    multipleTypes: Multiple types
    noHiddenItems: No items have been hidden for this input/recommender yet.
    noLongerAvailable: This item is no longer available
    noRealResults: You are right, you are not seeing real results.
    notExistingItem: Nonexisting item
    notExistingPinTooltip: The item is not in the product catalog, and the recommender will ignore the pin at this position. You can safely remove this pin.
    notifications:
      exported: Customizations have been successfully exported.
      notExisting: This type of recommender does not exist anymore.
      saved: Customization was successfully saved.
      updated: Customization was successfully updated.
    onThePage: On the page
    recoInput: Input
    recoTypeAndName: Type
    repeatingPinTooltip: Repeating rule, this result has been added and is automatically applied to all free positions of the recommender.
    save: Save customization
    searchByName: Hľadajte podľa názvu
    sidebar:
      addToPosition: 'Place the result at position:'
      addingChoose: 'Choose a type and set a custom result for a specific position:'
      addingHeader: Add custom result
      alreadyDefinedCannotSave: You already have this result scheduled elsewhere with unlimited validity. It cannot be added again.
      alreadyDefinedItem: At this time, you already have the same result scheduled. Do you want to move the <b>date to {activeFrom} - {activeTo}</b> after the original rule has ended?
      cannotEditWarning: Global result cannot be edited in this mode.
      chooseItem: Choose <b>an item from the catalog that you want</b> to display in the results at a specific position in the recommender.
      criteria: Custom criteria
      criteriaDesc: Create <b>custom criteria</b> using attributes with values that items must meet to be displayed in the results at a given position in the recommender.
      editTimeRange: Edit time frame
      endDate: 'End date:'
      hideResult: Hide specific result
      hidingHeader: Hide specific result
      item: Specific item
      itemByRecoType: The selection of the item always depends on the chosen type of recommender.
      itemPosition: Enter at which position in the order the result should be displayed in the recommender.
      itemPositionTitle: Position of the result in the recommender
      leaveBlank: Leave blank
      leaveTimeEmpty: Leave blank to pin the item permanently.
      moveRule: Move rule
      noAddedCriteria: No criteria has been created. Start by adding attributes and setting their values.
      noInterval: Permanently
      notEmptyPlace: The rule could not be moved. You don't have any free positions left.
      occupedPositionText: The selected position is occupied, do you want to release the position by moving the original rule down?
      occupiedText: At this time, another result is scheduled at the selected position. Do you want to move the <b>date to {activeFrom} - {activeTo}</b> after the original rule is completed?
      pinChosenResult: Pin the selected result
      pinTime: Time frame selection
      pinTimeDesc: You can schedule or time-limit pinning the result to a specific position.
      positionAlert: The result was originally <b>added to {x}. position</b> in the recommender. With this change, you will move it to a different position.
      positionAndTime: Setting the position and time frame
      startDate: 'Start date:'
    tags:
      desc: 'You can label your customization (e.g. #blackfriday, wintersale, shirts...) to find it more easily.'
      searchPlaceholder: Enter a tag name and press Enter
      title: Label with your own tag
    targetAll:
      desc: You can always apply specific customization or custom criteria using filters for the entire recommender (all its inputs regardless of their type).
      selectedTargetDesc: You are customizing the whole recommender. The setting is applied globally to all inputs.
      wholeRecommender: Whole recommender
    targetItem:
      choosePlaceholder: Choose a specific item...
      desc: This input is always dependent on the chosen type of recommender. Generally, these are products, categories, brands, or even articles. You can always choose only one item for which you are customizing the results.
    targetTypes:
      all: Whole Recommender
      criteria: Criteria
      item: Item
    unknownItem: Unknown item
    wholeReco: Whole Recommender
  recommenders:
    and: And
    appearanceTab:
      addTemplate: Add template
      allCount: The total number of items displayed in the recommender
      allCountBold: "<b>The total number of items</b> displayed in the recommender"
      appearance: Display and appearance
      basicFormOfView: Basic display form and number of recommended items
      carouselParams: Setting carousel parameters
      chooseThemeText: 'Select a theme to customize appearance of your items and recommender:'
      chooseViewType: Choose type of view
      displayedAttributes: 'Attributes displayed in the product tile:'
      gridView: The items in the recommender be display in a grid one after another.
      interactiveGrid: Adjust the display using the interactive grid
      itemTile: Product tile and attributes returned by the recommender in its response.
      keepOrSetTitle: Keep the existing one or enter your own title
      mobileCount: The number of items displayed in one row for <b>mobile</b>
      mobileRow: Number of items displayed in one line for <br><b>mobile</b>
      onlyAppearanceText: The preview is used for <b>testing the appearance only</b> and does not consider the logic of selected recommender.
      overviewInfo: It is not necessary to save the settings in advance to display this preview.
      overviewSettings: Overview of appearance settings
      pcCount: The number of items displayed in one row for <b>desktop computers and laptops</b>
      recoTitle: Title above recommendations
      replaceArrows: "<b>Replace on mobile arrows</b> by displaying an overlap of the next item in the sequence. Dragging and tapping this item will move the carousel."
      rotateItems:
        byOne: only one item at a time
        byRow: all over the line at once
        title: "<b>Move/rotate items</b> in the carousel:"
      rowAndColumns: Items in a row and number of rows
      rowView: The items in the recommender will rotate in a row one after another.
      showArrows: "<b>Show Arrows</b> - basic control for viewing each carousel's items"
      showPagination: Display pagination - control element and information about the order of displayed items in the recommender
      subTitle: Set the number of recommended items, texts, form and appearance according to your preferences.
      tabletCount: The number of items displayed in one row for <b>tablets</b> and smaller screens
      tabletRow: Number of items displayed in one line for <b>tablets</b> and smaller screens
      titlePosition:
        center: On the center
        left: On the left
        none: Do not display the title
        right: On the right
        title: 'Title placement above recommended items:'
      visibleRow: Number of items still visible in the carousel for <b>desktop and laptop</b>
      wswgButtonText: Product tile editor
      wswgText: The appearance of the tile and displayed attributes in the product tile can be changed using the product tile editor.
      wswgTitle: Product tile options and attributes editing
    attributeAssignment:
      absolute_margin_field:
        subTitle: Attribute named <b>MARGIN</b> or <b>ABSOLUTE MARGIN</b>, containing the margin of items. We understand that this is confidential information, so it is not accessible or transmitted anywhere and is only used to prioritize results in the recommender.
        title: Item margin
        useCustom: 'If there is a field in your feed with the absolute item margin under a different name (name doesn`t match “absolute_margin”) that you want to use, and its values (numbers from 0 to N) represent the margin of items, assign it:'
        useCustomRadio: Assign a custom field from the added feed to the absolute margin attribute
        useExisting: 'If there is a field in your feed with the relative item margin under a different name (name doesn`t match “_margin”) that you want to use, and its values (numbers from 0 to 1.0) represent the margin of items, assign it:'
        useExistingRadio: Assign a custom field from the added feed to the relative margin attribute
        useExistingText: Use the relative margin field (preferred option)
      assignedAttr: 'Assigned field: {element}'
      availability_field:
        subTitle: We prefer the <b>AVAILABILITY RANK</b> attribute, which displays various availability levels of the item in the range of 1-15. This attribute allows us to determine which items should be shown in the recommendation results (values 1-7 indicating availability) and which should not (values 8-15 indicating unavailable items). An alternative is the <b>AVAILABILITY</b> attribute, representing the availability (value 1) or unavailability of the item (value 0).
        title: Item availability
        useCustom: 'If you want to use “availability” or a custom field for availability, where values (numbers 1 and 0) represent available and unavailable items, assign it:'
        useCustomRadio: Assign a custom field from the added feed to the "availability" attribute
        useExisting: 'If there is a field in your feed with item availability under a different name (name doesn`t match “availability_rank”) that you want to use, and its values (whole numbers 1-15) represent item availability, assign it:'
        useExistingRadio: Assign a custom field from the added feed to the "availability_rank" attribute
        useExistingText: Use the "availability_rank" field (preferred option)
      availability_rank_field:
        subTitle: We prefer the <b>AVAILABILITY RANK</b> attribute, which displays various availability levels of the item in the range of 1-15. This attribute allows us to determine which items should be shown in the recommendation results (values 1-7 indicating availability) and which should not (values 8-15 indicating unavailable items). An alternative is the <b>AVAILABILITY</b> attribute, representing the availability (value 1) or unavailability of the item (value 0).
        title: Item availability
        useCustom: 'If you want to use for availability "availability" or a custom field whose values (numbers 1 and 0) represent available and unavailable items, assign it:'
        useCustomRadio: Assign a custom field from the added feed to the "availability" attribute
        useExisting: 'If there is an item availability field in your feed with a different name (the name does not match "availability_rank") that you want to use and its values (integers 1-15) represent item availability, assign it:'
        useExistingRadio: Assign a custom field from the added feed to the "availability_rank" attribute
        useExistingText: Use the "availability_rank" field (preferred option)
      beforeStart: Before you start ...
      boost_field:
        subTitle: The <b>BOOST</b> attribute determines how the items in the results will be preferred. Its values are calculated based on your settings in the application.
        title: Item prioritization
        useCustom: 'If there is a field in the feed with a different name (does not match "boost") that you want to use and its values (numbers 0,1,2,3) represent how the items will be preferred, assign it:'
        useExisting: We will calculate the value of the "boost" attribute based on the settings in the application.
      catalogBrowser: catalog browser.
      choose: Select field ...
      customField: Assign a custom field from the added feed to the attribute
      discount_field:
        subTitle: The <b>DISCOUNT</b> attribute determines the discount's value according to which the items in the recommender's results will be sorted, e.g., discount according to the discount amount.
        title: Item discount
        useCustom: 'If there is a custom attribute in your data that directly represents discount and its values are numbers from 0 to 1.0 (where e.g. 0.42 represent 42% discount), assign it to:'
        useExisting: 'We will calculate the value of the ''discount'' attribute based on the assigned price in the ''price'' attribute using the old price attribute ''price_old''. Choose the price field in the feed that you want to use for this calculation:'
        useExistingRadio: Assign a custom price field from the added feed to the attribute
        useExistingText: Use the precomputed discount field (preferred option)
      findHelp: You can find the necessary information about these attributes and their values in our documentation.
      finishedText: Now, we should have all the necessary settings and completed data to continue adding recommenders to your site.
      globalText: Global setting for all recommenders; if necessary, you can change it in each recommender's settings separately.
      introduced_at_field:
        subTitle: The attribute named <b>INTRODUCED AT</b> contains the date the item went on sale (do not confuse this attribute with the date the item was added to the catalog) for novelties recommenders.
        title: The date the item starts selling
        useCustom: 'If there is a field in your feed with the start date of the sale of the item under a different name (the name does not match "introduced_at"), assign it:'
        useExisting: The existing "introduced_at" field prepared based on the documentation will be used.
      margin_field:
        subTitle: The <b>MARGIN</b> (relative margin) or <b>ABSOLUTE MARGIN</b> attribute (absolute margin) contains the margin of items. We understand this is confidential information; therefore, it is not available or sent anywhere and is only used to prioritize the results in the recommender.
        title: Item margin
        useCustom: 'If there is an item absolute margin field in your feed with a different name (the name does not match "absolute_margin") that you want to use and its values (numbers from 0 to N) represent the item margin, assign it:'
        useCustomRadio: Assign a custom field from the added feed to the absolute margin attribute
        useExisting: 'If there is an item relative margin field in your feed with a different name (the name does not match “_margin”) that you want to use and its values (numbers from 0 to 1.0) represent the item margin, assign it:'
        useExistingRadio: Assign a custom field from the added feed to the relative margin attribute
        useExistingText: Use the relative margin field (preferred option)
      moreInfoAboutAttributes: You can find detailed information about the attributes <a href="https://docs.luigisbox.com/recommendations.html" target="_blank">in our documentation</a>. If you are unsure about the attributes in your feed, you can view the available product attributes and their values in our
      notInFeed: The attribute is not found in the feed, or you do not want to consider it in the recommenders. This setting may cause unexpected behavior of recommenders.
      notUseAttr: Do not use this attribute in recommenders
      onlyThisWarning: The setting change applies only to the <b>{recommender}</b> recommender
      popularity_field:
        subTitle: The <b>POPULARITY</b> attribute determines how items will be sorted by popularity in the recommender results, e.g., the most viewed products.
        title: Item popularity
        useCustom: 'If there is a field in the feed with a different name (the name does not match "popularity") that you want to use and its values (numbers 0-N) represent the popularity of the items, assign it:'
        useExisting: We will calculate the value of the "popularity" attribute from purchases, clicks and impressions.
      price_field:
        subTitle: The <b>PRICE</b> attribute contains the item's current price, which is used to recommend cheaper products in the cart and calculate discounts.
        title: Item price
        useCustom: 'If there is a field in your feed with a different name (the name does not match "price") that you want to use to represent the price of the items, assign it:'
        useExisting: The existing "price" field prepared based on the documentation will be used.
      saveAndContinue: Save and continue to the next attribute
      startTextHelp: Help us <b>understand your data</b> so we can fully utilize the referrer capabilities on your site.
      taskHelp: Your task will be to <b>confirm using some attributes</b> or assign them custom fields from your feed. This setup only takes a few minutes.
      title: Attribute assignment
      useCustom: Use custom settings
      useDefault: Use default settings
    attributes:
      availabilityInfo: You can usually find the attribute of availability under the name avalability_rank. It allows the recommendation of available items based on a numerical range of 1 - 15, where the numbers are assigned availability (1 - available to 15 - unavailable). We automatically display all products with a value lower than 8.
      backToRecoButton: Back to recommender settings
      editAttribute: Set attribute
      marginInfo: You can usually find the product margin attribute under the name margin. We calculate the absolute margin for referral preference using the margin and current price attributes.
      missingText: We're sorry, but we've noticed we're still missing some data for some recommenders to work properly.
      moreInfoText: You can find more about attributes and their values <a href="https://docs.luigisbox.com/#introduction">in our documentation.</a>
      noveltyInfo: The attribute of the date when the item started to be sold (not when it was added to the catalog), you can usually find it under the name introduced_at. This attribute is necessary for the correct functioning of novelties recommenders.
      priceInfo: You can usually find the attribute of the current price of the product under the name price_amount. We use it when calculating discounts when prioritizing and for recommenders of discounted items.
      priceOldInfo: You can usually find the attribute of the product's original price under the name price_old_amount. We use the price attributes (price_amount and price_old_amount) to calculate discounts.
      remindLater: Remind me later
      saveContinue: Save and continue
      selectAttribute: Select attribute...
      title: A few trivialities before you start...
    beSettingsCreated: Recommender settings have been set up.
    beSettingsSaved: Recommender settings have been saved.
    between: Between
    categoryStructure:
      allPlacementInfo: Choose if there are no categories related to the product placement hierarchy in the structure (these categories are often used rather than a filter attribute), e.g., Collection, Color, Signs...
      allPlacements: "<b>All product placements</b> in the structure according to the supplied feed"
      categoryBlacklist:
        addCustomCategory: Add Custom Category
        addToBlacklist: Add category to list
        allCategories: After marking a category as unsuitable for content recommendations, <b>all its subcategories will be automatically marked as well.</b>
        areYouSureSkip: Are you sure you want to skip adding categories unsuitable for content recommendations?
        blackList: Categories unsuitable for content recommendations
        chooseNotSuitable: Choose unsuitable categories
        dontForgetText: Remember, all subcategories will be automatically added to the list.
        empty: The list is currently empty
        modalManualAddText: If you know of a category that should be marked but you didn't find it in the list, search for it.
        noToAddText: If it's not necessary to include any categories unsuitable for content recommendations, you can safely skip this step.
        saveAndContinue: Save and continue
        skipAddingText: Skip adding
        skipStep: Skip this step
        skipText: Make sure before proceeding that <b>there is really no need to add any categories to the list.</b> These categories will not be considered for content product recommendations.
        skipTitle: Skip identification of categories unsuitable for content recommendations.
        subTitle: 'Help us identify marketing and seasonal categories linking unrelated products that won''t be considered for content based recommendations.<br> For example <b>Black Friday, Top, Discounted, Manufacturer, New Arrivals, Open Box, Brands, Clearance.</b> <br> <b>Important: By marking a category, you don''t disallow its products from recommending.</b> To forbid products from certain categories, you must configure filters in the recommendation settings.'
        title: Identification of categories unsuitable for content recommendations
        warningText: Marketing and seasonal categories have no additional information for recommending related or similar products. For example, input product contains category Black Friday. Such category doesn't help to understand product's features and might spoil recommendations.
      confirmAndContinue: Confirm ({step}/{all}) and select next
      confirmAndEnd: Finish and continue
      crossSell:
        alertText: If you don't plan to use cross-sell recommenders (in-cart recommendations, after-cart window recommendations), you can skip this setup and complete it later when needed.
        box:
          addProductPlaceholder: Match here a related product with a <b>price lower than €150.</b>
          helpText: You do not set your recommender results for the selected products by pairing. By matching products, you help us understand your categorization faster for cross-sell recommenders.
          mustBePrice: The related product must be priced below €150.
          option1: a related product or a typical accessory that is suitable to purchase,
          option2: a cheap product, <b>with a price lower than €150.</b>
          title: 'The correctly assigned product must be:'
        confirmAndContinue: Confirm and continue
        continueSettings: Continue to adding
        createNewReco: Create new recommender
        finishLater: Complete the setup later
        finishedText: Thanks to matching, we can understand product categorization for cross-sell recommenders faster. Feel free to proceed to create a new referrer.
        gameSimpleRules: It's like a game with simple rules
        gameText: Your task will be to <b>assign another, cheaper related product to the selected products suitable for additional purchase.</b> This is how you typically assign products that represent accessories.
        goodJob: Great job!
        haveTime: Do you still have a moment?
        matchingProductsText: If you have enjoyed matching related products and want your recommenders to be as effective as possible immediately, you can repeat the product recommendation process and help us even more.
        settingsAgain: Go through the settings again
        subTitle: Help us learn faster and understand the relationships between categories for cross-sell recommenders with easy product matching.
        title: Cross-sell categories
      mainPlacement: "<b>Only the main product placement</b> in the structure according to the supplied feed"
      mainPlacementInfo: This structure is defined as the main one in the feed. Select if the other locations do not start with a suitable category or if you cannot decide.
      structureDoneText: Great, now we know what we need to understand your data categorization better and choose the proper structure. Feel free to continue with the settings!
      subTitle: Help us understand your feed categorization so we know which structure to consider.
      title: Verification of the category structure
      verifyStructure: Verification of the structure
      verifyText: Help us determine, based on 5 randomly selected products, whether it is better to consider all categories in the feed or only the main category for the proper functioning of the recommenders.
      whichToConsider: Which of the product placements below shall we consider?
    chooseFromList: Choose from the list ...
    colors:
      colorNames:
        buttons_text_color: The color of the text in the button
        header_text_color: The color of the text in the title
        hover_buttons_color: The color of the button after hovering the mouse
        hover_buttons_text_color: The color of the text in the button after hovering the mouse
        hover_titles_color: The color of clickable texts (links) after hovering the mouse
        primary_color: Main conversion color (buttons, links...)
        primary_text_color: The color of clickable text (links)
        secondary_text_color: The color of the normal text
      info: General settings for recommender
      setColorScheme: Generate color scheme
      title: Color settings in the recommender
    confirm: confirm
    confirmPopup:
      close: Close
      confirmDelete: Are you sure you want to delete this recommender?
      confirmUnpublish: Are you sure you want to unpublish this recommender?
      delete: Delete
    contactIfInterested: Contact us if you are interested.
    continueToSettings: Continue to settings
    copySidebar:
      alreadyHasModel: This site already has a recommender model selected for copying.
      conflictsDetected: Conflicts Detected
      copyFail: The recommenders could not be copied. Please try again later.
      copySuccess: The recommenders have been successfully copied to the selected site destinations.
      destinationSites: Destination sites
      keepOrReplace: Would you like to keep the old recommenders or replace them?
      noDestinationSites: You have no other destination sites in your project for copying recommenders.
      pasteWhere: Where do you want to paste selected recommenders?
      proceed: Proceed
      projectSites: Project sites
      replace: Replace
      replaceText: Some domain pages already have recommenders selected for copying. If you choose to override, the existing recommenders will be deleted and replaced with new ones.
      selectAll: Select all
      skipAndKeep: Skip and keep existing
      sureToDeleteAll: Are you sure you want to remove selected recommenders?
      willCopy: 'Selected recommenders that will be copy-pasted to site destinations:'
    criteriaAlerts:
      conditionMissing: Criterium condition for {criterium} right_values are missing
      criteriaMissing: Criteria are missing
      valuesMissing: "{criterium} values are missing"
    customScript: Custom script
    dateMonths:
      '1': January
      '10': October
      '11': November
      '12': December
      '2': February
      '3': March
      '4': April
      '5': May
      '6': June
      '7': July
      '8': August
      '9': September
    descriptions:
      article_articles:
        description: Most relevant articles based on the input article (blog post).
        input: Article
        name: Article related articles
        placement: Article detail page
        placement_title: Recommended similar articles
        short_description: Articles similar to input article.
      article_items:
        description: Most relevant products based on the input article (blog post).
        input: Article
        name: Article related products
        placement: Article detail page
        placement_title: Recommended related products
        short_description: Products similar to input article.
      autocomplete_popup:
        description: Popular products, queries, categories, etc. in autocomplete screen, before entering the first character of the input query.
        name: Autocomplete popup (Top items)
        placement: Autocomplete init screen
        placement_title: Recommended popular products
        short_description: Popular products in autocomplete popup screen.
      basket:
        description: Related products usually bought together (cross-sell) with all products already in the basket. By default, recommender preffers cheaper products (complements). Products are diversified by category to offer variety of different products if possible.
        input: Products
        name: Basket related complementary products
        placement: Basket detail
        placement_title: Recommended related products
        short_description: Cross-sell products related to products in basket.
      basket_popup:
        description: Related products usually bought together (cross-sell) with a product most recently added into the basket. By default, recommender preffers cheaper products (complements). Products are diversified by category to offer variety of different products if possible.
        input: Product
        name: Basket popup related complementary products
        placement: Basket pop up
        placement_title: Recommended related products
        short_description: Cross-sell products related to the product, most recently added into basket.
      brand:
        description: Most relevant trendy products from input brand for the user. Results are diversified to as much categories as possible.
        input: Brand
        name: Top brand products
        placement: Product listing (PLP)
        placement_title: Recommended among this brand
        short_description: Trends from selected brand.
      category:
        description: Most relevant trending products from input category for the user. Results are diversified to as much next level subcategories as possible.
        input: Category
        name: Top category products
        placement: Product listing (PLP)
        placement_title: Recommended among this category
        short_description: Trends from selected category.
      discount:
        description: Discounted products based on discount amount. Could be used on homepage, in Product listing to offer products from this category/brand only, in PDP (in combination with some filter) to offer e.g. discounted products from the same collection.
        name: Discounted products
        placement: Arbitrary
        placement_title: Recommended discounnted products
        short_description: Discounted products by prices diff.
      discount_by_label:
        description: Popular products with defined label indicating discounted products. Could be used on homepage, in Product listing to offer products from this category/brand only, in PDP (in combination with some filter) to offer e.g. discounted products from the same collection.
        name: Discounted products by label
        placement: Arbitrary
        placement_title: Recommended discounnted products
        short_description: Discounted products by label.
      favorites:
        description: Favorite products most likely to be repeatedly bought by a user. This model is suitable for segments with consumable products, where users are likely to repeat purchases (e.g. groceries, pharmacies, b2b supplies).
        input: User
        name: Favorites
        placement: Arbitrary, basket, homepage
        placement_title: Repeatedly bought products
        short_description: Favorite products most likely to be repeatedly bought by a user.
      item_detail_alternatives:
        description: Similar products / alternatives recommender preferring slightly more expensive products compared to input product (upsell).
        input: Product
        name: Similar (alternative) products
        placement: Product detail page (PDP)
        placement_title: Recommended alternatives
        short_description: Up-sell alternative products to input product.
      item_detail_complements:
        description: Related products usually bought together (cross-sell) with input product. By default, recommender preffers cheaper products (complements). Products are diversified by category to offer variety of different products if possible.
        input: Product
        name: Related complementary products
        placement: Product detail page (PDP)
        placement_title: Recommended related products
        short_description: Cross-sell products related to input product.
      last_seen:
        description: Products most recently visited by a user.
        input: User
        name: Recently visited products
        placement: Arbitrary
        placement_title: Recently visited
        short_description: Products most recently visited by a user.
      news:
        description: Novel products based on date of introduction. Could be used on homepage, in Product listing to offer products from this category/brand only, in PDP (in combination with some filter) to offer e.g. novel products from the same collection.
        name: Novel products
        placement: Arbitrary
        placement_title: Recommended novel products
        short_description: Novel products by date of introduction.
      news_by_label:
        description: Popular products with defined label indicating novel products. Could be used on homepage, in Product listing to offer products from this category/brand only, in PDP (in combination with some filter) to offer e.g. novel products from the same collection.
        name: Novel products by label
        placement: Arbitrary
        placement_title: Recommended novel products
        short_description: Novel products by label.
      random:
        description: Random products. It gets more useful when combined with some characteristic, e.g., new products added within last month, discounted products, products from same brand as input product.
        name: Random products
        placement: Arbitrary
        placement_title: Recommended
        short_description: Random products.
      random_news:
        description: Random novel products. An example of random recommender usage. It recommends primarily random items upto 30 days old, then 60, 90. could be combined with more filters, e.g. from certain category.
        name: Random novel products
        placement: Arbitrary
        placement_title: Recommended novel products
        short_description: Random novel products.
      recently_ordered:
        description: Products most recently ordered by a user.
        input: User
        name: Recently ordered products
        placement: Arbitrary
        placement_title: Recently ordered
        short_description: Products most recently ordered by a user.
      test_reco:
        description: Simple, most popular recommender to test client requests.
        name: Test
        placement: Arbitrary
        placement_title: Recommended
        short_description: Global trends to test client requests.
      top_categories:
        description: Most relevant categories for the user. Used as a shortcut to user's preferred Product listings.
        input: User
        name: Top user's categories
        placement: Arbitrary, homepage, page 404
        placement_title: Recommended categories
        short_description: Most relevant categories for the user.
      trends:
        description: Most popular products. Could be used on homepage, in Product listing to offer products from this category/brand only, in PDP (in combination with some filter) to offer e.g. popular products from the same collection. Could be ordered by different attribute than popularity (e.g., price_amount, discount).
        name: Popular products / trends
        placement: Arbitrary, homepage, page 404
        placement_title: Recommended popular products
        short_description: Most popular products.
      user_click_based:
        description: Recommender offers products similar to those clicked but not bought recently by the user / or trends (if user has no past interactions). The idea is to help user finish the purchase.
        input: User
        name: User's clicks related products
        placement: Arbitrary, homepage, page 404
        placement_title: Recommended for you
        short_description: Products similar (up-sell) to user's recenty viewed products.
      user_conversion_based:
        description: Recommender offers products related (cross-sell) to previously purchased or trends (if user has no past interactions). The idea is to help user find more interesting products to buy.
        input: User
        name: User's conversions related products
        placement: Arbitrary, homepage, page 404
        placement_title: Recommended for you
        short_description: Products related (cross-sell) to user's recenty purchased products.
    detail:
      adjustReco: Adjust the recommender according to your preferences
      beText: For backend recommenders integration, send the code below and placement instructions to your agency or developer.
      createRecommender: Create a recommender
      delete: Delete recommender
      enterReco: Choose the recommender type
      examplePlacement: An example of placement
      howRecoWorks:
        howToChoose: How to choose a recommender type?
        infoText: You simply choose the recommender type according to the way it recommends items. Then you adjust its settings according to your requirements and determine where it should be displayed.
        title: How do recommenders work?
      inputForReco: Recommended placement
      nameOfReco: Name of your recommender
      recoTypesTitle: Types of recommenders
      save: Save recommender
      tabs:
        appearance: Appearance
        beIntegration: the recommender has a backend integration with Luigi's Box
        placement: Placement
        settings: Settings
    devOnly: "(DEV 🤓 person only)"
    discardForSureSetting: Are you sure you want to discard this setting?
    discardSetting: Discard setting
    dynamicMainValues: Dynamic values (main only)
    dynamicValues: Dynamic values
    editAttribute: Edit attribute
    enterRecoType: Enter recommender name...
    existanceFilters: "<b>Existence filters</b> are used to determine if an attribute exists, which means they don't need any values."
    globalSettings:
      crossSell:
        goToTraining: Go to training categories
        title: Training of cross-sell categories
      setAllAttributes: Set all attributes again
      subTitle: Attribute and category settings for all referrers and general settings you went through when activating the service.
      title: Global settings
    goBackToPlacement: Go back to placement
    grid: Show items in a grid
    if: If
    launchWizard: Launch the wizard
    list:
      activate: Activate recommender
      active: Active
      continue: Continue to settings
      copyAndPaste: Copy & Paste
      created: Created recommenders
      customizeNewButton: Add new recommender
      deactivate: Deactivate recommender
      deactivateTitle: If needed, you can unpublish the recommender (it will be hidden on the website) and publish it again anytime.
      delete: Remove
      duplicate: Duplicate
      edit: Edit
      failed: Failed
      failedOperational: Failed Operational
      findReco: Find recommender
      howItWorks: How does it work?
      inActive: Deactivated
      initializing: Initializing
      initializingOperational: Initializing Operational
      name: Recommender name
      published: Published
      simulator: Recommender simulator
      status: Status
      subTitle: Set up and add Luigi's Box recommenders anywhere on your site according to your preferences.
      title: Recommenders Setup
      unpublish: Unpublish recommender
      waitingForLaunch: Waiting for publishing
    longInit: Recommender training may take up to <b>several hours</b> to be ready to use.
    missingImportantAttr: The recommender is missing an important attribute!
    needToCompletePlacement: You need to complete Placement form first
    needToFillContinue: You need to fill all required fields before continuing to the next step.
    needToFillPublish: You need to fill all required fields before publishing.
    noPreview: There is no preview available.
    nonDev: Non-dev person?
    notAvailableYet: Not available yet
    not_in: Nor
    or: Or
    placementTab:
      addPlacement: Add another placement
      additionalUrlRules: Create additional rules for URLs using simple requirements and regular expressions. For example, you can use this setting if the placement using the selectors is not specific enough and you need to set the display of the recommender for a particular page or type of page.
      afterSelector: place after the specified selector
      blackListItemsCard:
        checkBox: Yes, I made custom annotations
        desc: This setting <b>is automated in LBX services</b>. Custom annotations can be defined for exceptional cases (e.g., another recommender service) to avoid item repetition on the same page. See <a target="_blank" href="https://docs.luigisbox.com/recommendations/recco_js.html#prevent-products-from-being-recommended-via-annotations">documentation</a> for more details.
        question: "<b>Did you already make some annotations?</b> (Just for the record.)"
        subTitle: This setting is optional and is automated in LBX services. The recommender will work correctly even without it.
        title: Items that must not be recommended (Optional)
      cardTitlePlacement: Placement of Recommender on Page
      contextCard:
        descPart1: Setting helps the recommender identify, e.g., used filters on the product listing page and use these settings into when recommending items.
        descPart2: You need to create a function that returns a dict of restrictions for the recommender identified in request time. See <a target="_blank" href="https://docs.luigisbox.com/recommendations/api.html">recommendation_context</a> for more details.
        subTitle: The setting can be done only with the custom script for now. However, <b>it is optional setting</b>, and the recommender will work correctly even without it.
        title: Contextual restrictions (Optional)
      createAdditionalReq: Create additional requirements for URL addresses if you want the recommender to appear on specific pages or types of pages (e.g., only on product listings).
      displayOnPages: Display on specific pages and types of pages
      enterSelector: Enter a selector and select the placement where you want the recommender to appear
      getItemIds:
        descPart1: Some recommenders need an information about <b>the product(s) they recommend to</b>. Depending on the recommender type it might be a product or category on whose detail we are, products in the basket, etc. Item identifiers for the function that returns a list of items to base recommendations on depending on the type of recommendation and placement. See <a target="_blank" href="https://docs.luigisbox.com/recommendations/recco_js.html">documentation</a> for more details.
        noCustomFieldText: "<b>You can use our wizard in simple steps</b>. If you are a person with advanced programming skills, you can set product identifiers using custom functions."
        title: Item identifiers (Required)
        titleOptional: Item identifiers (Optional)
      infoPlacement1: Using the HTML selector on the page, you can determine where the recommender should be displayed on the page. A non-dev person is a able to define the placement via a clickable wizard. <b>The recommender will appear</b> on every page of your website <b>where the specified selector will be located</b>.
      infoPlacement2: Choose a unique selector (at least one, ideally, more selectors) on the page
      infoPlacement3: You can enter multiple recommender placements for different pages
      intoSelector: place in the specified selector
      methods:
        devTeam: "<b>Use my development team</b> or the agency with which I collaborate (Suitable for back-end integration of Luigi's Box services)"
        manually: "<b>Set the placement manually</b> (Suitable for everyone, no programming skills required)"
        title: 'Choose your preferred method for placing the recommender on the page:'
      selectorsPlaceholder: 'Enter at least one, ideally more selectors, e.g.: body #category .content'
      specialSettings: Special recommender settings
      specialSettingsInfoMessage: This section allows to <b>specify important attributes</b> (to improve recommendation quality), set <b>business specific rules</b> (force / prevent products with certain characteristics), modify default logics (only if really needed).
      specialSettingsText: The recommender is pre-configured and ready to use. Advanced <b>users can customize it to meet specific needs or criteria</b>, but it performs well with the default settings.
      subTitle: Define where and on which pages you want this recommender to appear. You can specify where it should appear using the selectors or directly by inserting the unique code for this recommender, which you can find below, in the places where it should appear.
      title: Placement
      urls:
        placeholder_url_begins_with: Part of the URL, napr. https://www.example.com/category/
        placeholder_url_ends_with: Part of the URL, napr. /detail/
        placeholder_url_is: A specific URL, for example https://www.example.com
        placeholder_url_matches: Part of the URL, e.g. /product/
        placeholder_url_regex: Enter regex, e.g. \/page\/[^\/]+
        text_url_begins_with: Specify exactly how the URL must begin
        text_url_ends_with: Specify exactly how the URL must end
        text_url_is: Enter a specific URL that must match
        text_url_matches: Enter a string that must be somewhere in the URL
        text_url_regex: Enter a regular expression for the text in the URL
        url_begins_with: URL starts with
        url_ends_with: URL ends with
        url_is: URL is exactly
        url_matches: URL contains
        url_regex: URL matches regex
    popupLaunch:
      info: If you decide to proceed, <strong>you will activate the Luigi's Box Recommender service</strong> and replace the current solution on your site.
    pressEnterTo: Press enter to
    proceedToSpecialButton: Proceed to special settings
    proceedToSpecialSettings: Proceed to special settings and assign the missing attribute to ensure the recommender functions properly.
    removeAttribute: Remove attribute
    returnLater: Recommender traininig may take up to several hours. <b>Return later</b> to publish the recommender on your page.
    rows: Rotate items in a row (carousel)
    sample: Sample
    saveAllChanges: Save all changes
    saveAndGoTo: Save and continue to {to}
    saveChangesPopup:
      saveAndGoToPlacement: Save changes and go back to placement
      text: The changes you made will be automatically applied to the selected recommender after saving and <b>cannot be undone</b>. However, you can always <b>revert the recommender to its original settings</b>.
    searchInReco: 'Search in recommenders:'
    seeRecoExamples: For more information, see <a href="https://docs.luigisbox.com/recommendations/recco_js.html" target="_blank">recco.js </a> and <a href="https://docs.luigisbox.com/recommendations/api.html" target="_blank">Recommender API </a> documentations.
    segments:
      baby: e.g., model, age, gender, etc.
      body: e.g., active substance, subject, brand, etc.
      books: e.g., author, producer, console, etc.
      consumer: e.g., brand, model, compatibility, etc.
      fashion: e.g., gender, collection, color, cut, etc.
      food: e.g., bio, lacto free, gluten free, vegetarian, vegan, etc.
      garden: e.g., manufacturer, subject, etc.
      general: and industry segment specifics, e.g., for clothes segment - collection, gender, cut; for furniture - material, design, color, etc.
      hobby: e.g., material, etc.
      household: e.g., collection, material, design, color, etc.
      luxury: e.g., material, stone, collection, etc.
      market: e.g., segment, gender, etc.
      pets: e.g., animal, subject, manufacturer, etc.
      pharma: e.g., active substance, subject, brand, etc.
      segmentDesc: The recommender evaluates <b>the similarity</b> of items based on various attributes. You can specifically <b>extend these attributes based on your data</b>, {examples}
      sports: e.g., gender, sport, material, etc.
      supplies: e.g., manufacturer, compatibility, etc.
      travel: e.g., destination, transport, etc.
    selectRelatedProduct: Select a related product/accessory...
    settingsTab:
      addAttribute: Add attribute
      addCriteriaButton: Add criteria
      addMoreAttributes: Add attributes
      addMoreAttributesSubtitle: You can add additional (maximum 6) most important segment specific attributes to the basic attributes by which similarity is evaluated.
      addMoreAttributesTitle: Do you want to expand the attributes based on which the similarity of the recommended items is evaluated?
      addMoreInfo2: If the product names already <b>contain the name of the brand or manufacturer</b>, it is not necessary to consider the BRAND attribute, as it is already included in the TITLE.
      advancedSettings:
        attributesSettings:
          choosedOption: 'You have selected the option:'
          editSettings: Edit setting
          introducedAtAlert: The attribute named <b>INTRODUCED AT</b> contains the date the item went on sale (do not confuse this attribute with the date the item was added to the catalog) for novelties recommenders.
          title: Attributes settings
        categories:
          blacklist:
            addCategory: Add category
            alertInfo1: Marketing and seasonal categories linking unrelated products that we will not consider for content product recommendations.
            alertInfo2: If you see categories such as <b>Brands, Clearance, Black Friday, Top, Discounted, Manufacturer, New Arrivals, Open Box</b> in your top-level category list, mark them as inappropriate.
            subTitle: After marking a category as inappropriate for content recommendations, <b>all its subcategories will also be automatically marked.</b>
            title: List of categories that will not be considered in content recommendations.
          title: Category settings
        notModifyText: "<b>These settings are predefined and do not need to be modified.</b> We recommend changing them only if you are very familiar with the attributes of your product feed and need to align a specific attribute other than the default one that the recommender uses. <b>Your changes will be reflected only for this recommender.</b>"
        subTitle: Category and attribute settings that recommenders work with. General settings you went through when activating the service.
        title: Extended settings
      allowCustomizations: Are recommender customizations allowed for the recommender?
      allowCustomizationsDesc: There may be exceptional cases where disabling customizations (e.g. pin to position) makes sense, such as AB test where client want to measure how much customizations influence recommender performance and wants disable them in one on tested variant. However, we <b>do not</b> recommend <b>disabling customizations</b> by default.
      allowCustomizationsForReco: Allow customizations for this recommender
      allowPersonalization: Enable personalization for these recommendations.
      allowTrendComplement: Allow to supplement the lack of high quality recommendations by trends
      boostWithCriteriaInfo1: In the recommender, items that meet these criteria will be <b>prioritized over others</b>. However, <b>they are not a requirement</b> for display in the results. Instead, you define specific attributes, their values, and the requirements for fulfillment.
      boostWithCriteriaInfo2: Simplified, you instruct the recommender, e.g., I want to prefer "clothing from the same collection" or "furniture from the same material."
      boostWithCriteriaText: Add attributes with values that items should meet in order to be prioritized in the results over others.
      boostWithCriteriaTitle: Do you want to prioritize items meeting specific criteria over others in the recommender?
      brandIncluded: "<b>The name of the brand/manufacturer is included</b> in the names of the items in the catalog (e.g., <b>Apple</b> iPhone 15 Pro)."
      chooseTypeText: Specify the correct type of recommender output according to your data.
      choosenTypeInfo: Select proper data type from your product catalog that the recommender processes on its input. In the most cases, "product" or "item" is used, some recommenders accept "category" of "article". As these can be named differently in your data, make sure we have selected the correct type.
      correctTypeChoosen: Specify the correct type of recommender input according to your data.
      customCriteriaInfo1: Criteria that items in the recommender must <b>meet to be displayed</b>. You define specific attributes, their values, and the conditions they must meet.
      customCriteriaInfo2: Simplified, you instruct the recommender, e.g., recommend only "same gender, unwrapped products" or "things that are not on sale," etc.
      customCriteriaSubtitle: Add attributes with values that items must meet to be displayed in the recommender.
      customCriteriaTitle: Are there specific criteria items displayed in this recommender must meet?
      desiredFormatItemIds: 'It needs to return an array. For example: <b>return [''/item1'', ''/item2'']</b>'
      discardAll: Discard all changes
      discardAllText: If you choose to continue, you will discard the changes made and <b>restore the original settings</b> of the recommender.
      discount:
        computeDefault: Calculate item discount based on the assigned price in the selected attribute
        desc: To ensure the recommender functions correctly based on discount height, we need to verify that the correct <b>attribute for discount calculation</b> is assigned, or an attribute <b>containing the height of the item discount</b>, which is used to sort discounts in the recommender.
        subTitle: Ensure that the correct price attribute is specified for calculating item discounts, or an attribute containing the height of item discounts.
        title: Is the correct discount value attribute specified for sorting items in the recommender?
        useCustom: Use the attribute containing the height of the item discount (contains numbers from 0 to 1.0)
      discountStart: Is the correct label attribute specified for catalog discounts?
      discountStartHeader: Make sure the correct "Discount" label attribute is entered. If no attribute has been added, add it now.
      discountStartInfo: In order for the novelties recommender to work correctly, we need to verify that the correct <b>label attribute "Discount" is assigned</b>. Based on this attribute, the recommender evaluates <b>what are the discounts</b> in the catalog.
      nameExplanation: "<b>This is a unique name for the recommender</b>, allowing you to identify it wherever necessary, such as in Analytics, back-end requests, our unit stats, etc."
      needToSave: This field needs to be <b>filled before saving.</b>
      needToSaveBeforePublish: This field needs to be <b>filled before publishing.</b>
      needToSaveWizardBasket: We filled this setting from wizard, <b>save configuration to apply it</b>.
      needToSaveWizardBasketError: We couldn't fill this setting from wizard, <b>you can try expert mode, or contact support</b>.
      needToSaveWizardPlacement: We filled this setting from wizard, <b>save configuration to apply it</b>.
      newsStart: Is the correct label attribute specified for catalog novelties?
      newsStartHeader: Make sure the correct "New" label attribute is specified. If no attribute has been added, add it now.
      newsStartInfo: In order for the novelties recommender to work properly, we need to verify that the correct <b>"New" label attribute</b> is assigned. Based on this attribute, the recommender <b>evaluates what are novelties</b> in the catalog.
      noApplyThis: "<b>I don't want to apply</b> this setting in the recommender."
      notGoodRange: is out of interval range. Enter a number within the selected interval.
      personalizableInfo: There may be exceptional cases where turning off personalization makes sense, such as displaying recently viewed items. However, we <b>do not</b> recommend <b>turning off personalization</b> by default.
      personalizableTitle: Can personalization be used in the recommender where possible?
      purchaseStart: Is the correct item sale start date attribute specified?
      purchaseStartHeader: Make sure the correct date attribute is specified. If no attribute has been added, add it now.
      purchaseStartInfo: In order for the novelty recommender by date to work correctly, we need to verify that the correct on-sale <b>dates attribute of the items</b> is assigned. On the basis of this attribute, novel items are sorted in the recommender.
      sameCategoryInfo1: Setting <b>helps to diversify recommendations</b>. To achieve greater diversity, you can limit the maximum number of recommendations for one category (product type). In case of recommendation in the category detail, we diversify by its subcategories.
      sameCategoryInfo2: This <b>strategy is especially beneficial for cross-sell</b> recommenders (basket, complements, category reco) offering accessories or related products. On the other hand, <b>for up-sell</b> (item detail alternatives), <b>it's better not to limit the number</b> of recommendations fron one category.
      sameCategoryText: Enter how many recommendations (maximum 3) is recommender allowed to select from the same category if there is enough high quality results from other categories.
      sameCategoryTitle: How many recommendations can be from the same category?
      selectType: Select a type...
      selectValue: Select a value...
      sidebar:
        addAtribute: Add attribute
        addComplementAttribute: Add additional attribute
        addDiscountSmallTitle: Assign a label attribute for catalog discounts
        addDiscountSubtitle: 'Help us assign the correct attribute and value for the “Discount” label in the catalog. The name of this attribute depends on the solution and platforms may vary. Common attribute names are e.g.: label, tag, print..'
        addDiscountTitle: Attribute label for catalog discounts
        addMoreTitleSidebar: Attributes for similarity evaluation
        addNewsSmallTitle: Assign a label attribute for catalog novelties
        addNewsSubtitle: 'Help us assign the correct attribute and value for the “New” label in the catalog. The name of this attribute depends on the solution and platforms may vary. Common attribute names are e.g.: label, tag, print..'
        addNewsTitle: Attribute label for catalog novelties
        addNextValue: Add next value
        additionalAttributes: Additional attributes
        applyRule: 'Apply the rule if the value of the item attribute on the input:'
        attributeValue: Attribute value
        availabilityTooltip: We prefer to use the AVAILABILITY RANK attribute, which displays various availability levels of the item in the range of 1-15. This attribute allows to determine which items could be recommended <b>(values 1-7 indicating available items)</b> and which could not (values 8-15 indicating out of stock and unavailable items). An alternative is to use the AVAILABILITY attribute, representing available (value 1) or unavailable items (value 0).
        availabilityTooltipTitle: Availability and Availability Rank
        booleanFilterText: Filter the result in the recommender by entering an boolean attribute value.
        booleanValuesInfo: Enter a boolean value.
        brandDescription: The attribute includes the names of the brands in your catalog. If the tag is included in the product names, <b>turn off the attribute.</b>
        categoryDescription: The attribute includes the names of the categories in your catalog.
        chooseAttribute: Choose attribute...
        concrete_date: Enter a specific date
        concrete_numeric: Enter a specific number
        createFilterText: Create a filter by adding attributes with values that items in the recommender should meet in order to be prioritized in the results over others.
        createSpecialFilter: Create a custom filter by adding attributes with specific values. With this filter, you define mandatory requirements and criteria for the recommender. Only items that <b>meet all the requirements created below will always be displayed.</b>
        dateFilterText: Filter the result in the recommender by entering a specific date or time range in days.
        dateValuesInfo: Enter and define a time range in days for the attribute.
        existenceCheck: When checking the attribute's existence, it is not necessary to add anything else.
        filterForBoost: Filter for prioritizing items in the results
        filtersPreview: 'Preview of the created filter:'
        howAvailabilityWork: How does the availability criteria work?
        keywords: keywords
        keywordsDescription: Keywords description
        mainAttributes: Basic attributes
        mainAttributesText: Basic, predefined attributes of the recommender against which the similarity of recommended items is evaluated.
        mandatoryAttributes: Mandatory attributes of items in the recommender
        maxAttributes: We recommend to add <b>up to 6 additional attributes.</b>
        numericFilterText: Filter the result in the recommender by entering values in the attribute's units (euros, kilograms, liters,...).
        numericValuesInfo: Enter values in the attribute's units (euros, kilograms, liters,...).
        onlyTextAttributes: Adding attributes is limited to text values only (they must not contain a number, e.g., "elastane 70%"), which must not be too long (e.g., not contain more than 3 words).
        percentile: "- percentile"
        preferredAttributesTitle: Preferred attributes of items in the recommender
        removeNextValue: Remove added value
        returnedAttributesAdditionalText: Expand the list of basic attributes the recommender should return in responding to the call if needed. <b>The number of attributes affects the response speed;</b> the smaller the number, the faster the recommender.
        returnedAttributesText: A list of the most important attributes returned by the recommender to display results as quickly as possible.
        returnedTitleSidebar: Attributes in the recommender response
        setPercentile: Enter percentile
        specialFilter: Special filter for displaying recommendations
        textFilterText: Filter the result in the recommender by entering an attribute value or a dynamic context value.
        textValuesInfo: Enter attribute values or dynamic context values. These values cannot be combined.
        titleDescription: The attribute includes the names of the products in your catalog.
        values:
          all_of: All of (AND)
          atLeastOneOf: At least one of
          divide: "/ Divide"
          exists: Exists
          in: Belongs to (OR)
          is: Is
          isEqual: "= IS EQUAL"
          isGreater: "> IS GREATER THAN"
          isGreaterOrEqual: "≥ IS GREATER THAN OR EQUAL"
          isLower: "< IS LESS THAN"
          isLowerOrEqual: "≤ IS LESS THAN OR EQUAL"
          isNot: Is not
          isNotEqual: "≠ IS NOT EQUAL"
          less_than: Less than
          minus: "- Deduct"
          more_than: More than
          multiply: "* Multiply"
          noneOf: None of
          not_exists: Not exists
          not_in: Not belong to (NOT)
          plus: "+ Add"
        wantToApply: I want this filter to be applied only when a specific condition is met at the recommender's input.
        writeAttributeName: Write attribute name
      startTypeHere: Start typing here for values search
      subTitle: Your recommender <b>is pre-configured and ready to start.</b> Advanced users can customize it below to meet specific needs.<br> Feel free to proceed to the next step. The recommender <b>will work well with the default settings.</b>
      title: Settings of {recommenderName}
      toInitialValues: Original values
      trendComplementInfo1: Recommender always try to recommend high quality relevant results. Sometimes, there however simply don't exist enough relevant recommendations - e.g. due to some restrictive filter. In this case recommender can either return lower number of results as requested or <b>supplement the missing results with trends</b> (best-sellers). This can <b>prevent to negatively affect the appearance</b> and layout of the recommender on the page.
      trendComplementInfo2: Turn-off the setting is suitable for user's last seen products, recommender in product listing page (category detail), for the best-sellers from specific brand or when recommending novelties/discounted products only.
      trendComplementTitle: Can the recommender use trends as backup?
      typeInfo: Select proper data type from your product catalog that the recommender recommends on its output. In the <b>most cases</b>, "product" or "item" is used, some recommenders returns "category" of "article". As these can be named differently in your data, make sure we have selected the correct type.
    setup:
      customCode:
        title: Theme modification and custom code
    sidebar:
      recoTypes:
        subTitle: You simply choose the recommender type according to the way it recommends items. Then you adjust its settings according to your requirements and determine where it should be displayed.
        title: How to choose recommender type?
    states:
      Active: The recommender is fully initialized and operational.
      Deactivated: The recommender has been deactivated by the user. It returns an error when requested.
      Failed: An unrecoverable error occurred while initializing the recommender. Assistance is required.
      FailedOperational: An error occurred while training the recommender, however, the recommender can still serve requests using a previously initialized version.
      Initializing: The recommender is being initialized. It returns an error when requested.
      InitializingOperational: The recommender is being trained, however, the recommender can still serve requests using a previously initialized version.
      Published: The recommender is fully initialized and operational.
      Waitingforlaunch: The recommender has been fully initialized and is ready to be deployed on a website.
    staticValues: Attributes values
    stay: Stay
    toasts:
      deleted: Recommender settings have been deleted
      duplicated: Recommender was successfully duplicated.
      popupBlocked: Otvorenie náhľadu zlyhalo, povoľte prosím pop-up okná
      saved: Recommender settings have been saved
      savedAndPublished: Recommender settings have been saved and published
    type: Type
    useDiacritics: Use diacritics when typing.
    waitWizard: Do not wait, launch the wizard and set up the selectors.
  redesignInfoHtml: "<div>\n  <br>\n  <h1>Application Interface Redesign</h1>\n  <br>\n  <p>\n    In order to continue to develop our product effectively and, at the same time, improve your experience using the application, we have decided to innovate and redesign its entire interface.\n  </p>\n  <p>\n    During this redesign, the application <strong>will be updated gradually</strong> in parts, as we will switch individual pages <strong>from the original interface to the new one</strong>. Along with the redesign, we will also launch several new functionalities, which we will inform you about in separate newsletters.\n  </p>\n  <br>\n  <h2>\n    More about interface changes\n  </h2>\n  <br>\n  <p>\n    In addition to new features, the new interface will significantly <strong>improve UX</strong> (user experience). In the first phase of the redesign, the most fundamental change <strong>is the simplification of navigation and the navigation structure of the application</strong>.\n  </p>\n  {img01}\n  <hr>\n  <p>\n    <strong>Switching between projects</strong> has been preserved and works the same way (after clicking on the logo or the \"dropdown\" menu, a list of projects and pages will appear).\n  </p>\n  {img02}\n  <hr>\n  <p>\n    We have implemented a new structure in the application that simplifies the management of Luigi's Box products and allows them to continue to grow. <strong>Each product has its own list</strong> where you can find all its related settings and customization options.\n  </p>\n  {img03}\n  <br>\n  <br>\n  <h3>\n    Description of the main navigation level\n  </h3>\n  <p>\n    The new structure is divided at the main level into a product section where you manage your individual Luigi's Box products and the standard part of the app for general settings and support.\n  </p>\n  <ul class=\"redesign-navigation-ul\">\n    <li>\n      {iconSearch}\n      <div>\n        <h4>\n          Search\n        </h4>\n        <p>\n          Search analytics and all search and autocomplete options and settings.\n        </p>\n      </div>\n    </li>\n    <li>\n      {iconRecommender}\n      <div>\n        <h4>\n          Recommender\n        </h4>\n        <p>\n          Recommenders analytics and all related options and settings.\n        </p>\n      </div>\n    </li>\n    <li>\n      {iconProductListing}\n      <div>\n        <h4>\n          Product Listing\n        </h4>\n        <p>\n          Setting and editing the filtering option on product listings for selected categories.\n        </p>\n      </div>\n    </li>\n    <li>\n      {iconBannerCampaign}\n      <div>\n        <h4>\n          Banner campaigns\n        </h4>\n        <p>\n          Setting the displaying of banners in the autocomplete and the search results.\n        </p>\n      </div>\n    </li>\n    <li>\n      {iconCatalog}\n      <div>\n        <h4>\n          Catalog\n        </h4>\n        <p>\n          Original catalog management with browsing of records stored in Luigi's Box and options to set synonyms, keywords, and global item prioritization.\n        </p>\n      </div>\n    </li>\n    <li>\n      {iconSettings}\n      <div>\n        <h4>\n          General settings\n        </h4>\n        <p>\n          User account profile settings, general and advanced application settings.\n        </p>\n      </div>\n    </li>\n    <li>\n      {iconHelp}\n      <div>\n        <h4>\n          Support\n        </h4>\n        <p>\n          Available documentation and contacts.\n        </p>\n      </div>\n    </li>\n  </ul>\n  <hr>\n  <h3>\n    Product lists\n  </h3>\n  <p>\n    Product lists are divided and sorted into three groups:\n\n    <br>1. <strong>Analysis</strong> - groups analytics, statistics, and overviews of the selected product\n    <br>2. <strong>Improvements</strong> - groups the basic functions of the product and the possibilities of its modifications to improve its functioning\n    <br>3. <strong>Settings</strong> - groups activation options and basic settings for starting or correct functioning of the selected product\n  </p>\n  {img04}\n  <hr>\n  <p>\n    During the redesign, in addition to new functions, navigation, and navigation structure, <strong>the interface of all original functions will be updated and simplified</strong>, and the options for settings available to users will also be expanded.\n  </p>\n  <p>\n    Thanks to this, it will be possible to make some app changes without the need to communicate with our support.\n  </p>\n  {img05}\n  <br>\n  <p>\n    We are doing everything to make this change happen as quickly as possible and for you to enjoy your new interface to the fullest. That's why new functions and innovations will be coming from the beginning already in the new interface.\n  </p>\n  <p class=\"text-center\">\n    Please be patient! 🙏\n    <br>\n    Thank you.\n  </p>\n</div>\n"
  refinementTesting:
    description: On this page, you can try negative feedback in search results. Select correct type (probably product/item) and enter search query.
    title: Search Refinement testing
  rewriteQuery:
    additionalSettings:
      admitCheckText: In the search field and search results, we display information about rewriting the original query as a simple text output of the <b>original query → the destination query</b>.
      admitCheckTitle: "<b>I want to admit rewriting the query</b> in the search on the website."
      customMessageCheckText: We rewrote your entered query to another with better search results for you.
      customMessageCheckTitle: Display custom text below the heading with the rewritten query in the results.
      showExample: Show example
      subTitle: The settings are applied only when entering the original query ”{query}” into the search.
      title: Additional settings for the rewritten query
    allAttributes: All product attributes
    catalogSearch:
      foundTitle: Found results for entered query {query} (first {actualSize} from {actualHitsCount} results)
    confirmPopup:
      close: Close
      confirmDelete: Are you sure you want to delete the "Rule for rewriting the query {query}"? You can't take back this change.
      confirmDiscard: Are you sure you want to <b>discard all changes</b>?
      discard: Discard
    detail:
      about: Rewrite query
      aboutDiscardText: Keep the destination query field empty, and don't forget to set preferred filters for all items on the results page.
      aboutDiscardTitle: Do you want to discard rewritten query?
      aboutFiltersText: The items in the search results are displayed based on the selected filters. The recommended parameters include price range, categories, availability, and parameters for segment-dependent items such as color, size, etc.
      aboutFiltersTitle: How do filters work?
      aboutText: Enter the query whose results you want to edit. You can rewrite the current query to another and show its search results. You can add parameters to filtering items on the search result page, discard the current query, and display all items from the catalog with filters.
      addNextParameter: Add next parameter
      continue: Continue to query rewrite
      customizing: Rule for rewriting the query
      customizingText: You can rewrite the current query to another and show its search results. You can add parameters to filtering items on the search result page or discard the current query and display all items from the catalog with filters.
      deleteCustomization: Delete customization
      discard: Discard changes
      editQueryTitle: Keep, rewrite or delete your destination query
      enterQuery: Enter the query to customization
      noResultInfo: We couldn't find any results for the written destination query, so there is nothing to set up. However, you can rewrite to another query, reset the destination query to the original or delete it.
      notGoodFilter: This filter is not applicable to the current search results of destination query.
      remove: Remove
      resetQuery: Reset to default query
      saveCustomization: Save customization
      searchParameter: Search for parameter
      selectParameter: Select parameter...
      selectValue: Select value...
      selectValues: Select values...
      setFiltersTitle: Set your preferred filters for items on the results page
    discardRewrittenQuery: If you want to discard rewritten query, keep this field empty
    enterQuery: Enter search query
    list:
      customizeNew: Customize new query
      customized: Customized queries ({number})
      filters: Filters
      findRewriteQuery: Search for customization
      howItWorks: How it works?
      rewrittenQuery: Rewritten query
      subTitle: You can rewrite a custom query to another and show its search results. <br>You can add parameters to filtering items on the search result page or discard the current query and display all items from the catalog with filters.
      title: Rewrite query
    notifications:
      detailLoadError: Detail load error
      duplicitQueryRewriteQuery: Query rewrite using the same query (diacritics and case ignored) already exists
      emptyQueryRewriteFilters: Query rewrite must have either rewritten query filled in or filters filled in
      emptyQueryRewriteMessage: If you choose to display a custom text, it can not be empty
      emptyQueryRewriteQuery: Original query must be filled in
      error: Catalog types could not be loaded
      facetsLoadError: Facets could not be loaded
      notFoundError: Could not find requested query rewrite
    showJson: Show in JSON format
  screenshots:
    createScreenshots: Create screenshots
    queryLabel: Search query
    queryPlaceholder: Enter any phrase e.g. kalimba
    saveInfo: Be sure to save any changes before clicking this option.
    urlLabel: URL of the website
    urlPlaceholder: Enter the URL of the website
  searchResultCustomizations:
    buttons:
      add: Add
      addCustomResult: Add custom result
      addTag: Add tag
      approve: 'Yes'
      close: Close
      decline: 'No'
      delete: Delete
      discard: Discard
      editCustomization: Edit customization
      hideResult: Hide result
      moreOptions: More options
      removeCustomization: Remove customization
      save: Save
    create:
      cardDescription: In the customization, you can place any items in the results (for the autocomplete and search) in specific positions, completely prevent them from being displayed, or create custom criteria for their display using filters.
      cardTitle: Search results customizations
      chooseType: Choose type of result you are interested in (typically item or product)
      chooseTypePlaceholder: Choose type...
      customizeResults: Customize results
      enterQuery: Enter a query and choose the type to customize
      queryNamePlaceholder: Enter search query
      searchQuery: 'Search query which you want to customize results for:'
      title: Enter a query and choose the type to customize
    detail:
      addResult: Add custom result
      advancedModeDesc: "<b>You are in advanced mode</b> where you are customizing results for autocomplete and search separately. If you want to customize the query results for Autocomplete and Search together you need to switch to single mode."
      autocomplete: Autocomplete
      choosePlaceholder: Choose a specific item...
      collapse: Collapse future pinned results
      customResultsFor: Custom results for
      customizedResult: Customized search results
      customizedResults: Customized results
      customizingQuery: Customizing results for the query
      doesNotExistTooltip: "<b>The items does not exist</b></br>The item is not found in the product catalog. Pinning it to this position will be ignored. You can safely remove this pin."
      evenUnavailable: Even unavailable
      expand: 1+ additional rules planned for the future
      hiddenResults: Hidden results
      hideResult: Hide result
      noHiddenItems: No items have been hidden yet.
      overlapInfo: Rules are overlaping. Do you want to automatically move old rules down by 1 position?
      overlapTitle: Overlaping Rules
      search: Search
      searchQuery: Search Query
      sidebar:
        activePermanently: Permanently
        addToPosition: 'Place the result at position:'
        availableDesc: Unavailable results (e.g., sold-out products) are not usually pinned. <br>However, you can choose to pin it even if it is not available.
        availableTitle: Pin unavailable
        chooseSpecificItem: Choose the specific item...
        description: "<b>Choose an item from the catalog</b> that you want to display in the search results at a specific position."
        edit: Edit settings
        endDate: 'End date:'
        header: Add custom result
        hiddenSave: Hide specific item
        hide: Hide result
        itemPositionDesc: Enter at which position in the order the result should be displayed in the product listing.
        itemPositionTitle: Position of the result in the product listing
        leaveBlank: Leave blank
        leaveTimeEmpty: Leave blank to pin the item permanently.
        noInterval: Permanently
        pin: Pin to position
        pinTimeDesc: You can schedule or time-limit pinning the result to a specific position.
        pinTimeTitle: Time frame selection
        positionAndTime: Setting the position and time frame
        resultSave: Pin the slected result
        secondaryTitle: Setting the position and time frame
        specificItem: Specific item
        startDate: 'Start date:'
        title: 'Choose a type and set a custom result for a specific position:'
        unpin: Unpin result
      simpleModeDesc: "<b>You are in simple mode.</b> If you want to customize results for search and autocomplete separately you need to switch to advanced mode."
      startTypeHere: Start typing here for values search
      switchFromadvanced: Switch to simple mode
      switchFromsimple: Switch to advanced mode
      switchModal:
        advancedDesc: "<b>Do you really want to change the customizations mode?</b><br>If you switch to simple mode, you'll be managing settings for autocomplete and search together. Now you need to decide which settings should be transferred into the simple mode."
        advancedTitle: Switch to simple mode
        alertText: If you decide to switch again to advanced mode from simple mode later on, settings from that future simple mode will be used instead of what you have setup now in current advanced mode.
        simpleDesc: "<b>Do you really want to change the customizations mode?</b><br>You are about to switch to advanced mode of query customization, where you can adjust settings individually for autocomplete and search."
        simpleTitle: Switch to advanced mode
        whichSettings: Which settings should be used in the simple mode?
      tagsDesc: 'You can label your customization (e.g. #blackfriday, wintersale, shirts...) to find it more easily.'
      tagsSearchPlaceholder: Enter a tag name and press Enter
      tagsTitle: Label with your own tag
      type: Type
      unavailableTooltip: "<b>Currently unavailable item</b></br>The item is currently unavailable (e.g., out of stock, available for order...), but it will continue to be displayed in this position."
    errors:
      active_from_must_be_a_date: Active from must be a date
      active_to_must_be_a_date: Active to must be a date
      cannot_block_and_pin_item_for_one_query_at_the_same_time: Can't block and pin item for one query at the same time
      chosen_position_for_this_query_is_already_occupied_in_selected_active_range: Chosen position for this query is already occupied in selected active range
      default: Something went wrong
      export: Error occurred while exporting search result customizations
      import: Error occurred while importing search result customizations
      invalid_category_attribute: Error creating query - invalid category attribute
      item_url_cannot_be_empty: Item URL cannot be empty
      not_found: 404 - query not found
      position_must_be_number_greater_than_0: Position must be number greater than 0
      query_cannot_be_empty: Query cannot be empty
      results_position_cannot_be_empty: Results position cannot be empty
      this_item_is_already_blocked_for_this_query_in_selected_active_range: This item is already blocked for this query in selected active range
      this_item_is_already_set_for_this_query_in_selected_active_range: This item is already set for this query in selected active range
      type_cannot_be_empty: Type cannot be empty
    filters:
      cancelAll: Clear all filters
      close: Close
      modes: Modes
      saveButton: Filter the list
      searchByName: Search by name
      tags: Tags
      title: Filter the list
      types: Types
    importSidebar:
      buttons:
        close: Close
        continue: Continue
        export: Export customizations
        import: Import customizations
      example: Download Sample *.csv File
      export:
        info: Export all your created customizations to a *.csv file, which you can then easily open in any spreadsheet editor and modify or expand as needed. Or you can {example} and create a completely new list of your own customizations.
        infoEmpty: TAt this moment, there is nothing to export as no customizations have been added. However, you can {example} and create a completely new list of customizations for import yourself.
      import:
        dataError: Incorrect file type or format.
        info1: Save time by bulk importing your customizations. You can easily expand the list of existing ones, or discard all original customizations and upload a completely new list.
        info2: You only need to {example} (or modify the export of existing customizations), which you will open in your favorite spreadsheet editor and modify as needed.
        items: "{n} records"
        modes:
          add:
            info: Imported customizations will be added to the original list. If the import contains customizations for a word that already exists in the system, the new record will completely overwrite the original, older record.
            title: Add new customizations to existing ones
          replace:
            info: All previously created customizations will be removed. Only new imported customizations from the list will remain.
            title: Remove original customizations and keep only new ones
        step1: Choose your import method
        step2: Add your prepared list
        supportInfo_html: We only support <strong>CSV file format</strong>, which must not contain a legend (header row).
        uploadInfo_html: Drag and drop or <span>add a list</span> in <strong>CSV format</strong> here
      success: Done, your customizations list has been successfully added.
      title: Import/export customizations
    intro:
      buttonImport: Import customizations
      newQuery: Customize new query
      subTitle: Tailoring autocomplete and search results of a particular query according to your preferences.
      title: Search results customizations
    list:
      added: Added
      buttonImportExport: Import/Export customizations
      customizations: Customizations
      customize_button: Customize new query
      customized_queries: Customized queries and validity
      filterList: Filter the list
      noResult: We did not find any results matching your criteria.
      onThePage: On the page
      search: Search in customizations
      tags: Tags
      title: List of customizations
    modal:
      adjustSettings: Adjust settings
      deleteDesc: Do you really want to remove this customization? This change cannot be undone.
      deleteTitle: Delete customization
      discardDesc: Do you really want to discard these changes? This change cannot be undone.
      discardTitle: Discard changes
      unHide: Unhide
    queriesItem:
      blockedResults: "{n} blocked result | {n} blocked results"
      delete: Delete
      edit: Edit
      inAllResuls: in Autocomplete and Search
      inAutocompleteResuls: in Autocomplete
      inSearchResuls: in Search
      pinnedResults: "{n} pinned result | {n} pinned results"
      type: type
    success:
      created: Query created successfully
      deleted: Query deleted successfully
      saved: Query saved successfully
  searchSetup:
    acSettingsInfo: To fully utilize the potential of our product, it is necessary to <a href="{link}" target="_blank">set up and run the autocomplete</a>.
    addFacetModal:
      close: Close
      label: Selected facet
      sizeLabel: Number of visible items
      submit: Add
      title: Add a facet
    addSortModal:
      close: Close
      label: Select a sort
      labelTranslationAsc: Text for ascending order
      labelTranslationDesc: Text for descending order
      placeholderTranslationAsc: e.g. Oldest first
      placeholderTranslationDesc: e.g. Newest first
      submit: Add
      title: Add a sort
    advanced:
      cards:
        abTest: AB testing running and set up
        facets: Dynamic facets set up
        general: General settings
        screenshots: Screenshots of the integration page
      facets:
        facetSize: Max. number of displayed dynamic facets
        facetSizeTooltip: The max number of shown dynamic facets in filters.
        info: Automatically generated facets in filters based on displayed products in results.
        rightInfo: For launching dynamic facets or adding to the blacklist, {link}.
        rightInfoLink: continue here
        visibleFacetSize: The number of visible items in the facet
        visibleFacetSizeTooltip: The total number of items (parameters) in separate facets. Zero means all items will be visible.
      general:
        abTest: Run in AB testing mode
        activeAnywhere: Activate search on each subpage with the entered query parameter
        disableDeferring: Product listing is used, or category feed is not available, and "Fake nested" is used
        facetsQueryParam: URL facet parameter name (defaults to lb.f[])
        facetsQueryParamInfo: Use version with _ if webserver does not support dot in url parameters
        followSuggestedUrl: Recognize the types of results in the specified query (If the query matches, for example, a category, this category will be displayed instead of the results).
        gaTrackingCode: Google Analytics tracking code
        instantSearch: Start instant search (search activation already while typing the search phrase)
        locale: Locale
        removeFields: Removing fields in facets (Fill only if you want to override the default functionality of the search.js search form)
        scriptSrc: Script src for Search.js
        searchIntegration: Search integration (defaults to in-place)
        searchIntegrationInfo: Standalone will redirect to original search page first
        showLogo: Display logo
        showLogo2: Luigi's Box
      hideShowAll: Hide / show all
      info: These settings are intended for advanced users, programmers, and Luigi's Box integration team. If it is not necessary, do not change them.
      placeholders:
        locale: en, sk, cz...
        removeFields: Separate the values with a comma...
        scriptSrc: https://cdn.luigisbox.com/search.js
      title: Advanced search settings
    buttons:
      discard: Discard all changes
      firstSetupContinue:
        design: Continue to color settings
        structure: Continue to facets and results settings
      firstSetupDiscard: Discard all changes
      preview: Show live preview
      publish: Publish
      saveAndLaunch: Save and run
      saveDraft: Save as draft
    colors:
      colorNames:
        header_text_color: The color of the headings in the search header
        hover_buttons_color: The color of the button on mouse hover
        hover_titles_color: Color of clickable headings on mouse hover
        primary_text_color: The basic color of clickable text (clickable headings)
        search_primary_color: Main conversion color (buttons, checkboxes,...)
        secondary_text_color: The color of regular text
      info: Customize the search page color to your site design with a simple color change. Start by entering the main color, and we will design the entire color scheme based on it; then, it is up to you whether you keep this setting or decide to continue editing further.
      setColorScheme: Generate color scheme
      title: Colors of search
    components:
      colors: Color settings
      design: Appearance and display
      structure: Facets and results settings
    design:
      addCustomTemplate: Add custom template
      cards:
        code: Custom code
        templates: Base theme modification
        tileEditor: Use the editor to customize the display of the product tile according to your needs.
      customTemplateExists: Template with this name already exists
      customTemplateName: Enter name of new template
      customTileInUse: Product tile is customized via editor, so this template will not be in use. To disable custom tile, delete <code>template-wswg-searchjs</code> custom template.
      info: Select your style of search results to replace your original solution. You can change this setting anytime and adjust the results' colors, display, and layout.
      openTileEditor: Open editor
      resetTemplate: Original template
      templatesHeading: Base theme templates
      templatesInfo: The chosen theme represents our basic search results display on your site; if you need to customize the theme, you can do so below, but we recommend visiting the {link} first.
      templatesInfoLink: theme creation documentation
      title: Appearance and display
    dictionaryImport:
      button: import full dictionary
      info: If you need to customize translations, you can {button} to have reference for all available texts.
    draft:
      discard: Discard changes
      info: Your settings changes are waiting to be published. How do you wish to proceed?
      publish: Publish changes
    facetNames:
      _empty: Select a facet...
    popupDiscard:
      close: Close
      info: If you continue, you will lose all the unsaved changes you made in the settings.
      submit: Discard changes
      title: Discard all changes
    popupLaunch:
      close: Close
      info: If you continue, you will <strong>activate Luigi's Box Search service</strong> and replace the current solution on your site.
      submit: Run the service
      title: Service launch
    popupLeave:
      close: Close
      info: If you continue, you will <strong>lose all the unsaved changes</strong> you made in the settings.
      submit: Continue without saving
      title: You have unsaved changes
    preview:
      design: appearance and display
      info: Enter any phrase to display search results with current settings. It is unnecessary to save the settings in advance to display this preview.
      info2: Options for {tile} editing and theme changes can be found in the {design} section. The display of results in this preview is limited.
      mode: Results display options
      modes:
        default: basic display
        fixed: display with modified phrase
        noResults: display without results
      placeholder: Enter any phrase
      tile: product tile
      title: Theme preview
    quickSearchModal:
      close: Close
      label: Select additional result
      sizeLabel: Number of items
      submit: Add
      title: Add additional result
    setSelectors:
      heading: Autocomplete or results not showing (correctly) on your page?
      info: If there is a problem displaying autocomplete or search results on your page, please check your {link}.
      link: selector settings
    sorts:
      _empty: Select a sort...
    status:
      active: Active
      disabled: Disabled
      waitingForLaunch: Awaiting launch
    structure:
      facets:
        addFacet: "+ Add a facet"
        defaultFacets:
          title: Default Facets
        facets: Basic facets displayed in search result filters.
        facetsTooltip: Basic facets, which are always displayed in search result filters, regardless of the found items.
        title: Facets and parameters
      howToSetup: How to set up Search
      info: Several additional settings need to be made for the search to work correctly. For example, define the correct form of links for analytics, define basic facets in the filter, or assign missing attributes to be displayed for found products.
      quickSearches:
        addType: "+ Add a type"
        info: Standard categories, tags, articles, and other types of items you want to see in results in addition to products.
        title: Additional results
      settings:
        addSort: "+ Add option"
        defaultFilterType: Default type for filters
        defaultFilterTypeTooltip: Categories, brands, articles and other types of the results.
        generalTitle: General settings for search and found products
        queryFullUrl: Enter the search results URL for:{} {query}
        queryFullUrlPlaceholder: https://www.example.com/search?q=kalimba
        queryFullUrlTooltip: Enter and confirm the specific phrase "kalimba" in the search. Copy the URL address of your search results and paste it into the field below. It's perfectly fine if your search didn't find any results.
        queryUrlInfo: For the correct setup, use the specific phrase <strong>{query}</strong> in the search and enter the address of the results in the field.
        size: The number of products displayed per page in the results
        sizeTooltip: Max amount of products displayed on the results page.
        sorts: Product sorting options on the results page
        sortsTooltip: Sorting products in search results according to specific attributes, with default sorting by price and name.
        title: General settings
      title: Facets and results settings
      topItems:
        addType: "+ Add an item"
        info: The number of products or categories, brands, and other additional results that you will see solely on the "No results" page.
        title: Content of the search page without results
    title: Search set up
    toasts:
      activated: Search was activated
      disabled: Search was disabled
      popupBlocked: Opening of preview failed, please allow pop-up windows in your browser
      saved: Search settings were saved
      savedAndPublished: Search settings were saved and published
    topInfo:
      active: "<strong>Luigi's Box Search service is active</strong>. You can turn it off (deactivate) in an emergency anytime."
      buttonActivate: Activate search
      buttonDisable: Deactivate search
      disabled: "<strong>Luigi's Box Search service is inactive</strong>. However, you can restart the service at any time. When activated, the service starts with its last saved settings and replaces your original solution again."
      waitingForLaunch: You replace your original search results with Luigi's Box by setting up and running the service. You can change these settings anytime or simply turn off the service if necessary.
    topItemsModal:
      close: Close
      label: Select additional result
      sizeLabel: Number of results
      submit: Add
      title: Add additional result
    types:
      _empty: Select a type...
    welcome:
      button: Get started with your search set up
      info: Starting the service allows you to set up and add Luigi's Box Search to your website.
      title: Search set up
  segmentSelect:
    ALL_DATA: All data
    addFilter: Add filter
    cancel: Cancel
    createNewSegment: Create new segment
    delete: Delete
    edit: Edit
    internalSegment: Internal segment
    key: Key
    newOption: Add custom option by writing it and pressing Enter
    noRulesYet: You still need to add parameters for this segment.
    saveSegment: Save segment
    search: Start typing here to find segment
    segmentName: Segment name
    showABSegments: Show A/B tests segments
    trigger: 'Segments: {segments}'
    value: Value
  selectorSetup:
    addToCartSelector:
      description: Mark all buttons "Add to Cart" or "Buy" that are located on your website. This includes buttons on product detail page, buttons in product listing pages (category and brand pages) and buttons in recommenders.<br>In each "group", select a button only once.
      label: Selector for Add to Cart/Buy button
      placeholder: Selector for Add to Cart/Buy button
      title: 4. Selector for Add to Cart/Buy button
    description: Provide us with the selectors of your original search (search field, search results, and autocomplete). You can enter selectors manually. This option is suitable for users with a basic understanding of HTML and CSS, as well as for developers. {strong}, you can easily {widget} for us with just a few clicks on the page.
    descriptionStrong: If you're a non-dev person
    descriptionWidget: launch our wizard and setup the selectors
    devPerson:
      heading: DEV person?
      link: integration settings page
      text: If you are a person with advanced programming skills, you can set the CSS selectors manually on the {link}.
    hideSelector:
      description: Add the selector for the original autocomplete widget. You are marking the entire element with items that should be hidden (not individual elements).
      label: Selector of original autocomplete in desktop version
      placeholder: Does page not use autocomplete? Leave the field empty.
      playVideo: Selector for the original autocomplete
      title: 2. Selectors of original autocomplete widget
      videoLink: https://youtu.be/dQw4w9WgXcQ
    howToSetSelectors: How to set selectors
    inputSelector:
      description: Add the selector for the original search field. Make sure that the selected selector targets the ”input” element.
      label: Selector for the search field in desktop version
      placeholder: Selector for the search field in desktop version
      playVideo: Selector for the search field
      title: 1. Selectors for the search field
      videoLink: https://youtu.be/JAh-LLEqtQ8
    mobileHideSelector:
      label: Selector of original autocomplete in mobile version
      placeholder: Is selector the same as above? Leave the field empty.
    mobileInputSelector:
      label: Selector for the search field in mobile version
      placeholder: Is selector the same as above? Leave the field empty.
    saveButton: Save the configured selectors
    settingsFromIntegrator_html: We applied settings from interactive integration settings widget. <strong>Please click  ”Save the configured selectors”</strong>  button in order to confirm them.
    simple:
      heading: One more little thing...
      text: We're sorry, but we're missing a few more details so you can continue with the settings.
    title: Selectors
    widget:
      button: Launch the wizard
      description: "Launch the wizard 🪄 and set up the selectors."
      followGuide: Just follow the guide
      howitWorks: how the wizard works.
      info_html: Setting selectors in a few clicks directly on the page.
      title: Non-dev person?
    wrapperSelector:
      description: Use the search and mark on the results page the element (between the header and footer), in which all search results, including controls such as filtering, paging, or sorting, are displayed (wrapped).
      label: Selector for search results
      placeholder: Selector for search results
      playVideo: Selector for search results
      title: 3. Selector for search results
      videoLink: https://youtu.be/bDqzr6FpX2k
  settingThingsUp:
    description: We’re processing your request. This may take a few moments. <b>Please keep this window open</b> until the task is complete.
    title: Setting Things Up
  shopware:
    accountExists: Already have an account at Luigi's Box?
    address: Street Address
    billingEmail: Contact E-mail address
    billingInfo: Contact information
    city: City
    companyId: Company ID
    companyName: Company name
    country: Country
    email: User E-mail
    everythingFine: It looks like everything works fine
    linkAccount: Link account with this Shopware instance
    logInLuigis: Log in to Luigi's Box
    newAccount: Create a new account
    password: Password
    postalCode: ZIP / Postal code
    saveAndContinue: Save information and continue
    selectCountry: Select country
    shopNotFound: We could not find your Shopware shop
    shopNotFoundDescription: This probably means that an error occured during installation process. Our developers are already looking into it
    subTitle: To utilize our services, providing a few additional details is necessary.
    taxId: TAX ID
    vatId: VAT ID
    wantChanges: If you want to make some improvements or changes in settings and setup, continue to the Luigi's Box app.
  signin:
    contactSupport: If you need help, <a href="https://www.luigisbox.com/contact/" target="_blank">contact our support.</a>
    email: E-mail address
    expiredWarningText: If you have a <b>login problem</b> and are sure you used the <b>right password</b>, your registration wasn't probably finished, and you need to <a href="sign_up">create your account again</a>.
    forgotPassword: Forgot your password? <a href="password/new">Click here</a> to set up a new one.
    goToLogin: Go to login
    loginExpiredSubtitle: The confirmation link has expired. Please try to log in to your account.
    loginExpiredText: "⚠️ Expired link"
    loginSubtitle: Log in to your account. If you don't have an account with us, you can <a href="sign_up">create new account here.</a>
    loginText: Log in
    notifications:
      accountLocked: Account is locked, check your email for further instructions
      invalidCredentials: Invalid name or password
    platformsText: 'You can also log in using the following services, if you use Luigi''s Box there:'
    shopifySubtitle: Login to your Shopify e-shop and manage Luigi's Box with your plugin.
    shopifyTitle: Are you using the {shopifyIcon} Shopify platform?
    shoptetSubtitle: Login to your Shoptet e-shop and manage Luigi's Box with your plugin.
    shoptetTitle: Are you using the {shoptetIcon} Shoptet platform?
    shopwareSubtitle: Login to your Shopware e-shop and manage Luigi's Box with your plugin.
    shopwareTitle: Are you using the {shopwareIcon} Shopware platform?
    wrongEmail: Entered wrong e-mail address?
    wrongEmailBack: Create a new registration.
  site_settings_event:
    event_type:
      api_fingerprinting_full_enabled: Full API fingerprinting enabled.
      api_fingerprinting_nested_enabled: Nested API fingerprinting enabled.
      autocomplete_setup: Search setup
      autocomplete_setup_disabled: Autocomplete has been disabled.
      autocomplete_setup_enabled: Autocomplete has been enabled.
      autocomplete_setup_published: Autocomplete has been published.
      blacklisted_facet_created: 'Facet with name: "%{identification}", has been blocked.'
      blacklisted_facet_removed: 'Facet with name: "%{identification}", has been unbocked.'
      catalog_admin_disabled: Catalog has been disabled.
      catalog_admin_enabled: Catalog has been enabled.
      custom_tag_manager_setup_published: Custom LBX has been published.
      feeds_job_admin_removed: 'Feeds processing job for url: %{identification}, has been removed.'
      global_init_setup_disabled: Global init has been disabled.
      global_init_setup_enabled: Global init has been enabled.
      global_init_setup_published: Global init has been published.
      global_settings_setup_disabled: Global settings has been disabled.
      global_settings_setup_enabled: Global settings has been enabled.
      personalization_attribute_created: Personalization attribute with name "%{identification}" created.
      personalization_attribute_removed: Personalization attribute with name "%{identification}" removed.
      pp_forbidden_index_time: Product parameters are now forbidden in index time.
      recommender_admin_disabled: Recommender has been disabled.
      recommender_admin_enabled: Recommender has been enabled.
      recommenders_setup_disabled: 'Recommenders: %{identification}, have been disabled.'
      recommenders_setup_enabled: 'Recommenders: %{identification}, have been enabled.'
      recommenders_setup_published: 'Recommenders: %{identification}, have been published.'
      recommenders_setup_removed: 'Recommender: %{identification}, has been removed.'
      search_setup: Search setup
      search_setup_disabled: Search has been disabled.
      search_setup_enabled: Search has been enabled.
      search_setup_published: Search has been published.
      setup_shoptet_integration_updated: Shoptet integration has been updated.
      site_api_fingerprinting_updated: 'Site API fingerprinting has been updated to: "%{identification}"'
      soft_service_disable_activated: Service has been soft-disabled.
      soft_service_disable_deactivated: Service soft-disable has been deactivated.
      voice_search_setup_disabled: Voice search has been disabled.
      voice_search_setup_enabled: Voice search has been enabled.
      voice_search_setup_published: Voice search has been published.
    form_modal:
      cancel: Cancel
      input_placeholder: Enter a short description of the change.
      save: Save
      textarea_placeholder: Leave an optional, more detailed description of the change.
      title: Describe changes in this release
    side_panel:
      title: Site settings events
      warning: The list of events may be incomplete!
  sitesGroups:
    description: Site groups allow for easier navigation and management of sites within your project. <br><br>They enable easier service management. <br><br>Sites can be organized into any number of groups, with each site being in only one group. You can edit groups and move sites at any time without changing their settings.
    errors:
      duplicitName: Duplicit sites group title detected
      duplicitSiteAssignment: Duplicit site detected
      duplicitSitesGroup: Duplicit sites group detected
      emptyName: Empty sites group title detected
      invalidGroupSortOrder: Multiple sites groups are occupying the same spot
      invalidSiteAssignment: Not all sites are assigned to group
      invalidSiteSortOrder: Multiple sites are occupying the same spot in sites group
      invalidSitesGroup: Invalid sites group detected
      noSitesGroup: You must provide sites groups
    modal:
      description: All new changes will be unsaved.
      title: Close sites & groups
    sidebar:
      buttons:
        add_new_project: Add new site/project
        back: Back
        cancel: Cancel
        close: Close
        confirm: Confirm
        create_group: create group
        dropdown_items:
          edit: Edit name
          move_down: Move down
          move_up: Move up
          remove: Remove
        new_group_with_selection: New group with selection
        save_changes: Save changes
        select_all: Select all
        toggle: Manage sites
        tooltips:
          remove_disabled: Group that contains sites cannot be deleted
      content:
        description: Groups improve the clarity of pages (e.g., separating testing pages from production pages) and offer the possibility to use different settings for different groups within the same project in some cases.
        group:
          move_selection: Move selection
          name_required: Group name is required
          your_name: Your Name of Group
        site:
          move_site: Move site to
          noSites: No sites have been moved yet! <strong>We will remove this empty group</strong> after saving your changes.
          title: Sites in this group
        title: All sites in the project
      header:
        title: Overview of sites in project
    title: About the management of sites in the project
  softDisabled:
    adminPanel: admin panel
    beforeProceeding: before proceeding with any further actions.
    checkAdmin: Please ensure that services are enabled in the
    currentlyDisabled: This client’s services are currently returning no results due to unpaid invoices or lack of response.
    title: "🔴 Attention: Services disabled"
  synonyms:
    detailSidebar:
      buttons:
        cancel: Cancel
        close: Close Preview
        delete: Remove Synonym and Update Items
        edit: Edit Synonym
        next: Next
        save: Save Synonym and Update Items
        showDelete: Remove Synonym
      impact:
        items:
          previewItems: Preview for “{word}” in Results
          preview_html: "<strong>Sample {n} categories and their items</strong> in search results"
          title: Defined Synonym will affect {n1} categories and {n2} items search
        noImpact:
          info_html: There will be <strong>no results</strong> at the moment. When you save the selected synonym, all new results will be enriched. <br> However, nothing will change for your visitors yet. Are you sure you <strong>want to save synonyms for the future?</strong>
          title: Defined Synonym will not affect any categories and items search
        reindexInfo: After saving, it is usually necessary to reindex a large number of items, so changes will be reflected later.
        synonym:
          from: "<strong>Word in the catalog:</strong> <br> Catalog word whose result does not change"
          synonyms: "<strong>Entered synonyms:</strong>"
          title: Your synonym
          type: 'Type of synonym:'
      step1:
        title: Type of synonym
        typeDescription_html:
          alternate_correction: Alternative correction
          one_way: The expressions are not the same, they are similar only from one specific perspective (one direction). Therefore, they cannot be arbitrarily interchanged during search.<br><br><strong>Example:</strong><br>jeans = trousers &emsp;&emsp;&emsp;&emsp;&emsp;&emsp;&emsp; trousers ≠ jeans<br>every jeans are trousers &emsp;&emsp;&emsp;&emsp;not every trousers are jeans
          two_way: Individual expressions have very similar meanings and can be interchanged arbitrarily.<br><br><strong>Example:</strong> &emsp; iphone = apple phone &emsp; apple phone = iphone
      step2:
        bottomInfo: Confirm by pressing the button or pressing the enter key.
        button: Confirm
        description: The results for this catalog word will not change. The results of this word will be used to enrich the results of one-way synonyms defined in the next step. The size of the letters does not matter. For clarity, we recommend using diacritics.
        placeholder: Word or phrase for which you want to create a synonym
        title: Catalog word whose results do not change
        warning: The entered word is already in use. One-way synonyms have already been created for the word “{word}”.
      step3:
        added: 'You entered:'
        bottomInfo: Confirm by clicking the button or pressing the enter key
        button: Add synonyms
        description: Add synonyms separated by commas or one by one. The order of synonyms or the size of letters does not matter, special characters are not allowed (e.g. /, *, +, -). For clarity, we recommend using diacritics, but the expression does not need to be added again without it.
        description_one_way: Enter one or more words whose results should be supplemented with results of the catalog word defined in the previous step.
        limitReached: You can set maximum of {limit} synonyms
        placeholder: Alternative names or phrases, e.g. kalimba, calimba, ...
        title: Synonyms
        title2: Enter synonyms
        title_one_way: Synonyms whose results will be expanded
      title:
        create: Create new synonym
        delete: Remove synonym
        edit: Edit synonym
        preview: Synonym preview
    errors:
      conflict: Synonym for "{root} already exists"
      deletingSynononym: Error occurred while removing synonym
      export: Error occurred while exporting synonyms
      import: Error occurred while importing synonyms
      loadingDetail: Error occurred while loading synonym
      loadingImpact: Error occurred while loading synonym impact preview
      loadingSynonyms: An error occurred while loading synonyms
      rejectFailed: Removing synonym suggestion failed
      savingSynononym: An error occurred while saving synonym
    importSidebar:
      buttons:
        close: Close
        continue: Continue to recommended synonyms
        export: Export synonyms
        import: Import Synonyms
      example: Download Sample *.csv File
      export:
        info: Export all your created synonyms to a *.csv file, which you can then easily open in any spreadsheet editor and modify or expand as needed. Or you can {example} and create a completely new list of your own synonyms.
        infoEmpty: There is nothing to export at the moment because no synonyms have been added. However, you can {example} and create a completely new list of synonyms for import yourself.
      fromScratch: Sample *.csv file you can use to create your list from scratch
      import:
        dataError: Incorrect file type or format.
        info1: Save time by bulk importing your own synonyms. You can easily expand the list of existing ones, or discard all original synonyms and upload a completely new list.
        info2: You only need to {example} (or modify the export of existing synonyms), which you will open in your favorite spreadsheet editor and modify as needed.
        items: "{n} records"
        modes:
          add:
            info: Imported synonyms will be added to the original list. If the import contains synonyms for a word that already exists in the system, the new record will completely overwrite the original, older record.
            title: Add new synonyms to existing ones
          replace:
            info: All previously created synonyms will be removed. Only new imported synonyms from the list will remain.
            title: Remove original synonyms and keep only new ones
        onlyMeaningful: Import only meaningful synonyms
        onlyMeaningfulInfo: Each record will be evaluated (using ChatGPT) to determine if it is truly a synonym. If yes, it will be imported. If the record represents a typo or words that are not synonyms, it will not be imported.
        step1: Choose your import method
        step2: Add your prepared list
        supportInfo_html: We only support <strong>CSV file format</strong>, which must not contain a legend (header row).
        uploadInfo_html: Drag and drop or <span>add a list</span> in <strong>CSV format</strong> here
      success: Done, your synonym list has been successfully added.
      title: Import/export synonyms
    list:
      buttonAdd: Add new synonym
      buttonImportExport: Import/export synonyms
      description: You can add synonyms gradually or in bulk using import. We distinguish two types that you can create, one-way and two-way synonyms. More information and examples of use can be found in the {link} section.
      descriptionLink: How synonyms work
      emptyButton: Add most used synonyms
      emptyDescription: If you don't know where to start, we recommend adding the 3 most used synonyms according to our selection
      emptyFilterDescription: Try adjusting the filtering
      emptyFilterTitle: No synonyms match the search criteria
      emptyTitle: No synonyms have been added yet
      title: Your created synonyms
    notifications:
      addedMostUsedSynonyms: Most used synonyms added
      deleted: Synonym deleted successfully
      rejected: Synonym recommendation removed
      saved: Synonym saved successfully
    recommended:
      description_html: "<p>We regularly analyze how your visitors search on your pages and based on their behavior, we try to bring recommendations for synonyms to help them find what they are looking for better and easier. You can easily:</p><ul><li><strong>edit and save</strong>, it will move among created synonyms, or</li><li><strong>ignore</strong>, the recommendation will simply be discarded.</li></ul>"
      emptyButton: How do synonyms work
      emptyDescription: If you don't know how to start, visit the how do synonyms work section for more information and examples.
      emptyTitle: We currently have no synonym recommendations.
      title: Recommended synonyms
    saving:
      close: Close window
      message: After saving, it is usually necessary to reindex a large number of items. This operation may take some time. You can close the window without any worries.
      title: Synonym is being processed
    table:
      heading:
        filter: Filter
        pageSize: 'On page:'
        search: 'Search in synonyms:'
        synonyms: Synonyms
        type: Type
      item:
        buttons:
          accept: Confirm
          convert_to_two_way: Convert to two-way synonym
          edit: Edit
          more: More options
          reject: Ignore
          remove: Remove
        failed: Synonym creation failed
        finishing: Finishing up
        hover_one_way: Results for catalog word {word} also show for
        hover_two_way: Same results for
        or: or
        progress: Finishing up
        removing: Removing
        type:
          alternate_correction: A < B
          one_way: A < B
          two_way: A = B
        typeText:
          all: All synonyms
          alternate_correction: Alternative correction
          one_way: One-way synonym
          two_way: Two-way synonym
        waiting: Finishing up
    tabs:
      list: Created synonyms
      recommended: Recommended synonyms
    top:
      alert: We have improved synonyms for you! At the same time, we automatically converted your one-way synonyms to two-way using chatGPT where it made sense. {link}
      alertLink: More about the changes
      description: Synonyms tell the search engine which terms have the same meaning. For example, when creating the synonym "apple phone = iphone", entering the term "apple phone" will display the same results as the term "iphone".
      title: Synonyms
    welcome:
      create: Create your own synonyms
      description: Synonyms tell the search engine which terms have the same meaning. For example, when creating the synonym "apple phone = iphone", entering the term "apple phone" will display the same results as the term "iphone".
      heading: Synonyms
      help: How do synonyms work?
      recommended: Recommended synonyms for your e-shop
  tagsSetting:
    add: Add
    addTag: Add tag
    addTagAndPressEnter: Enter the tag name and press Enter
    all: All
    desc: 'You can customize it by labeling it (e.g. #blackfriday, wintersale, shirts...) to find it more easily.'
    suitableTag: Enter or select a suitable tag
    title: Label with your own tag
  transactionsHistory:
    chooseFile: Choose file
    csv: "(*.csv, *.json)"
    easilyAdd: Overcome the initial cold start period while Luigi's Box collect enough behavioral data (products bought together within same session, by same user, etc.) to achieve full effectiveness of our services by providing <b>the past user transactions</b> collected before the integration with Luigi's Box.<br> For omnichannel clients, in this way, it is possible to <b>import user transactions outside collected channels</b> (typically from brick&mortar stores).
    errors:
      missingKey: "Missing API key 🔑"
      subTitle: To get an API key <a href="https://www.luigisbox.com/contact/" target="_blank">contact our support team.</a>
    exportFile: <a href="https://docs.luigisbox.com/analytics/past_transactions_import.html" target="_blank">Past, offline and omnichannel transactions import documentation</a>.
    exportMustHave: The transactions file has two mandatory attributes <b>order_id</b> and <b>product identity</b>. For more information see
    fileUploaded: Transactions file was successfully uploaded
    maxSize: The size of any uploaded file cannot be larger than 1 GB
    title: Transactions history import
    uploadButtonText: Upload transactions file
    uploadHistory: Upload transactions file
    uploadingButtonText: Uploading transactions file...
  userRoles:
    askLater: Ask later
    save: Save and continue
    tellUsMore: "Tell us 🙏 more about yourself, we care about you and want to get to know you 🤝 so we can best adapt 🎯 to your needs and requirements."
    title: Help us improve
    whatIsYourRole: What is your role in the organization?
